import ManualApplicationButton from '../components/ManualApplicationButton'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import EasePicker, { AmpPlugin, PresetPlugin, RangePlugin } from 'react-easepick'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import api from '../api'
import { ReactComponent as CloseIcon } from '../assets/images/icons/ic-close.svg'
import { ReactComponent as CopyIcon } from '../assets/images/icons/ic-copy.svg'
import { ReactComponent as CheckCircleIcon } from '../assets/images/icons/ic-check-circle.svg'
import { ReactComponent as CalendarIcon } from '../assets/images/icons/ic-calendar.svg'
import { ReactComponent as ChevronIcon } from '../assets/images/icons/ic-chevron.svg'
import { ReactComponent as DismissCircleIcon } from '../assets/images/icons/ic-dismiss-circle.svg'
import { ReactComponent as EditIcon } from '../assets/images/icons/ic-edit.svg'
import { ReactComponent as EmailIcon } from '../assets/images/icons/ic-email.svg'
import { ReactComponent as ExportIcon } from '../assets/images/icons/ic-export.svg'
import { ReactComponent as FilterIcon } from '../assets/images/icons/ic-filter.svg'
import { ReactComponent as PhoneIcon } from '../assets/images/icons/ic-phone.svg'
import { ReactComponent as ClockIcon } from '../assets/images/icons/ic-clock.svg'
import { ReactComponent as PlayIcon } from '../assets/images/icons/ic-play.svg'
import { ReactComponent as PlusIcon } from '../assets/images/icons/ic-plus.svg'
import { ReactComponent as ReloadIcon } from '../assets/images/icons/ic-reload.svg'
import { ReactComponent as AttentionIcon } from '../assets/images/icons/ic-attention.svg'
import { ReactComponent as TestInactiveIcon } from '../assets/images/icons/ic-test-inactive.svg'
import { ReactComponent as WhatsappIcon } from '../assets/images/icons/ic-whatsapp.svg'
import { ButtonStatus, CourseType } from '../common/constants'
import Button from '../components/Button'
import ApplicationsManagerDialog from '../components/ApplicationsManagerDialog'
import AircallWorkspacePanel from '../components/AircallWorkspacePanel'
import LeadFlowHelpDialog from '../components/LeadFlowHelpDialog'
import Checkbox from '../components/Checkbox'
import Loader from '../components/Loader'
import Pagination from '../components/Pagination'
import MultipleSelect from '../components/MultipleSelect'
import Select from '../components/Select'
import Skeleton from '../components/Skeleton'
import TextInput from '../components/TextInput'
import TooltipWrapper from '../components/TooltipWrapper'
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout'
import styles from './ApplicationsV2.module.css'

const STAGES = [
    { id: 0, value: '', name: 'Tutti gli stage attivi' },
    { id: 1, value: 'all', name: 'Tutti, inclusi i chiusi' },
    { id: 2, value: 'new', name: 'Nuovi' },
    { id: 3, value: 'contacting', name: 'In contatto' },
    { id: 4, value: 'qualification', name: 'Qualifica' },
    { id: 5, value: 'closing', name: 'Chiusura' },
    { id: 6, value: 'closed', name: 'Chiusi' },
]

const STAGE_LABELS = {
    new: 'Nuovo',
    contacting: 'In contatto',
    qualification: 'Qualifica',
    closing: 'Chiusura',
    closed: 'Chiuso',
}

const TEMPERATURES = [
    { id: 0, value: '', name: 'Tutte le temperature' },
    { id: 1, value: 'hot', name: 'Hot · meno di 15 min' },
    { id: 2, value: 'warm', name: 'Warm · da 15 min a 4 ore' },
    { id: 3, value: 'cooling', name: 'Cooling · da 4 a 24 ore' },
    { id: 4, value: 'cold', name: 'Cold · oltre 24 ore' },
]
const TEMPERATURE_FILTER_OPTIONS = TEMPERATURES.filter((option) => option.value)

const TEMPERATURE_LABELS = {
    hot: 'Hot',
    warm: 'Warm',
    cooling: 'Cooling',
    cold: 'Cold',
}

const QUALIFICATION_LABELS = {
    not_assessed: 'Da valutare',
    sal: 'Accettato dal Sales (SAL)',
    mql: 'Qualificato Marketing (MQL)',
    lq: 'Lead qualificato (LQ)',
    non_lq: 'Non qualificato',
}

const QUALIFICATIONS = [
    { id: 0, value: '', name: 'Tutte le qualifiche' },
    { id: 1, value: 'not_assessed', name: 'Da valutare' },
    { id: 2, value: 'sal', name: 'Accettato dal Sales (SAL)' },
    { id: 3, value: 'lq', name: 'Qualificato (LQ)' },
    { id: 4, value: 'non_lq', name: 'Non qualificato' },
]
const QUALIFICATION_FILTER_OPTIONS = QUALIFICATIONS.filter((option) => option.value)

const OUTCOME_LABELS = {
    won: 'Vinto',
    lost: 'Perso',
    postponed: 'Posticipato',
}

const OUTCOMES = [
    { id: 0, value: '', name: 'Tutti gli esiti' },
    ...Object.entries(OUTCOME_LABELS).map(([value, name], index) => ({ id: index + 1, value, name })),
]

const PAYMENT_STATUS_LABELS = {
    pending: 'In attesa',
    processing: 'In elaborazione',
    requires_action: 'Azione richiesta',
    partially_paid: 'Parzialmente pagato',
    succeeded: 'Pagato',
    failed: 'Fallito',
    unpaid: 'Non pagato',
    cancelled: 'Annullato',
    abandoned: 'Abbandonato',
    waiting_for_refund: 'Rimborso in attesa',
    refunded: 'Rimborsato',
    partially_refunded: 'Parzialmente rimborsato',
    refund_failed: 'Rimborso fallito',
}

const PAYMENT_METHOD_LABELS = {
    paypal: 'PayPal',
    customer_balance: 'Bonifico bancario',
    external_bank_transfer: 'Bonifico esterno',
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    scalapay: 'Scalapay',
    sepa_debit: 'Addebito SEPA',
}

const TASK_LABELS = {
    first_call: 'Prima chiamata',
    second_call: 'Seconda chiamata',
    third_call: 'Terza chiamata',
    follow_up: 'Follow-up',
    send_whatsapp: 'Invia WhatsApp',
    send_email: 'Email automatica di ricontatto',
    request_phone_update_email: 'Richiesta correzione numero',
    verify_updated_phone: 'Ricontatta sul nuovo numero',
    scheduled_call: 'Chiamata schedulata',
    send_payment_link: 'Invia link di pagamento',
    nurture_follow_up: 'Follow-up nurture',
    contact_feedback: 'Completa il feedback del contatto',
}

const AUTOMATED_CONTACT_SEQUENCE_TASKS = new Set([
    'first_call',
    'send_whatsapp',
    'second_call',
    'send_email',
    'third_call',
    'verify_updated_phone',
    'scheduled_call',
])

const AUTOMATED_EMAIL_TASKS = new Set([
    'send_email',
    'request_phone_update_email',
])

const PAYMENT_LINK_CHANNELS = [
    { id: 1, value: 'whatsapp', name: 'WhatsApp' },
    { id: 2, value: 'email', name: 'Email manuale' },
]

const CONTACT_TASKS_WITH_COLLAPSIBLE_MANUAL = new Set([
    'first_call',
    'second_call',
    'third_call',
    'follow_up',
    'nurture_follow_up',
    'verify_updated_phone',
    'scheduled_call',
    'send_whatsapp',
])

const ACTIVITY_LABELS = {
    call: 'Chiamata',
    whatsapp: 'WhatsApp',
    email: 'Email',
    sms: 'SMS',
}

const TASK_STATUS_LABELS = {
    open: 'Da completare',
    completed: 'Completato',
    cancelled: 'Annullato',
}

const TASK_FILTER_OPTIONS = [
    { id: 'scheduled_call', value: 'scheduled_call', name: 'Chiamate schedulate' },
    { id: 'follow_up', value: 'follow_up', name: 'Follow-up' },
    { id: 'first_call', value: 'first_call', name: 'Prima chiamata' },
    { id: 'second_call', value: 'second_call', name: 'Seconda chiamata' },
    { id: 'third_call', value: 'third_call', name: 'Terza chiamata' },
    { id: 'send_whatsapp', value: 'send_whatsapp', name: 'WhatsApp' },
    { id: 'send_email', value: 'send_email', name: 'Email' },
    { id: 'verify_updated_phone', value: 'verify_updated_phone', name: 'Ricontatto nuovo numero' },
    { id: 'nurture_follow_up', value: 'nurture_follow_up', name: 'Follow-up nurture' },
    { id: 'send_payment_link', value: 'send_payment_link', name: 'Link di pagamento' },
    { id: 'contact_feedback', value: 'contact_feedback', name: 'Feedback contatto' },
]

const CALENDAR_TASK_FILTER_OPTIONS = [
    { id: 'scheduled_call', name: 'Chiamata pianificata', group: 'call', values: ['scheduled_call'] },
    { id: 'call_sequence', name: 'Chiamate', group: 'call', values: ['first_call', 'second_call', 'third_call', 'verify_updated_phone'] },
    { id: 'follow_up', name: 'Follow-up', group: 'followup', values: ['follow_up', 'nurture_follow_up'] },
    { id: 'send_whatsapp', name: 'WhatsApp', group: 'whatsapp', values: ['send_whatsapp'] },
    { id: 'send_email', name: 'Email', group: 'email', values: ['send_email', 'request_phone_update_email'] },
    { id: 'send_payment_link', name: 'Pagamento', group: 'payment', values: ['send_payment_link'] },
    { id: 'contact_feedback', name: 'Feedback', group: 'other', values: ['contact_feedback'] },
]

const TASK_DUE_FILTER_OPTIONS = [
    { id: 'overdue', value: 'overdue', name: 'Scaduti' },
    { id: 'today', value: 'today', name: 'Oggi' },
    { id: 'upcoming', value: 'upcoming', name: 'Prossimi giorni' },
]

const TASK_SCHEDULE_OPTIONS = [
    { id: 'follow_up', value: 'follow_up', name: 'Follow-up' },
    { id: 'scheduled_call', value: 'scheduled_call', name: 'Chiamata schedulata' },
]

const AUTOMATED_EMAIL_STATUS_LABELS = {
    scheduled: 'In attesa dell’invio da parte del sistema',
    sent: 'Email inviata dal sistema',
    failed: 'Invio non riuscito, nuovo tentativo automatico in programma',
    cancelled: 'Invio annullato',
    draft: 'Email in preparazione',
}

const DIRECTION_LABELS = {
    inbound: 'In entrata',
    outbound: 'In uscita',
    system: 'Sistema',
    payment: 'Pagamento',
}

const CONTACT_OUTCOME_LABELS = {
    answered: 'Risposta',
    missed: 'Persa',
    unanswered: 'Senza risposta',
    sent: 'Inviato',
    received: 'Ricevuto',
}

const CONTACT_CHANNEL_ISSUES = [
    { id: 1, value: 'invalid_phone', name: 'Numero errato o inesistente' },
    { id: 2, value: 'temporarily_unreachable', name: 'Numero temporaneamente non raggiungibile' },
    { id: 3, value: 'whatsapp_unavailable', name: 'Il contatto non utilizza WhatsApp' },
    { id: 4, value: 'other', name: 'Altro problema con il canale' },
]

const CALL_CHANNEL_ISSUES = CONTACT_CHANNEL_ISSUES.filter(
    (item) => item.value !== 'whatsapp_unavailable',
)

const WHATSAPP_CHANNEL_ISSUES = CONTACT_CHANNEL_ISSUES.filter(
    (item) => ['whatsapp_unavailable', 'other'].includes(item.value),
)

const CHANNEL_ISSUE_TASK_TYPES = new Set([
    'first_call',
    'second_call',
    'third_call',
    'verify_updated_phone',
    'scheduled_call',
    'send_whatsapp',
])

const CONTACT_CHANNEL_ISSUE_LABELS = Object.fromEntries(
    CONTACT_CHANNEL_ISSUES.map((item) => [item.value, item.name]),
)

const CONTACT_CHANNEL_STATUS_LABELS = {
    unknown: 'Da verificare',
    invalid: 'Non valido',
    unavailable: 'Non disponibile',
    temporarily_unreachable: 'Temporaneamente non raggiungibile',
}

const MESSAGE_DELIVERY_STATUS_LABELS = {
    pending: 'Invio in corso',
    sent: 'Inviato',
    delivered: 'Consegnato',
    read: 'Letto',
    failed: 'Invio non riuscito',
    undelivered: 'Non consegnato',
    received: 'Ricevuto',
}

const MESSAGE_CONVERSATION_STATUS_LABELS = {
    awaiting_reply: 'In attesa di risposta',
    replied: 'Il lead ha risposto',
    delivery_failed: 'Invio non riuscito',
    received: 'Risposta ricevuta',
}

const LEGACY_TOUCH_OUTCOME_LABELS = {
    answered: 'Risposta',
    not_answered: 'Senza risposta',
    awaiting_response: 'In attesa di risposta',
    postponed: 'Contatto posticipato',
    wrong_contact: 'Contatto errato',
}

const LEGACY_LEAD_FEEDBACK_LABELS = {
    purchase_intent: 'Intenzione di acquisto',
    info_request: 'Richiesta di informazioni',
    wrong_intent: 'Intento errato',
    not_interested: 'Non interessato',
    price_objection: 'Obiezione sul prezzo',
    product_objection: 'Obiezione sul prodotto',
}

const MANUAL_ACTIVITY_CHANNELS = [
    { id: 1, value: 'call', name: 'Chiamata esterna' },
    { id: 2, value: 'whatsapp', name: 'WhatsApp manuale' },
    { id: 3, value: 'email', name: 'Email' },
]

const MANUAL_ACTIVITY_OUTCOMES = {
    call: [
        { id: 1, value: 'answered', name: 'Ha risposto' },
        { id: 2, value: 'unanswered', name: 'Nessuna risposta' },
    ],
    whatsapp: [
        { id: 1, value: 'sent', name: 'Inviato, in attesa di risposta' },
        { id: 2, value: 'received', name: 'Il lead ha risposto' },
    ],
    email: [
        { id: 1, value: 'sent', name: 'Email inviata' },
    ],
}

const CONVERSATION_OUTCOMES = [
    { id: 1, value: 'call_scheduled', name: 'Call pianificata' },
    { id: 2, value: 'information_sent', name: 'Informazioni inviate' },
    { id: 3, value: 'decision_pending', name: 'Decisione in sospeso' },
    { id: 4, value: 'not_interested', name: 'Non interessato' },
    { id: 5, value: 'wrong_intent', name: 'Intento errato' },
    { id: 6, value: 'out_of_target', name: 'Fuori target' },
    { id: 7, value: 'qualification', name: 'Avvia la qualifica' },
    { id: 8, value: 'next_edition', name: 'Prossima edizione' },
]

const CLOSING_CONVERSATION_OUTCOMES = [
    { id: 1, value: 'awaiting_payment', name: 'In attesa del pagamento' },
    { id: 2, value: 'payment_issue', name: 'Problema con il pagamento' },
    { id: 3, value: 'financing_pending', name: 'In attesa di prestito o finanziamento' },
    { id: 4, value: 'decision_pending', name: 'Decisione in sospeso' },
    { id: 5, value: 'clarification_requested', name: 'Richiesti chiarimenti o informazioni' },
    { id: 6, value: 'not_proceeding', name: 'Non procede più' },
    { id: 7, value: 'next_edition', name: 'Prossima edizione' },
]

const QUALIFICATION_CONVERSATION_OUTCOMES = CONVERSATION_OUTCOMES.map((item) => (
    item.value === 'not_interested'
        ? { ...item, value: 'not_proceeding', name: 'Non procede più' }
        : item
))

const ALL_CONVERSATION_OUTCOMES = [
    ...CONVERSATION_OUTCOMES,
    ...CLOSING_CONVERSATION_OUTCOMES,
]

const CONVERSATION_OUTCOME_LABELS = Object.fromEntries(
    ALL_CONVERSATION_OUTCOMES.map((item) => [item.value, item.name]),
)

const conversationOutcomesForStage = (stage, qualificationStatus = null) => {
    if (stage === 'closing') return CLOSING_CONVERSATION_OUTCOMES
    if (stage !== 'qualification') return CONVERSATION_OUTCOMES
    if (qualificationStatus === 'lq') {
        return QUALIFICATION_CONVERSATION_OUTCOMES.filter((item) => item.value !== 'qualification')
    }
    return QUALIFICATION_CONVERSATION_OUTCOMES
}

const UNCONFIRMED_CONVERSATION_OUTCOME = {
    id: 'unconfirmed-conversation-outcome',
    value: '',
    name: 'Da confermare',
}

const CONTACT_NEXT_STEPS = [
    { id: 1, value: 'follow_up', name: 'Programma follow-up' },
    { id: 8, value: 'scheduled_call', name: 'Programma chiamata' },
    { id: 2, value: 'start_qualification', name: 'Avvia qualifica' },
    { id: 3, value: 'qualify', name: 'Completa qualifica' },
    { id: 4, value: 'mark_unqualified', name: 'Segna come non qualificato' },
    { id: 5, value: 'mark_lost', name: 'Segna come perso' },
    { id: 6, value: 'continue_sequence', name: 'Continua sequenza automatica' },
    { id: 7, value: 'postpone', name: 'Metti in riciclo' },
]

const CONTACT_NEXT_STEP_LABELS = Object.fromEntries(
    CONTACT_NEXT_STEPS.map((item) => [item.value, item.name]),
)

const SOURCE_LABELS = {
    aircall: 'Aircall',
    mailing: 'Email automatica',
    backoffice: 'Backoffice',
    backoffice_application: 'Backoffice',
    manual: 'Inserimento manuale',
    legacy: 'Processo precedente',
    webapp: 'Piattaforma',
    webapp_application: 'Piattaforma',
    fast_checkout: 'Checkout',
    referral_application: 'Referral',
    activecampaign_webhook: 'ActiveCampaign',
    backfill_cli: 'Migrazione dati',
    system: 'Sistema',
}

const ACQUISITION_LABELS = {
    webapp_application: 'Candidatura Acquisita da Piattaforma',
    fast_checkout: 'Candidatura Acquisita da Checkout',
    referral_application: 'Candidatura Acquisita da Referral',
    activecampaign_webhook: 'Candidatura Acquisita da ActiveCampaign',
    backoffice_application: 'Candidatura Acquisita da Backoffice',
    legacy: 'Candidatura Importata dal Processo Precedente',
    backfill_cli: 'Candidatura Importata dal Processo Precedente',
}

const UTM_LABELS = {
    utm_source: 'utm_source',
    utm_medium: 'utm_medium',
    utm_campaign: 'utm_campaign',
    utm_content: 'utm_content',
    utm_term: 'utm_term',
}

const LOSS_REASONS = [
    { id: 1, value: 'no_budget', name: 'Budget non disponibile' },
    { id: 2, value: 'timing', name: 'Tempistiche non compatibili' },
    { id: 3, value: 'competitor', name: 'Ha scelto un’alternativa' },
    { id: 4, value: 'changed_mind', name: 'Non procede più / ha cambiato idea' },
    { id: 5, value: 'other', name: 'Altro' },
]

const LOSS_REASON_LABELS = {
    ...Object.fromEntries(LOSS_REASONS.map((reason) => [reason.value, reason.name])),
    no_response: 'Non raggiungibile',
}

const UNQUALIFIED_REASONS = [
    { id: 1, value: 'not_interested', name: 'Non interessato fin dal primo contatto' },
    { id: 2, value: 'wrong_intent', name: 'Intento errato' },
    { id: 3, value: 'out_of_target', name: 'Fuori target' },
    { id: 4, value: 'invalid_contact', name: 'Contatti non validi' },
    { id: 5, value: 'missing_requirements', name: 'Requisiti mancanti' },
    { id: 6, value: 'product_mismatch', name: 'Corso non adatto al bisogno' },
    { id: 7, value: 'unreachable', name: 'Non raggiungibile dopo la sequenza di contatto' },
    { id: 8, value: 'timing_mismatch', name: 'Tempistiche non compatibili' },
    { id: 9, value: 'budget_mismatch', name: 'Budget non compatibile' },
    { id: 10, value: 'duplicate', name: 'Lead duplicato' },
    { id: 11, value: 'other', name: 'Altro motivo di non qualifica' },
]

const UNQUALIFIED_REASON_LABELS = Object.fromEntries(
    UNQUALIFIED_REASONS.map((reason) => [reason.value, reason.name]),
)

const NEGATIVE_CLOSURE_REASONS = [
    ...UNQUALIFIED_REASONS.map((reason) => ({
        ...reason,
        id: `unqualified-${reason.id}`,
        value: `unqualified:${reason.value}`,
        reason: reason.value,
        action: 'mark_unqualified',
        outcomeLabel: 'Non qualificato',
    })),
    ...LOSS_REASONS.map((reason) => ({
        ...reason,
        id: `lost-${reason.id}`,
        value: `lost:${reason.value}`,
        reason: reason.value,
        action: 'mark_lost',
        outcomeLabel: 'Perso',
    })),
]

const NEGATIVE_CLOSURE_TYPES = [
    { id: 'unqualified', value: 'unqualified', name: 'Non qualificato', action: 'mark_unqualified' },
    { id: 'lost', value: 'lost', name: 'Perso', action: 'mark_lost' },
]

const displayTransitionActions = (actions = []) => {
    const result = []
    actions.forEach((action) => {
        if (['mark_unqualified', 'mark_lost'].includes(action)) {
            if (!result.includes('close_negative')) result.push('close_negative')
            return
        }
        result.push(action)
    })
    return result
}

const availableNegativeClosureReasons = (actions = []) => NEGATIVE_CLOSURE_REASONS.filter((reason) => (
    actions.includes(reason.action)
))

const availableNegativeClosureTypes = (actions = []) => NEGATIVE_CLOSURE_TYPES.filter((type) => (
    actions.includes(type.action)
))

const negativeDecisionLabel = (actions = []) => {
    const canMarkUnqualified = actions.includes('mark_unqualified')
    const canMarkLost = actions.includes('mark_lost')
    if (canMarkUnqualified && canMarkLost) return 'Segna come perso / non qualificato'
    if (canMarkLost) return 'Segna come perso'
    return 'Segna come non qualificato'
}

const QUALIFICATION_CRITERIA_BY_FIELD = {
    interest: [
        { id: 1, value: 'confirmed', name: 'Interesse alto' },
        { id: 2, value: 'uncertain', name: 'Interesse medio' },
        { id: 3, value: 'not_met', name: 'Interesse basso' },
        { id: 4, value: 'not_assessed', name: 'Non valutato' },
    ],
    product_fit: [
        { id: 1, value: 'confirmed', name: 'Fit alto' },
        { id: 2, value: 'uncertain', name: 'Fit parziale' },
        { id: 3, value: 'not_met', name: 'Non in linea' },
        { id: 4, value: 'not_assessed', name: 'Non valutato' },
    ],
    timing: [
        { id: 1, value: 'confirmed', name: 'Questa edizione' },
        { id: 2, value: 'uncertain', name: 'Prossima edizione' },
        { id: 3, value: 'not_met', name: 'Non compatibile' },
        { id: 4, value: 'not_assessed', name: 'Non valutato' },
    ],
    budget: [
        { id: 1, value: 'confirmed', name: 'Disponibile' },
        { id: 2, value: 'uncertain', name: 'Da verificare' },
        { id: 3, value: 'not_met', name: 'Non disponibile' },
        { id: 4, value: 'not_assessed', name: 'Non valutato' },
    ],
}

const QUALIFICATION_FIELD_LABELS = {
    interest: 'Livello di interesse',
    product_fit: 'Fit con il prodotto',
    timing: 'Timing commerciale',
    budget: 'Disponibilità di budget',
}

const QUALIFICATION_WIZARD_FIELDS = [
    'interest',
    'product_fit',
    'timing',
    'budget',
    'objection',
]

const QUALIFICATION_OBJECTIONS = [
    { id: 1, value: 'none', name: 'Nessuna obiezione' },
    { id: 2, value: 'price_budget', name: 'Prezzo / budget' },
    { id: 3, value: 'timing', name: 'Timing' },
    { id: 4, value: 'product_fit', name: 'Product fit' },
    { id: 5, value: 'wrong_intent', name: 'Intento non coerente' },
    { id: 6, value: 'not_interested', name: 'Non interessato' },
    { id: 7, value: 'info_request', name: 'Richiesta informazioni' },
    { id: 8, value: 'requirements_seniority', name: 'Requisiti / seniority' },
    { id: 9, value: 'payment_purchase', name: 'Pagamento / modalità di acquisto' },
    { id: 10, value: 'unreachable', name: 'Non raggiungibile' },
    { id: 11, value: 'invalid_contact', name: 'Numero o email errati' },
    { id: 12, value: 'competitor', name: 'Competitor / comparazione' },
    { id: 13, value: 'other', name: 'Altro' },
]

const CONTACT_OBJECTIONS = QUALIFICATION_OBJECTIONS

const QUALIFICATION_NEXT_STEPS = [
    { id: 1, value: 'proposal', name: 'Invia proposta' },
    { id: 2, value: 'follow_up', name: 'Programma follow-up' },
    { id: 3, value: 'send_information', name: 'Invia informazioni' },
    { id: 4, value: 'pm_call', name: 'Organizza call con PM' },
    { id: 5, value: 'application_review', name: 'Valuta candidatura' },
    { id: 6, value: 'payment_link', name: 'Invia link di pagamento' },
    { id: 7, value: 'alternative_product', name: 'Proponi prodotto alternativo' },
    { id: 8, value: 'stop', name: 'Chiudi il percorso' },
]

const QUALIFICATION_OBJECTION_LABELS = Object.fromEntries(QUALIFICATION_OBJECTIONS.map((item) => [item.value, item.name]))
const QUALIFICATION_NEXT_STEP_LABELS = Object.fromEntries(QUALIFICATION_NEXT_STEPS.map((item) => [item.value, item.name]))

const qualificationCriterionLabel = (field, value) => (
    QUALIFICATION_CRITERIA_BY_FIELD[field]?.find((item) => item.value === value)?.name || value
)

const qualificationWizardOptions = (field) => {
    if (field === 'objection') return QUALIFICATION_OBJECTIONS
    return QUALIFICATION_CRITERIA_BY_FIELD[field] || []
}

const qualificationWizardLabel = (field, value) => (
    qualificationWizardOptions(field).find((item) => item.value === value)?.name || value
)

const qualificationFeedbackComplete = (feedback = {}) => (
    QUALIFICATION_WIZARD_FIELDS.every((field) => Boolean(feedback[field]))
)

const getContactSequenceProgress = (opportunity = {}) => {
    const activities = opportunity.activities || []
    const callAttempts = activities.filter((activity) => (
        activity.type === 'call'
        && activity.direction === 'outbound'
        && ['missed', 'unanswered'].includes(activity.contact_outcome)
    )).length
    const whatsappSent = activities.some((activity) => (
        ['whatsapp', 'sms'].includes(activity.type)
        && activity.direction === 'outbound'
        && activity.contact_outcome === 'sent'
    ))
    const emailSent = activities.some((activity) => (
        activity.type === 'email'
        && activity.direction === 'outbound'
        && activity.contact_outcome === 'sent'
    ))
    const missing = []
    if (callAttempts < 3) {
        const remainingCalls = 3 - callAttempts
        missing.push(`${remainingCalls} ${remainingCalls === 1 ? 'chiamata senza risposta' : 'chiamate senza risposta'}`)
    }
    if (!whatsappSent) missing.push('WhatsApp inviato')
    if (!emailSent) missing.push('email inviata')

    const nextTask = (opportunity.tasks || []).find((task) => (
        task.status === 'open'
        && AUTOMATED_CONTACT_SEQUENCE_TASKS.has(task.type)
    ))

    return {
        complete: missing.length === 0,
        missing,
        nextTask,
    }
}

const deriveQualificationResult = (feedback = {}, contactSequence = {}) => {
    if (feedback.objection === 'not_interested') {
        return {
            action: 'mark_unqualified',
            label: 'Non qualificato',
            description: 'Il lead non supera la qualifica perché non è interessato a proseguire.',
            qualificationReason: 'not_interested',
            nextStep: 'stop',
        }
    }
    if (feedback.objection === 'unreachable') {
        if (!contactSequence.complete) {
            const missingDescription = contactSequence.missing?.length
                ? ` Mancano: ${contactSequence.missing.join(', ')}.`
                : ''
            return {
                action: 'complete_contact_sequence',
                label: 'Sequenza da completare',
                description: `Il lead non può ancora essere chiuso per mancata risposta.${missingDescription}`,
                nextStep: null,
                nextStepLabel: contactSequence.nextTask
                    ? TASK_LABELS[contactSequence.nextTask.type] || contactSequence.nextTask.type
                    : 'Completa i tentativi mancanti',
                blocked: true,
            }
        }
        return {
            action: 'mark_unqualified',
            label: 'Non qualificato',
            description: 'La sequenza di contatto è terminata senza riuscire a valutare il lead.',
            qualificationReason: 'unreachable',
            nextStep: 'stop',
        }
    }
    if (
        feedback.product_fit === 'not_met'
        || ['wrong_intent', 'product_fit', 'requirements_seniority', 'invalid_contact'].includes(feedback.objection)
    ) {
        const qualificationReasonByObjection = {
            wrong_intent: 'wrong_intent',
            product_fit: 'product_mismatch',
            requirements_seniority: 'missing_requirements',
            invalid_contact: 'invalid_contact',
        }
        return {
            action: 'mark_unqualified',
            label: 'Non qualificato',
            description: 'Il bisogno o il profilo non sono coerenti con il corso selezionato.',
            qualificationReason: qualificationReasonByObjection[feedback.objection] || 'product_mismatch',
            nextStep: feedback.objection === 'product_fit' ? 'alternative_product' : 'stop',
        }
    }
    if (feedback.interest === 'not_met') {
        return {
            action: 'mark_unqualified',
            label: 'Non qualificato',
            description: 'Il lead non supera la qualifica per mancanza di interesse.',
            qualificationReason: 'not_interested',
            nextStep: 'stop',
        }
    }
    if (feedback.timing === 'uncertain') {
        return {
            action: 'postpone',
            label: 'Riciclo',
            description: 'Il lead è potenzialmente valido, ma va ricontattato per una prossima edizione.',
            nextStep: 'follow_up',
        }
    }
    if (feedback.timing === 'not_met') {
        return {
            action: 'mark_unqualified',
            label: 'Non qualificato',
            description: 'Il lead non supera la qualifica perché le tempistiche non sono compatibili.',
            qualificationReason: 'timing_mismatch',
            nextStep: 'stop',
        }
    }
    if (feedback.budget === 'not_met') {
        return {
            action: 'mark_unqualified',
            label: 'Non qualificato',
            description: 'Il lead non supera la qualifica perché il budget non è compatibile.',
            qualificationReason: 'budget_mismatch',
            nextStep: 'stop',
        }
    }
    if (
        feedback.interest === 'confirmed'
        && feedback.product_fit === 'confirmed'
        && feedback.timing === 'confirmed'
        && ['confirmed', 'uncertain'].includes(feedback.budget)
    ) {
        return {
            action: 'qualify',
            label: 'Qualificato',
            description: 'Il lead è coerente con il corso e può passare alla fase di chiusura.',
            nextStep: feedback.objection === 'info_request'
                ? 'send_information'
                : (feedback.budget === 'uncertain' || ['price_budget', 'payment_purchase'].includes(feedback.objection)
                    ? 'follow_up'
                    : 'payment_link'),
        }
    }
    return {
        action: 'schedule_follow_up',
        label: 'Follow-up',
        description: 'Servono altre informazioni o una nuova conversazione prima della decisione.',
        nextStep: feedback.objection === 'info_request'
            ? 'send_information'
            : (feedback.product_fit === 'uncertain' ? 'pm_call' : 'follow_up'),
    }
}

const AIRCALL_CONTACT_STATUS_LABELS = {
    synced: 'Contatto Aircall sincronizzato',
    pending: 'Sincronizzazione Aircall in attesa',
    processing: 'Sincronizzazione Aircall in corso',
    failed: 'Sincronizzazione Aircall non riuscita',
    not_synced: 'Contatto non ancora sincronizzato',
}

const AIRCALL_SIMULATION_SCENARIOS = [
    {
        id: 1,
        value: 'answered_call',
        name: 'Chiamata con risposta',
        description: 'Simula solamente la conclusione di una chiamata a cui il lead ha risposto.',
    },
    {
        id: 2,
        value: 'missed_call',
        name: 'Chiamata senza risposta',
        description: 'Simula una chiamata conclusa senza che il lead abbia risposto.',
    },
    {
        id: 3,
        value: 'inbound_answered_call',
        name: 'Chiamata in entrata con risposta',
        description: 'Simula una chiamata del lead a cui il sales ha risposto: interrompe la sequenza automatica e richiede il feedback.',
    },
    {
        id: 4,
        value: 'inbound_missed_call',
        name: 'Chiamata in entrata persa',
        description: 'Simula una chiamata del lead non risposta: viene registrata senza completare i task di chiamata del sales.',
    },
    {
        id: 5,
        value: 'complete_call',
        name: 'Flusso completo chiamata',
        description: 'Simula la chiamata con risposta e applica casualmente uno o due tag SFBS tra loro coerenti.',
    },
    {
        id: 6,
        value: 'whatsapp_sent',
        name: 'WhatsApp inviato e letto',
        description: 'Simula l’invio via Aircall e il successivo aggiornamento dello stato a letto.',
    },
    {
        id: 7,
        value: 'whatsapp_received',
        name: 'WhatsApp ricevuto',
        description: 'Simula un messaggio WhatsApp inviato dal lead alla linea Aircall.',
    },
    {
        id: 8,
        value: 'whatsapp_conversation',
        name: 'Conversazione WhatsApp completa',
        description: 'Simula invio, lettura e risposta del lead, verificando la chiusura dell’attesa e la creazione del feedback.',
    },
]

const ACTIONS = {
    schedule_follow_up: {
        label: 'Programma follow-up',
        title: 'Quando vuoi ricontattare il lead?',
        description: 'L’opportunità resta attiva e viene creato un nuovo task di ricontatto.',
    },
    start_qualification: {
        label: 'Avvia qualifica',
        title: 'Avviare la qualifica?',
        description: 'Il lead entra nella fase di valutazione. Subito dopo compilerai interesse, fit, timing, budget e obiezione; il sistema suggerirà il prossimo passo.',
    },
    qualify: {
        label: 'Valuta il lead',
        title: 'Valutazione commerciale guidata',
        description: 'Rispondi alle domande: il sistema proporrà qualificato, perso, riciclo, non qualificato o follow-up.',
    },
    mark_unqualified: {
        label: 'Non qualificato',
        title: 'Segnare il lead come non qualificato?',
        description: 'Il lead resterà nello stage Qualifica con attributo Non qualificato e potrà essere rivalutato in seguito.',
        destructive: true,
    },
    start_closing: {
        label: 'Avvia chiusura',
        title: 'Avviare la fase di chiusura?',
        description: 'Il lead è qualificato. Passa alla fase in cui il sales accompagna il pagamento e gestisce eventuali obiezioni finali.',
        positive: true,
    },
    mark_won: {
        label: 'Segna come vinto',
        title: 'Confermare la vendita?',
        description: 'L’opportunità verrà chiusa con esito vinto.',
        positive: true,
    },
    mark_lost: {
        label: 'Segna come perso',
        title: 'Segnare l’opportunità come persa?',
        description: 'L’opportunità verrà chiusa senza vendita.',
        destructive: true,
    },
    close_negative: {
        label: 'Segna come perso / non qualificato',
        title: 'Registrare la decisione commerciale?',
        description: 'Scegli prima se il lead è non qualificato o perso, quindi indica la motivazione.',
        destructive: true,
    },
    postpone: {
        label: 'Metti in riciclo',
        title: 'Spostare il lead alla prossima edizione?',
        description: 'L’opportunità esce dalla pipeline attiva fino alla data di riciclo indicata.',
    },
    reopen: {
        label: 'Riapri opportunità',
        title: 'Riaprire l’opportunità?',
        description: 'Il lead tornerà nella fase di contatto e potrai registrare nuove attività e decisioni commerciali.',
    },
}

const lifecycleTitle = (event) => {
    if (event.metadata?.trigger === 'payment_link_sent') return 'Link di pagamento inviato'
    if (event.event_type === 'opportunity_created') {
        return ACQUISITION_LABELS[event.source] || 'Candidatura Acquisita'
    }
    if (event.event_type === 'activity_recorded') {
        const activity = ACTIVITY_LABELS[event.metadata?.activity_type] || 'Contatto'
        if (event.metadata?.capture_mode === 'standalone_manual_activity') return `Attività manuale · ${activity}`
        if (event.metadata?.activity_type === 'call') {
            if (event.metadata?.direction === 'inbound') return 'Chiamata Ricevuta'
            if (event.metadata?.direction === 'outbound') return 'Chiamata Effettuata'
            return 'Chiamata Registrata'
        }
        if (event.metadata?.activity_type === 'email') return 'Email inviata'
        return `${activity} registrato`
    }
    if (event.event_type === 'opportunity_reopened') return 'Opportunità riaperta'
    if (event.event_type === 'follow_up_scheduled') return 'Follow-up programmato'
    if (event.event_type === 'scheduled_call_scheduled') return 'Chiamata schedulata'
    if (event.event_type === 'task_schedule_updated') return 'Task modificato'
    if (event.event_type === 'contact_feedback_requested') return 'Feedback commerciale richiesto'
    if (event.event_type === 'activity_feedback_recorded') return 'Feedback del contatto completato'
    if (event.event_type === 'task_completed') return 'Task completato manualmente'
    if (event.event_type === 'owner_changed') return 'Responsabile commerciale modificato'
    if (event.event_type === 'course_changed') return 'Corso del lead modificato'
    if (event.event_type === 'test_status_changed') return 'Stato test modificato'
    if (event.event_type === 'contact_updated') return 'Informazioni di contatto modificate'
    if (event.event_type === 'contact_phone_updated_by_lead') return 'Numero aggiornato dal lead'
    if (event.event_type === 'contact_channel_status_changed') return 'Disponibilità dei canali aggiornata'
    if (event.event_type === 'calendly_meeting_scheduled') return 'Chiamata prenotata dal lead'
    if (event.event_type === 'calendly_meeting_canceled') return 'Prenotazione Calendly annullata'
    if (event.metadata?.action === 'start_qualification') return 'Qualifica avviata'
    if (event.metadata?.action === 'qualify') return 'Lead qualificato (LQ)'
    if (event.event_type === 'opportunity_closed') {
        if (event.to_outcome === 'won') return event.source === 'payment' ? 'Vendita confermata dal pagamento' : 'Vendita conclusa'
        if (event.to_outcome === 'lost') return 'Opportunità persa'
        if (event.to_outcome === 'postponed') return 'Ricontatto programmato'
        return 'Opportunità chiusa'
    }
    if (event.event_type === 'qualification_changed') return 'Valutazione commerciale aggiornata'
    if (event.event_type === 'stage_changed') return 'Fase commerciale aggiornata'
    const fallback = String(event.event_type || 'Evento commerciale').replaceAll('_', ' ')
    return fallback.charAt(0).toUpperCase() + fallback.slice(1)
}

const lifecycleChanges = (event) => {
    const changes = []
    if (event.from_stage !== event.to_stage && event.to_stage) {
        changes.push(`${STAGE_LABELS[event.from_stage] || event.from_stage || 'Ingresso'} → ${STAGE_LABELS[event.to_stage] || event.to_stage}`)
    }
    if (event.from_qualification_status !== event.to_qualification_status && event.to_qualification_status) {
        changes.push(`${QUALIFICATION_LABELS[event.from_qualification_status] || event.from_qualification_status || 'Da valutare'} → ${QUALIFICATION_LABELS[event.to_qualification_status] || event.to_qualification_status}`)
    }
    if (event.to_outcome) changes.push(`Esito: ${OUTCOME_LABELS[event.to_outcome] || event.to_outcome}`)
    if (event.event_type === 'owner_changed' && event.metadata?.to_owner) {
        changes.push(
            `${event.metadata.from_owner ? fullName(event.metadata.from_owner) : 'Non assegnato'} → ${fullName(event.metadata.to_owner)}`,
        )
    }
    if (event.event_type === 'course_changed' && event.metadata?.to_course) {
        changes.push(
            `${event.metadata.from_course?.name || 'Nessun corso'} → ${event.metadata.to_course.name}`,
        )
    }
    if (event.event_type === 'test_status_changed') {
        changes.push(event.metadata?.to_test ? 'Candidatura contrassegnata come test' : 'Candidatura rimossa dai test')
    }
    if (event.event_type === 'task_schedule_updated') {
        const previousType = TASK_LABELS[event.metadata?.from_task_type] || event.metadata?.from_task_type
        const nextType = TASK_LABELS[event.metadata?.to_task_type] || event.metadata?.to_task_type
        if (previousType && nextType && previousType !== nextType) {
            changes.push(`${previousType} → ${nextType}`)
        }
        if (event.metadata?.from_due_at && event.metadata?.to_due_at) {
            changes.push(`Scadenza: ${formatDate(event.metadata.from_due_at)} → ${formatDate(event.metadata.to_due_at)}`)
        }
        if (event.metadata?.to_task_owner && event.metadata?.from_task_owner?.id !== event.metadata.to_task_owner.id) {
            changes.push(`Assegnatario: ${fullName(event.metadata.from_task_owner)} → ${fullName(event.metadata.to_task_owner)}`)
        }
    }
    if (['contact_updated', 'contact_phone_updated_by_lead'].includes(event.event_type) && event.metadata?.changes) {
        const labels = { name: 'Nome', surname: 'Cognome', email: 'Email', phone: 'Telefono' }
        Object.entries(event.metadata.changes).forEach(([field, values]) => {
            changes.push(`${labels[field] || field}: ${values?.from || '—'} → ${values?.to || '—'}`)
        })
    }
    return changes
}

const lifecycleDetails = (event) => {
    const details = []
    if (event.metadata?.recycle_at) details.push(`Ricontatto: ${formatDate(event.metadata.recycle_at)}`)
    if (event.metadata?.follow_up_due_at) details.push(`Prossimo contatto: ${formatDate(event.metadata.follow_up_due_at)}`)
    if (event.metadata?.start_at && event.event_type.startsWith('calendly_')) {
        details.push(`Appuntamento: ${formatDate(event.metadata.start_at)}`)
    }
    if (event.metadata?.commercial_objection) {
        details.push(`Obiezione: ${QUALIFICATION_OBJECTION_LABELS[event.metadata.commercial_objection] || event.metadata.commercial_objection}`)
    }
    if (event.metadata?.issue_type) {
        details.push(`Segnalazione: ${CONTACT_CHANNEL_ISSUE_LABELS[event.metadata.issue_type] || event.metadata.issue_type}`)
    }
    if (event.metadata?.completed_task_id && event.event_type !== 'task_completed') details.push(`Task #${event.metadata.completed_task_id} completato`)
    if (event.event_type === 'contact_feedback_requested' && event.metadata?.created_task_id) {
        details.push(`Creato il task #${event.metadata.created_task_id}`)
    }
    if (event.event_type === 'task_completed') {
        const taskName = TASK_LABELS[event.metadata?.task_type] || event.metadata?.task_type || 'Task'
        details.push(`${taskName} · Task #${event.metadata?.completed_task_id}`)
    }
    if (['activity_recorded', 'activity_feedback_recorded'].includes(event.event_type) && event.metadata?.contact_outcome) {
        details.push(`Esito: ${CONTACT_OUTCOME_LABELS[event.metadata.contact_outcome] || event.metadata.contact_outcome}`)
    }
    if (['activity_recorded', 'activity_feedback_recorded'].includes(event.event_type) && event.metadata?.conversation_outcome) {
        details.push(`Esito commerciale: ${CONVERSATION_OUTCOME_LABELS[event.metadata.conversation_outcome] || event.metadata.conversation_outcome}`)
    }
    if (['activity_recorded', 'activity_feedback_recorded'].includes(event.event_type) && event.metadata?.next_step_type) {
        details.push(`Prossimo passo: ${CONTACT_NEXT_STEP_LABELS[event.metadata.next_step_type] || event.metadata.next_step_type}`)
    }
    if (event.metadata?.next_step_due_at) details.push(`Scadenza: ${formatDate(event.metadata.next_step_due_at)}`)
    if (event.metadata?.trigger === 'payment_link_sent') {
        details.push(`Modalità: ${event.metadata.payment_link_delivery === 'automatic' ? 'Email automatica' : 'Invio manuale'}`)
        if (event.metadata.payment_link_channel) {
            details.push(`Canale: ${event.metadata.payment_link_channel === 'whatsapp' ? 'WhatsApp' : 'Email'}`)
        }
    }
    if (event.metadata?.next_task_id) details.push(`Creato il task #${event.metadata.next_task_id}`)
    if (event.metadata?.notes) details.push(`Nota: ${event.metadata.notes}`)
    if (event.metadata?.sequence_task_ids?.length) {
        const taskTypes = event.metadata.sequence_task_types || []
        const labels = taskTypes.map((type) => TASK_LABELS[type] || type).join(', ')
        details.push(`Sequenza automatica: ${labels || `${event.metadata.sequence_task_ids.length} task creati`}`)
    }
    if (event.metadata?.cancelled_task_ids?.length) {
        const count = event.metadata.cancelled_task_ids.length
        details.push(`${count} task ${count === 1 ? 'annullato' : 'annullati'}`)
    }
    if (event.metadata?.reassigned_task_ids?.length) {
        const count = event.metadata.reassigned_task_ids.length
        details.push(`${count} task ${count === 1 ? 'riassegnato' : 'riassegnati'}`)
    }
    if (event.metadata?.loss_reason) {
        details.push(`Motivazione: ${LOSS_REASON_LABELS[event.metadata.loss_reason] || event.metadata.loss_reason}`)
    }
    if (event.metadata?.qualification_reason) {
        details.push(`Motivazione: ${UNQUALIFIED_REASON_LABELS[event.metadata.qualification_reason] || event.metadata.qualification_reason}`)
    }
    if (event.metadata?.order_id) details.push(`Ordine #${event.metadata.order_id}`)
    if (event.metadata?.trigger === 'order_succeeded') details.push('Pagamento ordine completato')
    if (event.metadata?.trigger === 'first_installment_succeeded') details.push(`Prima rata pagata${event.metadata?.installment_id ? ` (#${event.metadata.installment_id})` : ''}`)
    const feedback = event.metadata?.qualification_feedback
    if (feedback) {
        details.push(`Interesse: ${qualificationCriterionLabel('interest', feedback.interest)}`)
        details.push(`Fit prodotto: ${qualificationCriterionLabel('product_fit', feedback.product_fit)}`)
        details.push(`Timing: ${qualificationCriterionLabel('timing', feedback.timing)}`)
        details.push(`Budget: ${qualificationCriterionLabel('budget', feedback.budget)}`)
        details.push(`Obiezione: ${QUALIFICATION_OBJECTION_LABELS[feedback.objection] || feedback.objection}`)
        details.push(`Prossimo passo: ${QUALIFICATION_NEXT_STEP_LABELS[feedback.next_step] || feedback.next_step}`)
    }
    return details
}

const parseDate = (value) => {
    if (!value) return null
    const normalized = typeof value === 'string' && !/(Z|[+-]\d{2}:?\d{2})$/.test(value)
        ? `${value}Z`
        : value
    const date = new Date(normalized)
    return Number.isNaN(date.getTime()) ? null : date
}

const formatDate = (value, includeTime = true) => {
    if (!value) return '—'
    const date = parseDate(value)
    if (!date) return '—'
    return new Intl.DateTimeFormat('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    }).format(date)
}

const formatCurrency = (value) => new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
}).format((Number(value) || 0) / 100)

const isSameDay = (firstDate, secondDate) => (
    firstDate.getFullYear() === secondDate.getFullYear()
    && firstDate.getMonth() === secondDate.getMonth()
    && firstDate.getDate() === secondDate.getDate()
)

const formatTimelineDate = (value) => {
    const date = parseDate(value)
    if (!date) return '—'

    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)

    if (isSameDay(date, today)) return 'Oggi'
    if (isSameDay(date, yesterday)) return 'Ieri'

    return new Intl.DateTimeFormat('it-IT', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).format(date)
}

const formatTimelineTime = (value) => {
    const date = parseDate(value)
    if (!date) return '—'
    return new Intl.DateTimeFormat('it-IT', {
        hour: '2-digit',
        minute: '2-digit',
    }).format(date)
}

const fullName = (person) => {
    const value = [person?.name, person?.surname].filter(Boolean).join(' ').trim()
    return value || 'Non assegnato'
}

const taskLabel = (task) => {
    if (task?.metadata?.calendly?.status === 'scheduled') {
        return task?.type === 'first_call'
            ? 'Prima chiamata prenotata dal lead'
            : 'Chiamata prenotata dal lead'
    }
    if (task?.metadata?.migration_resume) return 'Riprendi lead migrato'
    if (task?.metadata?.automation === 'sales_recycle') return 'Ricontatta lead in riciclo'
    return TASK_LABELS[task?.type] || task?.type || '—'
}
const isTaskOverdue = (task) => {
    const dueAt = parseDate(task?.due_at)
    return task?.status === 'open' && dueAt && dueAt.getTime() < Date.now()
}
const taskDateLabel = (task) => {
    if (task?.status === 'completed') {
        return `Completato: ${formatDate(task.completed_at || task.due_at)}`
    }
    if (task?.status === 'cancelled') {
        return task.cancelled_at ? `Annullato: ${formatDate(task.cancelled_at)}` : 'Task annullato'
    }
    if (task?.type === 'contact_feedback') return 'Da completare prima della prossima azione'
    return `Scadenza: ${formatTimelineDate(task?.due_at)}, ${formatTimelineTime(task?.due_at)}`
}
const activityLabel = (activity) => ACTIVITY_LABELS[activity?.type] || activity?.type || '—'
const aircallMissedReasonLabel = (activity, reason) => {
    if (reason === 'agents_did_not_answer') {
        return activity?.direction === 'outbound'
            ? 'Il cliente non ha risposto'
            : 'Nessun operatore ha risposto'
    }
    return reason
}
const formatDuration = (seconds) => {
    const value = Number(seconds)
    if (!Number.isFinite(value) || value < 0) return null
    const minutes = Math.floor(value / 60)
    const remainingSeconds = Math.floor(value % 60)
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`
}
const activityDetails = (activity) => {
    const details = []
    const aircall = activity?.metadata?.aircall || {}
    const legacyTouch = activity?.metadata?.legacy_touch || {}
    if (aircall.simulated) details.push('Simulazione Aircall')
    if (activity?.contact_outcome) {
        details.push(CONTACT_OUTCOME_LABELS[activity.contact_outcome] || activity.contact_outcome)
    }
    if (activity?.conversation_outcome) {
        details.push(`Esito commerciale: ${CONVERSATION_OUTCOME_LABELS[activity.conversation_outcome] || activity.conversation_outcome}`)
    }
    if (activity?.metadata?.commercial_objection) {
        details.push(`Obiezione: ${QUALIFICATION_OBJECTION_LABELS[activity.metadata.commercial_objection] || activity.metadata.commercial_objection}`)
    }
    if (activity?.metadata?.reply_to_activity_id) {
        details.push(`Risposta al messaggio #${activity.metadata.reply_to_activity_id}`)
    }
    if (activity?.next_step_type) {
        const nextStep = CONTACT_NEXT_STEP_LABELS[activity.next_step_type] || activity.next_step_type
        details.push(`Prossimo passo: ${nextStep}${activity.next_step_due_at ? ` · ${formatDate(activity.next_step_due_at)}` : ''}`)
    }
    const talkDuration = formatDuration(aircall.talk_duration)
    if (talkDuration) details.push(`Conversazione: ${talkDuration}`)
    if (aircall.missed_call_reason) details.push(`Motivo: ${aircallMissedReasonLabel(activity, aircall.missed_call_reason)}`)
    if (aircall.delivery_status && activity?.type !== 'call') {
        details.push(`Consegna: ${MESSAGE_DELIVERY_STATUS_LABELS[aircall.delivery_status] || aircall.delivery_status}`)
    }
    if (aircall.conversation_status && activity?.type !== 'call') {
        details.push(`Conversazione: ${MESSAGE_CONVERSATION_STATUS_LABELS[aircall.conversation_status] || aircall.conversation_status}`)
    }
    if (aircall.tags?.length) details.push(`Tag: ${aircall.tags.join(', ')}`)
    if (legacyTouch.contact_outcome) {
        details.push(`Esito precedente: ${LEGACY_TOUCH_OUTCOME_LABELS[legacyTouch.contact_outcome] || legacyTouch.contact_outcome}`)
    }
    if (legacyTouch.lead_feedback) {
        details.push(`Feedback precedente: ${LEGACY_LEAD_FEEDBACK_LABELS[legacyTouch.lead_feedback] || legacyTouch.lead_feedback}`)
    }
    return details
}
const activityDetailFields = (activity) => {
    const fields = []
    const aircall = activity?.metadata?.aircall || {}
    const legacyTouch = activity?.metadata?.legacy_touch || {}
    if (aircall.simulated) fields.push({ label: 'Origine', value: 'Simulazione Aircall' })
    if (activity?.contact_outcome) {
        fields.push({
            label: 'Esito tecnico',
            value: CONTACT_OUTCOME_LABELS[activity.contact_outcome] || activity.contact_outcome,
        })
    }
    if (activity?.conversation_outcome) {
        fields.push({
            label: 'Esito commerciale',
            value: CONVERSATION_OUTCOME_LABELS[activity.conversation_outcome] || activity.conversation_outcome,
        })
    }
    if (activity?.metadata?.commercial_objection) {
        fields.push({
            label: 'Obiezione emersa',
            value: QUALIFICATION_OBJECTION_LABELS[activity.metadata.commercial_objection] || activity.metadata.commercial_objection,
        })
    }
    if (activity?.next_step_type) {
        const nextStep = CONTACT_NEXT_STEP_LABELS[activity.next_step_type] || activity.next_step_type
        fields.push({
            label: 'Prossimo passo',
            value: `${nextStep}${activity.next_step_due_at ? ` · ${formatDate(activity.next_step_due_at)}` : ''}`,
        })
    }
    const talkDuration = formatDuration(aircall.talk_duration)
    if (talkDuration) fields.push({ label: 'Durata conversazione', value: talkDuration })
    if (aircall.missed_call_reason) {
        fields.push({
            label: 'Motivo mancata risposta',
            value: aircallMissedReasonLabel(activity, aircall.missed_call_reason),
        })
    }
    if (aircall.delivery_status && activity?.type !== 'call') {
        fields.push({
            label: 'Consegna',
            value: MESSAGE_DELIVERY_STATUS_LABELS[aircall.delivery_status] || aircall.delivery_status,
        })
    }
    if (aircall.conversation_status && activity?.type !== 'call') {
        fields.push({
            label: 'Conversazione',
            value: MESSAGE_CONVERSATION_STATUS_LABELS[aircall.conversation_status] || aircall.conversation_status,
        })
    }
    if (aircall.tags?.length) fields.push({ label: 'Tag Aircall', value: aircall.tags.join(', ') })
    if (legacyTouch.contact_outcome) {
        fields.push({
            label: 'Esito precedente',
            value: LEGACY_TOUCH_OUTCOME_LABELS[legacyTouch.contact_outcome] || legacyTouch.contact_outcome,
        })
    }
    if (legacyTouch.lead_feedback) {
        fields.push({
            label: 'Feedback precedente',
            value: LEGACY_LEAD_FEEDBACK_LABELS[legacyTouch.lead_feedback] || legacyTouch.lead_feedback,
        })
    }
    return fields
}
const labeledLifecycleDetails = (event) => lifecycleDetails(event).map((detail) => {
    const separatorIndex = detail.indexOf(': ')
    if (separatorIndex > 0) {
        return {
            label: detail.slice(0, separatorIndex),
            value: detail.slice(separatorIndex + 2),
        }
    }
    const completedTask = detail.match(/^Task #(\d+) completato$/)
    if (completedTask) return { label: 'Task completato', value: `#${completedTask[1]}` }
    const createdTask = detail.match(/^Creato il task #(\d+)$/)
    if (createdTask) return { label: 'Task creato', value: `#${createdTask[1]}` }
    if (detail.startsWith('Pagamento ')) return { label: 'Pagamento', value: detail }
    return { label: 'Dettaglio', value: detail }
})
const isOverdue = (task) => {
    const dueAt = parseDate(task?.due_at)
    return Boolean(dueAt && dueAt < new Date())
}

const taskDateKey = (value) => {
    const date = parseDate(value)
    if (!date) return ''
    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
    ].join('-')
}

const taskColorGroup = (type) => {
    if (['first_call', 'second_call', 'third_call', 'scheduled_call', 'verify_updated_phone'].includes(type)) return 'call'
    if (['follow_up', 'nurture_follow_up'].includes(type)) return 'followup'
    if (type === 'send_whatsapp') return 'whatsapp'
    if (['send_email', 'request_phone_update_email'].includes(type)) return 'email'
    if (type === 'send_payment_link') return 'payment'
    return 'other'
}

const TASK_GROUP_LABELS = {
    call: 'Chiamate',
    followup: 'Follow-up',
    whatsapp: 'WhatsApp',
    email: 'Email',
    payment: 'Pagamenti',
    other: 'Altri task',
}
const TASK_GROUP_ORDER = ['call', 'followup', 'whatsapp', 'email', 'payment', 'other']
const TASK_VIEW_STORAGE_KEY = 'sales-applications-task-view'

const taskGroupIcon = (group) => ({
    call: PhoneIcon,
    followup: ClockIcon,
    whatsapp: WhatsappIcon,
    email: EmailIcon,
    payment: CopyIcon,
    other: CheckCircleIcon,
}[group] || CheckCircleIcon)

const TaskAgendaSkeleton = ({ calendar = false }) => calendar ? (
    <div className={`${styles.taskCalendar} ${styles.taskCalendarSkeleton}`} aria-hidden="true">
        <div className={styles.taskCalendarPicker}>
            <div className={styles.taskCalendarSkeletonHeader}>
                <div><Skeleton width="130px" height="17px" borderRadius="5px" /><Skeleton width="85px" height="9px" borderRadius="4px" /></div>
                <Skeleton width="104px" height="34px" borderRadius="7px" />
            </div>
            <div className={styles.taskCalendarSkeletonWeekdays}>{Array.from({ length: 7 }, (_, index) => <Skeleton width="42%" height="8px" borderRadius="4px" key={index} />)}</div>
            <div className={styles.taskCalendarSkeletonDays}>{Array.from({ length: 35 }, (_, index) => <div key={index}><Skeleton type="circle" width="23px" height="23px" /><Skeleton width={index % 3 === 0 ? '28px' : '18px'} height="10px" borderRadius="4px" /></div>)}</div>
        </div>
        <aside className={styles.taskCalendarAgenda}>
            <div className={styles.taskCalendarSkeletonHeader}><div><Skeleton width="82px" height="8px" borderRadius="4px" /><Skeleton width="145px" height="14px" borderRadius="4px" /></div><Skeleton width="28px" height="28px" borderRadius="7px" /></div>
            <div className={styles.taskCalendarSkeletonAgenda}>{Array.from({ length: 4 }, (_, index) => <div key={index}><Skeleton width="32px" height="32px" borderRadius="7px" /><span><Skeleton width={index % 2 ? '58%' : '72%'} height="10px" borderRadius="4px" /><Skeleton width="42%" height="9px" borderRadius="4px" /></span></div>)}</div>
        </aside>
    </div>
) : (
    <div className={styles.upcomingTasksGrid} aria-hidden="true">
        {Array.from({ length: 5 }, (_, index) => <div className={`${styles.upcomingTask} ${styles.upcomingTaskSkeleton}`} key={index}>
            <Skeleton width="36px" height="36px" borderRadius="8px" />
            <span><Skeleton width={index % 2 ? '110px' : '145px'} height="10px" borderRadius="4px" /><Skeleton width={index % 2 ? '58%' : '72%'} height="12px" borderRadius="4px" /><Skeleton width="44%" height="9px" borderRadius="4px" /></span>
        </div>)}
    </div>
)

const stageClass = (stage) => [styles.stage, styles[`stage_${stage}`]].filter(Boolean).join(' ')
const temperatureClass = (temperature) => [styles.temperature, styles[`temperature_${temperature}`]].filter(Boolean).join(' ')
const outcomeClass = (outcome) => [styles.outcome, styles[`outcome_${outcome}`]].filter(Boolean).join(' ')

const makeIdempotencyKey = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID()
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const appendSelectedFilters = (params, key, selected) => {
    (selected || []).forEach((option) => {
        if (option?.value !== undefined && option?.value !== null && option.value !== '') {
            params.append(key, String(option.value))
        }
    })
}

const checkoutLinkForOpportunity = (opportunity) => {
    const course = opportunity?.application?.course
    const email = opportunity?.lead?.email
    if (!process.env.REACT_APP_WEBAPP_URL || !course?.slug || !email) return null
    const path = course.type === CourseType.Bootcamp ? 'bootcamp' : 'courses'
    return `${process.env.REACT_APP_WEBAPP_URL}/${path}/${course.slug}/checkout?email=${encodeURIComponent(email)}`
}

const ApplicationsV2 = () => {
    const [opportunities, setOpportunities] = useState([])
    const [count, setCount] = useState(0)
    const [upcomingTasks, setUpcomingTasks] = useState([])
    const [upcomingTasksCount, setUpcomingTasksCount] = useState(0)
    const [upcomingTasksPage, setUpcomingTasksPage] = useState(0)
    const opportunitiesRequestController = useRef(null)
    const opportunitiesRequestId = useRef(null)
    const [taskView, setTaskView] = useState(() => {
        try {
            const savedView = window.localStorage.getItem(TASK_VIEW_STORAGE_KEY)
            return ['list', 'calendar'].includes(savedView) ? savedView : 'calendar'
        } catch {
            return 'calendar'
        }
    })
    const [selectedTaskTypes, setSelectedTaskTypes] = useState([])
    const [selectedTaskDueFilters, setSelectedTaskDueFilters] = useState([])
    const [taskCalendarMonth, setTaskCalendarMonth] = useState(() => {
        const date = new Date()
        return new Date(date.getFullYear(), date.getMonth(), 1)
    })
    const [selectedTaskCalendarDay, setSelectedTaskCalendarDay] = useState(() => taskDateKey(new Date()))
    const upcomingTasksRequest = useRef(0)
    const [loadingUpcomingTasks, setLoadingUpcomingTasks] = useState(false)
    const [page, setPage] = useState(0)
    const [per] = useState(25)
    const [queryInput, setQueryInput] = useState('')
    const [query, setQuery] = useState('')
    const [filtersOpen, setFiltersOpen] = useState(false)
    const [stage, setStage] = useState(() => {
        const value = new URLSearchParams(window.location.search).get('stage')
        return STAGES.some(option => option.value === value) ? value : ''
    })
    const [outcome, setOutcome] = useState('')
    const [selectedQualifications, setSelectedQualifications] = useState([])
    const [selectedTemperatures, setSelectedTemperatures] = useState([])
    const [courseOptions, setCourseOptions] = useState([])
    const [applicationOptions, setApplicationOptions] = useState([])
    const [courseSearch, setCourseSearch] = useState('')
    const [detailCourseSearch, setDetailCourseSearch] = useState('')
    const [selectedCourses, setSelectedCourses] = useState([])
    const [loadingCourses, setLoadingCourses] = useState(false)
    const [ownerOptions, setOwnerOptions] = useState([])
    const [loadingOwners, setLoadingOwners] = useState(false)
    const [arrivedFrom, setArrivedFrom] = useState(null)
    const [arrivedTo, setArrivedTo] = useState(null)
    const [selectedOwners, setSelectedOwners] = useState([])
    const [onlyMine, setOnlyMine] = useState(() => new URLSearchParams(window.location.search).get('only_mine') === 'true')
    const [includeTest, setIncludeTest] = useState(false)
    const [loading, setLoading] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [selectedId, setSelectedId] = useState(() => {
        const value = Number(new URLSearchParams(window.location.search).get('opportunity'))
        return Number.isSafeInteger(value) && value > 0 ? value : null
    })
    const [detail, setDetail] = useState(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [whatsappDraft, setWhatsappDraft] = useState(null)
    const [confirmingWhatsapp, setConfirmingWhatsapp] = useState(false)
    const [manualActivityDraft, setManualActivityDraft] = useState(null)
    const [savingManualActivity, setSavingManualActivity] = useState(false)
    const [channelIssueDraft, setChannelIssueDraft] = useState(null)
    const [savingChannelIssue, setSavingChannelIssue] = useState(false)
    const [dialingAircall, setDialingAircall] = useState(false)
    const [openingAircallMessage, setOpeningAircallMessage] = useState(false)
    const [syncingAircallContact, setSyncingAircallContact] = useState(false)
    const [aircallDialMessage, setAircallDialMessage] = useState('')
    const [aircallPanelMounted, setAircallPanelMounted] = useState(false)
    const [aircallPanelOpen, setAircallPanelOpen] = useState(false)
    const [pendingAircallDial, setPendingAircallDial] = useState(null)
    const [aircallSimulationOpen, setAircallSimulationOpen] = useState(false)
    const [aircallSimulationScenario, setAircallSimulationScenario] = useState(AIRCALL_SIMULATION_SCENARIOS[0])
    const [aircallSimulationMessage, setAircallSimulationMessage] = useState('')
    const [simulatingAircall, setSimulatingAircall] = useState(false)
    const [transitionDraft, setTransitionDraft] = useState(null)
    const [transitioning, setTransitioning] = useState(false)
    const [paymentLinkCopied, setPaymentLinkCopied] = useState(false)
    const [taskDraft, setTaskDraft] = useState(null)
    const [completingTask, setCompletingTask] = useState(false)
    const [taskScheduleDraft, setTaskScheduleDraft] = useState(null)
    const [updatingTaskSchedule, setUpdatingTaskSchedule] = useState(false)
    const [expandedCompletedTaskId, setExpandedCompletedTaskId] = useState(null)
    const [ownerDraft, setOwnerDraft] = useState(null)
    const [changingOwner, setChangingOwner] = useState(false)
    const [courseDraft, setCourseDraft] = useState(null)
    const [changingCourse, setChangingCourse] = useState(false)
    const [changingTestStatus, setChangingTestStatus] = useState(false)
    const [contactDraft, setContactDraft] = useState(null)
    const [savingContact, setSavingContact] = useState(false)

    useEffect(() => {
        setPage(0)
        setQuery(queryInput.trim())
    }, [queryInput])

    const loadCourseOptions = useCallback(async () => {
        setLoadingCourses(true)
        try {
            const [courses, applications] = await Promise.all([
                api.get('/admin/courses?complete=false'),
                api.get('/admin/applications'),
            ])
            const eligibleCourses = (courses || [])
                .filter((course) => course.type !== CourseType.Webinar)
            const coursesById = new Map(
                eligibleCourses.map((course) => [course.id, course]),
            )
            const applicationsByCourse = (applications || []).reduce((counts, application) => {
                counts.set(application.course_id, (counts.get(application.course_id) || 0) + 1)
                return counts
            }, new Map())

            setCourseOptions(
                eligibleCourses
                    .map((course) => ({ id: course.id, value: course.id, name: course.name }))
                    .sort((a, b) => a.name.localeCompare(b.name)),
            )
            setApplicationOptions(
                (applications || [])
                    .filter((application) => coursesById.has(application.course_id))
                    .map((application) => {
                        const course = coursesById.get(application.course_id)
                        const hasMultipleApplications = applicationsByCourse.get(application.course_id) > 1
                        return {
                            id: application.id,
                            value: application.id,
                            courseId: application.course_id,
                            name: hasMultipleApplications
                                ? `${course.name} · ${application.name}`
                                : course.name,
                        }
                    })
                    .sort((a, b) => a.name.localeCompare(b.name)),
            )
        } catch {
            setCourseOptions([])
            setApplicationOptions([])
        } finally {
            setLoadingCourses(false)
        }
    }, [])

    useEffect(() => {
        loadCourseOptions()
    }, [loadCourseOptions])

    useEffect(() => {
        const loadOwners = async () => {
            setLoadingOwners(true)
            try {
                const admins = await api.get('/admin/admins')
                setOwnerOptions(
                    (admins || [])
                        .filter((admin) => admin.active_id)
                        .map((admin) => ({
                            id: admin.id,
                            value: admin.id,
                            name: fullName(admin),
                        }))
                        .sort((a, b) => a.name.localeCompare(b.name)),
                )
            } catch {
                setOwnerOptions([])
            } finally {
                setLoadingOwners(false)
            }
        }

        loadOwners()
    }, [])

    const filteredCourseOptions = useMemo(() => {
        const normalizedSearch = courseSearch.trim().toLocaleLowerCase('it')
        if (!normalizedSearch) return courseOptions

        return courseOptions.filter((course) =>
            course.name.toLocaleLowerCase('it').includes(normalizedSearch),
        )
    }, [courseOptions, courseSearch])

    const detailCourseOptions = useMemo(() => {
        const normalizedSearch = detailCourseSearch.trim().toLocaleLowerCase('it')
        if (!normalizedSearch) return applicationOptions

        return applicationOptions.filter((application) =>
            application.name.toLocaleLowerCase('it').includes(normalizedSearch),
        )
    }, [applicationOptions, detailCourseSearch])

    const arrivalDateOptions = useMemo(() => ({
        css: [
            'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css',
            'https://cdn.jsdelivr.net/npm/@easepick/range-plugin@1.2.1/dist/index.css',
            '/css/easepick-range.css',
        ],
        zIndex: 1000,
        plugins: [RangePlugin, PresetPlugin, AmpPlugin],
        lang: 'it-IT',
        format: 'DD MMM YYYY',
        AmpPlugin: {
            resetButton: () => {
                setPage(0)
                setArrivedFrom(null)
                setArrivedTo(null)
                return true
            },
            darkMode: false,
            dropdown: {
                months: true,
                years: true,
                minYear: 2020,
            },
        },
        PresetPlugin: {
            customPreset: {
                Oggi: [
                    new Date(new Date().setHours(0, 0, 0, 0)),
                    new Date(new Date().setHours(23, 59, 59, 999)),
                ],
                '7 giorni': [
                    (() => {
                        const date = new Date()
                        date.setDate(date.getDate() - 6)
                        return new Date(date.setHours(0, 0, 0, 0))
                    })(),
                    new Date(new Date().setHours(23, 59, 59, 999)),
                ],
                '30 giorni': [
                    (() => {
                        const date = new Date()
                        date.setDate(date.getDate() - 29)
                        return new Date(date.setHours(0, 0, 0, 0))
                    })(),
                    new Date(new Date().setHours(23, 59, 59, 999)),
                ],
                Mese: [
                    new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0),
                    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59, 999),
                ],
                Anno: [
                    new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0),
                    new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999),
                ],
            },
        },
        setup(picker) {
            picker.on('select', (event) => {
                const { start, end } = event.detail
                const rangeStart = new Date(start)
                const rangeEnd = new Date(end)
                rangeStart.setHours(0, 0, 0, 0)
                rangeEnd.setHours(23, 59, 59, 999)
                setPage(0)
                setArrivedFrom(rangeStart)
                setArrivedTo(rangeEnd)
            })
        },
    }), [])

    const loadOpportunities = useCallback(async () => {
        opportunitiesRequestController.current?.abort()
        const controller = new AbortController()
        const requestId = Symbol('sales-opportunities-request')
        opportunitiesRequestController.current = controller
        opportunitiesRequestId.current = requestId
        setLoading(true)
        try {
            const params = new URLSearchParams({
                page: String(page),
                per: String(per),
                only_assigned_to_me: String(onlyMine),
                include_closed: String(['all', 'closed'].includes(stage)),
                include_test: String(includeTest),
            })
            appendSelectedFilters(params, 'owner_id', selectedOwners)
            if (query) params.set('q', query)
            if (stage && stage !== 'all') params.set('stage', stage)
            if (stage === 'closed' && outcome) params.set('outcome', outcome)
            appendSelectedFilters(params, 'qualification_status', selectedQualifications)
            appendSelectedFilters(params, 'temperature', selectedTemperatures)
            appendSelectedFilters(params, 'course_id', selectedCourses)
            if (arrivedFrom instanceof Date) params.set('arrived_from', arrivedFrom.toISOString())
            if (arrivedTo instanceof Date) params.set('arrived_to', arrivedTo.toISOString())
            const response = await api.get(
                `/admin/sales/opportunities?${params.toString()}`,
                { signal: controller.signal },
            )
            if (opportunitiesRequestId.current !== requestId) return
            setOpportunities(response.opportunities || [])
            setCount(response.count || 0)
        } catch (error) {
            if (
                controller.signal.aborted
                || error?.code === 'ERR_CANCELED'
                || error?.name === 'CanceledError'
                || error?.name === 'AbortError'
            ) return
            // The global API handler already exposes the error to the user.
        } finally {
            if (opportunitiesRequestId.current === requestId) {
                setLoading(false)
                opportunitiesRequestController.current = null
                opportunitiesRequestId.current = null
            }
        }
    }, [arrivedFrom, arrivedTo, includeTest, onlyMine, outcome, page, per, query, selectedCourses, selectedOwners, selectedQualifications, selectedTemperatures, stage])

    const exportOpportunities = useCallback(async () => {
        if (exporting) return

        setExporting(true)
        try {
            const params = new URLSearchParams({
                only_assigned_to_me: String(onlyMine),
                include_closed: String(['all', 'closed'].includes(stage)),
                include_test: String(includeTest),
            })
            appendSelectedFilters(params, 'owner_id', selectedOwners)
            if (query) params.set('q', query)
            if (stage && stage !== 'all') params.set('stage', stage)
            if (stage === 'closed' && outcome) params.set('outcome', outcome)
            appendSelectedFilters(params, 'qualification_status', selectedQualifications)
            appendSelectedFilters(params, 'temperature', selectedTemperatures)
            appendSelectedFilters(params, 'course_id', selectedCourses)
            if (arrivedFrom instanceof Date) params.set('arrived_from', arrivedFrom.toISOString())
            if (arrivedTo instanceof Date) params.set('arrived_to', arrivedTo.toISOString())

            const csv = await api.get(`/admin/sales/opportunities/export/csv?${params.toString()}`, {
                responseType: 'blob',
                timeout: 0,
            })
            const blob = csv instanceof Blob ? csv : new Blob([csv], { type: 'text/csv;charset=utf-8' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `opportunita-commerciali-${new Date().toISOString().slice(0, 10)}.csv`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch {
            // The global API handler already exposes the export error to the user.
        } finally {
            setExporting(false)
        }
    }, [arrivedFrom, arrivedTo, exporting, includeTest, onlyMine, outcome, query, selectedCourses, selectedOwners, selectedQualifications, selectedTemperatures, stage])

    const taskTypeFilterKey = taskView === 'list'
        ? selectedTaskTypes.map((option) => option.value).sort().join(',')
        : ''

    useEffect(() => {
        try {
            window.localStorage.setItem(TASK_VIEW_STORAGE_KEY, taskView)
        } catch {
            // The selected view remains available for the current session.
        }
    }, [taskView])

    useEffect(() => {
        setUpcomingTasksPage(0)
    }, [includeTest, selectedTaskDueFilters, taskTypeFilterKey, taskView])

    const loadUpcomingTasks = useCallback(async () => {
        const request = ++upcomingTasksRequest.current
        setLoadingUpcomingTasks(true)
        try {
            const params = new URLSearchParams({
                limit: taskView === 'calendar' ? '1000' : '5',
                page: taskView === 'calendar' ? '0' : String(upcomingTasksPage),
                include_test: String(includeTest),
            })
            taskTypeFilterKey.split(',').filter(Boolean).forEach((value) => params.append('task_types', value))
            if (taskView !== 'calendar') selectedTaskDueFilters.forEach((option) => params.append('due_filters', option.value))
            if (taskView === 'calendar') {
                const monthStart = new Date(taskCalendarMonth.getFullYear(), taskCalendarMonth.getMonth(), 1)
                const monthEnd = new Date(taskCalendarMonth.getFullYear(), taskCalendarMonth.getMonth() + 1, 1)
                params.set('calendar', 'true')
                params.set('due_from', monthStart.toISOString())
                params.set('due_to', monthEnd.toISOString())
            }
            const response = await api.get(`/admin/sales/tasks/upcoming?${params.toString()}`)
            if (request !== upcomingTasksRequest.current) return
            setUpcomingTasks(response.tasks || [])
            setUpcomingTasksCount(response.count || 0)
            if (Number.isInteger(response.page)) setUpcomingTasksPage(response.page)
        } catch {
            if (request !== upcomingTasksRequest.current) return
            setUpcomingTasks([])
            setUpcomingTasksCount(0)
        } finally {
            if (request === upcomingTasksRequest.current) setLoadingUpcomingTasks(false)
        }
    }, [includeTest, selectedTaskDueFilters, taskTypeFilterKey, taskCalendarMonth, taskView, upcomingTasksPage])

    const loadDetail = useCallback(async (opportunityId) => {
        setDetailLoading(true)
        try {
            const response = await api.get(`/admin/sales/opportunities/${opportunityId}`)
            setDetail(response)
        } catch {
            setDetail(null)
        } finally {
            setDetailLoading(false)
        }
    }, [])

    useEffect(() => {
        loadOpportunities()
        return () => opportunitiesRequestController.current?.abort()
    }, [loadOpportunities])

    useEffect(() => {
        loadUpcomingTasks()
    }, [loadUpcomingTasks])

    useEffect(() => {
        setWhatsappDraft(null)
        setManualActivityDraft(null)
        setAircallDialMessage('')
        setTransitionDraft(null)
        setTaskDraft(null)
        setTaskScheduleDraft(null)
        setExpandedCompletedTaskId(null)
        setOwnerDraft(null)
        setCourseDraft(null)
        setContactDraft(null)
        setChannelIssueDraft(null)
        setDetailCourseSearch('')
        setAircallSimulationOpen(false)
        setAircallSimulationMessage('')
        if (selectedId) loadDetail(selectedId)
        else setDetail(null)
    }, [loadDetail, selectedId])

    useEffect(() => {
        if (!selectedId) return undefined

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [selectedId])

    useEffect(() => {
        if (!selectedId) return undefined
        const interval = window.setInterval(() => {
            if (document.visibilityState === 'visible') loadDetail(selectedId)
        }, 10000)
        return () => window.clearInterval(interval)
    }, [loadDetail, selectedId])

    useEffect(() => {
        const handleAircallSimulationShortcut = (event) => {
            if (
                event.code !== 'KeyA'
                || !event.shiftKey
                || !event.altKey
                || (!event.metaKey && !event.ctrlKey)
                || !detail?.test
                || !detail?.aircall_webhook_simulator_enabled
            ) return
            event.preventDefault()
            setAircallSimulationOpen((current) => !current)
            setAircallSimulationMessage('')
        }
        window.addEventListener('keydown', handleAircallSimulationShortcut)
        return () => window.removeEventListener('keydown', handleAircallSimulationShortcut)
    }, [detail?.aircall_webhook_simulator_enabled, detail?.test])

    const openTasks = useMemo(
        () => (detail?.tasks || []).filter((task) => task.status === 'open'),
        [detail],
    )
    const sortedTasks = useMemo(
        () => [...(detail?.tasks || [])].sort((firstTask, secondTask) => {
            const statusOrder = { open: 0, completed: 1, cancelled: 2 }
            const statusDifference = (statusOrder[firstTask.status] ?? 3) - (statusOrder[secondTask.status] ?? 3)
            if (statusDifference !== 0) return statusDifference
            if (firstTask.status === 'open') {
                return (parseDate(firstTask.due_at)?.getTime() || 0) - (parseDate(secondTask.due_at)?.getTime() || 0)
            }
            const firstEventAt = firstTask.status === 'completed' ? firstTask.completed_at : firstTask.cancelled_at
            const secondEventAt = secondTask.status === 'completed' ? secondTask.completed_at : secondTask.cancelled_at
            return (parseDate(secondEventAt || secondTask.due_at)?.getTime() || 0)
                - (parseDate(firstEventAt || firstTask.due_at)?.getTime() || 0)
        }),
        [detail],
    )
    const selectedTaskTypeValues = useMemo(
        () => new Set(selectedTaskTypes.map((option) => option.value)),
        [selectedTaskTypes],
    )
    const calendarTasks = useMemo(
        () => selectedTaskTypeValues.size === 0
            ? upcomingTasks
            : upcomingTasks.filter((task) => selectedTaskTypeValues.has(task.type)),
        [selectedTaskTypeValues, upcomingTasks],
    )
    const taskCalendarCells = useMemo(() => {
        const monthStart = new Date(taskCalendarMonth.getFullYear(), taskCalendarMonth.getMonth(), 1)
        const mondayOffset = (monthStart.getDay() + 6) % 7
        const gridStart = new Date(monthStart)
        gridStart.setDate(monthStart.getDate() - mondayOffset)
        const daysInMonth = new Date(taskCalendarMonth.getFullYear(), taskCalendarMonth.getMonth() + 1, 0).getDate()
        const calendarDayCount = Math.ceil((mondayOffset + daysInMonth) / 7) * 7
        const tasksByDay = (calendarTasks || []).reduce((groups, task) => {
            const key = taskDateKey(task.due_at)
            if (!key) return groups
            if (!groups[key]) groups[key] = []
            groups[key].push(task)
            return groups
        }, {})
        return Array.from({ length: calendarDayCount }, (_, index) => {
            const date = new Date(gridStart)
            date.setDate(gridStart.getDate() + index)
            const key = taskDateKey(date)
            const tasks = tasksByDay[key] || []
            const summaries = Object.entries(tasks.reduce((groups, task) => {
                const group = taskColorGroup(task.type)
                groups[group] = (groups[group] || 0) + 1
                return groups
            }, {}))
                .map(([group, count]) => ({ group, count }))
                .sort((first, second) => TASK_GROUP_ORDER.indexOf(first.group) - TASK_GROUP_ORDER.indexOf(second.group))
            return {
                key,
                date,
                currentMonth: date.getMonth() === taskCalendarMonth.getMonth(),
                today: taskDateKey(date) === taskDateKey(new Date()),
                tasks,
                summaries,
            }
        })
    }, [calendarTasks, taskCalendarMonth])
    const selectedTaskCalendarTasks = useMemo(
        () => (calendarTasks || [])
            .filter((task) => taskDateKey(task.due_at) === selectedTaskCalendarDay)
            .sort((first, second) => (parseDate(first.due_at)?.getTime() || 0) - (parseDate(second.due_at)?.getTime() || 0)),
        [calendarTasks, selectedTaskCalendarDay],
    )
    const selectedTaskCalendarLabel = useMemo(() => {
        const date = new Date(`${selectedTaskCalendarDay}T12:00:00`)
        return date ? new Intl.DateTimeFormat('it-IT', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
        }).format(date) : 'Giorno selezionato'
    }, [selectedTaskCalendarDay])
    const navigateTaskCalendar = (delta) => {
        const nextMonth = new Date(taskCalendarMonth.getFullYear(), taskCalendarMonth.getMonth() + delta, 1)
        setTaskCalendarMonth(nextMonth)
        setSelectedTaskCalendarDay(taskDateKey(nextMonth))
    }
    const selectTaskCalendarDay = (cell) => {
        setSelectedTaskCalendarDay(cell.key)
        if (!cell.currentMonth) setTaskCalendarMonth(new Date(cell.date.getFullYear(), cell.date.getMonth(), 1))
    }
    const toggleCalendarTaskFilter = (filter) => {
        const removeFilter = filter.values.some((value) => selectedTaskTypeValues.has(value))
        setSelectedTaskTypes((current) => removeFilter
            ? current.filter((option) => !filter.values.includes(option.value))
            : [
                ...current,
                ...TASK_FILTER_OPTIONS.filter((option) => filter.values.includes(option.value) && !current.some((selected) => selected.value === option.value)),
            ])
    }
    const taskOptions = useMemo(
        () => openTasks.map((task) => ({
            id: task.id,
            value: task.id,
            name: `${taskLabel(task)} · ${formatDate(task.due_at)}`,
        })),
        [openTasks],
    )
    const manuallyRepliedActivityIds = useMemo(
        () => new Set((detail?.activities || [])
            .map((activity) => Number(activity.metadata?.reply_to_activity_id))
            .filter((activityId) => Number.isSafeInteger(activityId) && activityId > 0)),
        [detail],
    )

    const openWhatsapp = () => {
        const phone = String(detail?.lead?.phone || '').replace(/[^0-9]/g, '')
        if (!phone) return
        const defaultFollowUpAt = new Date()
        defaultFollowUpAt.setDate(defaultFollowUpAt.getDate() + 1)
        setTransitionDraft(null)
        setManualActivityDraft(null)
        setOwnerDraft(null)
        setCourseDraft(null)
        window.open(`https://wa.me/${phone}`, '_blank', 'noopener,noreferrer')
        setWhatsappDraft({
            idempotencyKey: makeIdempotencyKey(),
            sentAt: new Date().toISOString(),
            taskId: '',
            contactOutcome: 'sent',
            conversationOutcome: '',
            commercialObjection: '',
            nextStepType: 'follow_up',
            nextStepDueAt: defaultFollowUpAt,
            notes: '',
        })
    }

    const openTaskWhatsapp = () => {
        const phone = String(detail?.lead?.phone || '').replace(/[^0-9]/g, '')
        if (!phone || !taskDraft) return
        const defaultFollowUpAt = new Date()
        defaultFollowUpAt.setDate(defaultFollowUpAt.getDate() + 1)
        window.open(`https://wa.me/${phone}`, '_blank', 'noopener,noreferrer')
        if (['first_call', 'second_call', 'third_call'].includes(taskDraft.taskType)) {
            setTaskDraft(null)
            setWhatsappDraft({
                idempotencyKey: makeIdempotencyKey(),
                sentAt: new Date().toISOString(),
                taskId: '',
                contactOutcome: 'sent',
                conversationOutcome: '',
                commercialObjection: '',
                nextStepType: 'follow_up',
                nextStepDueAt: defaultFollowUpAt,
                notes: '',
            })
            return
        }
        setTaskDraft({
            ...taskDraft,
            activityType: 'whatsapp',
            contactOutcome: 'sent',
            conversationOutcome: '',
            nextStepType: 'follow_up',
            nextStepDueAt: defaultFollowUpAt,
        })
    }

    const whatsappDraftIsValid = Boolean(
        whatsappDraft?.contactOutcome
        && whatsappDraft?.nextStepType
        && (whatsappDraft.contactOutcome !== 'received' || whatsappDraft.conversationOutcome)
        && (!['follow_up', 'scheduled_call', 'postpone'].includes(whatsappDraft.nextStepType) || whatsappDraft.nextStepDueAt instanceof Date),
    )

    const openManualActivity = () => {
        setWhatsappDraft(null)
        setTransitionDraft(null)
        setTaskDraft(null)
        setTaskScheduleDraft(null)
        setOwnerDraft(null)
        setCourseDraft(null)
        setContactDraft(null)
        setManualActivityDraft({
            idempotencyKey: makeIdempotencyKey(),
            replyToActivityId: null,
            activityType: 'whatsapp',
            contactOutcome: 'sent',
            conversationOutcome: '',
            notes: '',
        })
    }

    const openTaskChannelIssue = (task) => {
        setChannelIssueDraft({
            taskId: task.id,
            channel: task.type === 'send_whatsapp' ? 'whatsapp' : 'call',
            idempotencyKey: makeIdempotencyKey(),
            issueType: '',
            notes: '',
        })
    }

    const saveChannelIssue = async () => {
        if (!detail || !channelIssueDraft?.issueType || savingChannelIssue) return
        setSavingChannelIssue(true)
        try {
            const response = await api.post(
                `/admin/sales/opportunities/${detail.id}/tasks/${channelIssueDraft.taskId}/channel-issue`,
                {
                    idempotency_key: channelIssueDraft.idempotencyKey,
                    expected_version: detail.version,
                    issue_type: channelIssueDraft.issueType,
                    notes: channelIssueDraft.notes.trim() || null,
                },
            )
            setDetail(response.opportunity)
            setChannelIssueDraft(null)
            await Promise.all([loadOpportunities(), loadUpcomingTasks()])
        } finally {
            setSavingChannelIssue(false)
        }
    }

    const openManualActivityReply = (activity) => {
        setWhatsappDraft(null)
        setTransitionDraft(null)
        setTaskDraft(null)
        setManualActivityDraft({
            idempotencyKey: makeIdempotencyKey(),
            replyToActivityId: activity.id,
            activityType: 'whatsapp',
            contactOutcome: 'received',
            conversationOutcome: '',
            notes: '',
        })
    }

    const manualActivityIsValid = Boolean(
        manualActivityDraft?.activityType && manualActivityDraft?.contactOutcome,
    )

    const saveManualActivity = async () => {
        if (!detail || !manualActivityIsValid || savingManualActivity) return
        setSavingManualActivity(true)
        try {
            const response = await api.post(
                `/admin/sales/opportunities/${detail.id}/activities/manual/standalone`,
                {
                    idempotency_key: manualActivityDraft.idempotencyKey,
                    reply_to_activity_id: manualActivityDraft.replyToActivityId,
                    activity_type: manualActivityDraft.activityType,
                    contact_outcome: manualActivityDraft.contactOutcome,
                    conversation_outcome: manualActivityDraft.conversationOutcome || null,
                    occurred_at: new Date().toISOString(),
                    notes: manualActivityDraft.notes.trim() || null,
                },
            )
            setDetail(response.opportunity)
            setManualActivityDraft(null)
        } catch {
            // Preserve the idempotency key so the activity can be retried safely.
        } finally {
            setSavingManualActivity(false)
        }
    }

    const callWithAircall = async () => {
        if (!detail?.id || !detail.lead?.phone) return
        setDialingAircall(true)
        setAircallDialMessage('')
        setAircallPanelMounted(true)
        setAircallPanelOpen(true)
        try {
            let phoneNumber = detail.aircall_contact?.phone_number
            if (detail.aircall_contact?.status !== 'synced' || !phoneNumber) {
                const response = await api.post(
                    `/admin/sales/opportunities/${detail.id}/aircall/contact/sync`,
                )
                phoneNumber = response.contact?.phone_number
                await loadDetail(detail.id)
            }
            if (!phoneNumber) return
            await api.post(
                `/admin/sales/opportunities/${detail.id}/aircall/call-context`,
            )
            setPendingAircallDial({
                id: makeIdempotencyKey(),
                phoneNumber,
            })
            setAircallDialMessage('Apri il dialer Aircall per avviare la chiamata')
        } catch {
            // The global API handler exposes contact synchronization errors.
        } finally {
            setDialingAircall(false)
        }
    }

    const messageWithAircall = async () => {
        if (!detail?.id || !detail.lead?.phone) return
        setOpeningAircallMessage(true)
        setAircallDialMessage('')
        setAircallPanelMounted(true)
        setAircallPanelOpen(true)
        try {
            if (detail.aircall_contact?.status !== 'synced' || !detail.aircall_contact?.phone_number) {
                await api.post(`/admin/sales/opportunities/${detail.id}/aircall/contact/sync`)
            }
            await api.post(
                `/admin/sales/opportunities/${detail.id}/aircall/message-context`,
            )
            setAircallDialMessage('Apri la conversazione Aircall e invia il messaggio')
            await loadDetail(detail.id)
        } catch {
            // The global API handler exposes contact/context errors.
        } finally {
            setOpeningAircallMessage(false)
        }
    }

    const syncAircallContact = async () => {
        if (!detail?.id || !detail.lead?.phone) return
        setSyncingAircallContact(true)
        setAircallDialMessage('')
        try {
            const response = await api.post(
                `/admin/sales/opportunities/${detail.id}/aircall/contact/sync`,
            )
            setAircallDialMessage(response.message || 'Contatto Aircall sincronizzato')
            await loadDetail(detail.id)
        } catch {
            await loadDetail(detail.id)
        } finally {
            setSyncingAircallContact(false)
        }
    }

    const openContactEdit = () => {
        if (!detail?.lead) return
        setWhatsappDraft(null)
        setTransitionDraft(null)
        setOwnerDraft(null)
        setCourseDraft(null)
        setTaskDraft(null)
        setAircallDialMessage('')
        setContactDraft({
            name: detail.lead.name || '',
            surname: detail.lead.surname || '',
            email: detail.lead.email || '',
            phone: detail.lead.phone || '',
            idempotencyKey: makeIdempotencyKey(),
        })
    }

    const contactDraftHasChanges = contactDraft && ['name', 'surname', 'email', 'phone'].some(
        (field) => contactDraft[field].trim() !== String(detail?.lead?.[field] || '').trim(),
    )
    const contactDraftIsValid = Boolean(contactDraft
        && contactDraft.name.trim()
        && contactDraft.surname.trim()
        && contactDraft.email.trim()
        && contactDraft.phone.trim()
        && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contactDraft.email.trim())
        && contactDraftHasChanges)

    const saveContact = async () => {
        if (!detail || !contactDraftIsValid || savingContact) return
        setSavingContact(true)
        setAircallDialMessage('')
        try {
            const response = await api.put(
                `/admin/sales/opportunities/${detail.id}/contact`,
                {
                    name: contactDraft.name.trim(),
                    surname: contactDraft.surname.trim(),
                    email: contactDraft.email.trim(),
                    phone: contactDraft.phone.trim(),
                    idempotency_key: contactDraft.idempotencyKey,
                    expected_version: detail.version,
                },
            )
            setDetail(response.opportunity)
            setContactDraft(null)
            setAircallDialMessage(
                response.aircall_sync?.status === 'pending'
                    ? 'Dati salvati. Sincronizzazione Aircall in attesa.'
                    : 'Dati di contatto e contatto Aircall aggiornati.',
            )
            await Promise.all([loadOpportunities(), loadUpcomingTasks()])
        } catch {
            // Keep the draft and idempotency key so the operation can be retried safely.
        } finally {
            setSavingContact(false)
        }
    }

    const simulateAircallWebhook = async () => {
        if (!detail?.id || !aircallSimulationScenario?.value) return
        setSimulatingAircall(true)
        setAircallSimulationMessage('')
        try {
            const response = await api.post(
                `/admin/sales/opportunities/${detail.id}/aircall/webhook/simulate`,
                { scenario: aircallSimulationScenario.value },
            )
            const processedEvents = response.events?.length || 0
            const simulatedTags = (response.tags || []).map((tag) => tag.name).filter(Boolean)
            const tagsMessage = simulatedTags.length
                ? ` Tag scelti: ${simulatedTags.join(' · ')}.`
                : ''
            setAircallSimulationMessage(
                `${processedEvents} ${processedEvents === 1 ? 'evento simulato' : 'eventi simulati'} correttamente.${tagsMessage}`,
            )
            await Promise.all([loadDetail(detail.id), loadOpportunities(), loadUpcomingTasks()])
        } catch {
            // The global API handler exposes simulator errors.
        } finally {
            setSimulatingAircall(false)
        }
    }

    const confirmWhatsapp = async () => {
        if (!detail || !whatsappDraftIsValid || confirmingWhatsapp) return
        setConfirmingWhatsapp(true)
        try {
            const subsequentAction = whatsappDraft.nextStepType === 'qualify'
                ? buildTransitionDraft(whatsappDraft.nextStepType)
                : null
            const response = await api.post(
                `/admin/sales/opportunities/${detail.id}/activities/whatsapp/manual`,
                {
                    idempotency_key: whatsappDraft.idempotencyKey,
                    task_id: whatsappDraft.taskId ? Number(whatsappDraft.taskId) : null,
                    sent_at: whatsappDraft.sentAt,
                    contact_outcome: whatsappDraft.contactOutcome,
                    conversation_outcome: whatsappDraft.conversationOutcome || null,
                    commercial_objection: whatsappDraft.commercialObjection || null,
                    next_step_type: whatsappDraft.nextStepType,
                    next_step_due_at: ['follow_up', 'scheduled_call', 'postpone'].includes(whatsappDraft.nextStepType)
                        ? whatsappDraft.nextStepDueAt.toISOString()
                        : null,
                    notes: whatsappDraft.notes.trim() || null,
                },
            )
            setDetail(response.opportunity)
            setWhatsappDraft(null)
            if (subsequentAction) {
                setTransitionDraft({ ...subsequentAction, completedTaskId: '' })
            }
            await Promise.all([loadDetail(detail.id), loadOpportunities(), loadUpcomingTasks()])
        } catch {
            // Keep the confirmation form open so the same idempotency key can be retried.
        } finally {
            setConfirmingWhatsapp(false)
        }
    }

    const buildTransitionDraft = (action, qualificationPrefill = {}) => {
        const defaultFollowUpAt = new Date()
        defaultFollowUpAt.setDate(defaultFollowUpAt.getDate() + 1)
        const defaultRecycleAt = new Date()
        defaultRecycleAt.setDate(defaultRecycleAt.getDate() + 90)
        const isNonQualified = action === 'mark_unqualified'
        return {
            action,
            idempotencyKey: makeIdempotencyKey(),
            completedTaskId: detail?.next_task?.id || '',
            recycleAt: ['postpone', 'qualify'].includes(action) ? defaultRecycleAt : null,
            nextTaskDueAt: ['schedule_follow_up', 'qualify'].includes(action) ? defaultFollowUpAt : null,
            nextTaskType: 'follow_up',
            qualificationStep: 0,
            lossReason: '',
            qualificationReason: '',
            negativeClosureType: '',
            negativeClosureReason: '',
            qualificationFeedback: {
                interest: isNonQualified ? 'not_assessed' : '',
                product_fit: isNonQualified ? 'not_assessed' : '',
                timing: isNonQualified ? 'not_assessed' : '',
                budget: isNonQualified ? 'not_assessed' : '',
                objection: '',
                next_step: isNonQualified ? 'stop' : '',
                ...qualificationPrefill,
            },
            notes: '',
        }
    }

    const openTransition = (action) => {
        setWhatsappDraft(null)
        setManualActivityDraft(null)
        setTaskDraft(null)
        setTaskScheduleDraft(null)
        setOwnerDraft(null)
        setCourseDraft(null)
        setContactDraft(null)
        const draft = buildTransitionDraft(action)
        if (action === 'close_negative') {
            const closureTypes = availableNegativeClosureTypes(detail?.available_actions || [])
            if (closureTypes.length === 1) {
                draft.negativeClosureType = closureTypes[0].value
            }
        }
        setTransitionDraft(draft)
    }

    const openTaskCompletion = (task) => {
        if (!detail || task.status !== 'open') return
        if (taskDraft?.taskId === task.id) {
            setTaskDraft(null)
            setTaskScheduleDraft(null)
            return
        }
        setWhatsappDraft(null)
        setTransitionDraft(null)
        setOwnerDraft(null)
        setCourseDraft(null)
        setContactDraft(null)
        setChannelIssueDraft(null)
        setExpandedCompletedTaskId(null)
        setTaskScheduleDraft(null)
        const defaultFollowUpAt = new Date()
        defaultFollowUpAt.setDate(defaultFollowUpAt.getDate() + 1)
        const sourceActivity = task.type === 'contact_feedback'
            ? (detail.activities || []).find((activity) => activity.id === Number(task.source_activity_id))
            : null
        if (task.type === 'contact_feedback' && !sourceActivity) return
        const sequenceActivityType = {
            first_call: 'call',
            second_call: 'call',
            third_call: 'call',
            verify_updated_phone: 'call',
            scheduled_call: 'call',
            send_whatsapp: 'whatsapp',
            send_email: 'email',
        }[task.type] || ''
        const sequenceOutcomes = MANUAL_ACTIVITY_OUTCOMES[sequenceActivityType] || []
        const sequenceContactOutcome = sequenceOutcomes.length === 1
            ? sequenceOutcomes[0].value
            : ''
        const aircallSuggestion = task.metadata?.aircall_feedback_suggestion || null
        const rawSuggestedConversationOutcome = aircallSuggestion?.status === 'ready'
            ? aircallSuggestion.conversation_outcome || ''
            : ''
        const normalizedSuggestedConversationOutcome = rawSuggestedConversationOutcome === 'not_interested'
            && ['qualification', 'closing'].includes(detail.stage)
            ? 'not_proceeding'
            : rawSuggestedConversationOutcome
        const availableConversationOutcomes = new Set(
            conversationOutcomesForStage(detail.stage, detail.qualification_status).map((item) => item.value),
        )
        const suggestedConversationOutcome = availableConversationOutcomes.has(normalizedSuggestedConversationOutcome)
            ? normalizedSuggestedConversationOutcome
            : (normalizedSuggestedConversationOutcome && detail.stage !== 'closing' ? 'qualification' : '')
        const aircallSuggestionIncompatible = Boolean(
            normalizedSuggestedConversationOutcome
            && !availableConversationOutcomes.has(normalizedSuggestedConversationOutcome)
            && detail.stage === 'closing',
        )
        setTaskDraft({
            taskId: task.id,
            taskType: task.type,
            mode: task.type === 'contact_feedback' ? 'feedback' : 'activity',
            activityId: sourceActivity?.id || null,
            idempotencyKey: makeIdempotencyKey(),
            activityType: sourceActivity?.type || sequenceActivityType,
            contactOutcome: sourceActivity?.contact_outcome || sequenceContactOutcome,
            conversationOutcome: suggestedConversationOutcome,
            commercialObjection: CONTACT_OBJECTIONS.some((item) => item.value === aircallSuggestion?.qualification_prefill?.objection)
                ? aircallSuggestion.qualification_prefill.objection
                : '',
            nextStepType: suggestedConversationOutcome
                ? nextStepForConversation(suggestedConversationOutcome)
                : (sequenceContactOutcome && AUTOMATED_CONTACT_SEQUENCE_TASKS.has(task.type)
                    ? 'continue_sequence'
                    : ''),
            nextStepDueAt: defaultFollowUpAt,
            manualContactOpen: false,
            paymentLinkChannel: 'whatsapp',
            notes: '',
            aircallSuggestion,
            aircallSuggestionIncompatible,
        })
    }

    const toggleCompletedTask = (task) => {
        if (task.status !== 'completed') return
        setTaskDraft(null)
        setTaskScheduleDraft(null)
        setChannelIssueDraft(null)
        setExpandedCompletedTaskId((current) => current === task.id ? null : task.id)
    }

    const nextStepForConversation = (conversationOutcome) => {
        if (conversationOutcome === 'call_scheduled') return 'scheduled_call'
        if ([
            'information_sent',
            'awaiting_payment',
            'payment_issue',
            'financing_pending',
            'decision_pending',
            'clarification_requested',
        ].includes(conversationOutcome)) return 'follow_up'
        if (conversationOutcome === 'not_interested') return 'mark_unqualified'
        if (conversationOutcome === 'not_proceeding') {
            return detail?.qualification_status === 'lq' ? 'mark_lost' : 'mark_unqualified'
        }
        if (conversationOutcome === 'next_edition') return 'postpone'
        if (['wrong_intent', 'out_of_target'].includes(conversationOutcome)) return 'mark_unqualified'
        if (conversationOutcome === 'qualification') {
            return detail?.stage === 'qualification' ? 'qualify' : 'start_qualification'
        }
        return 'follow_up'
    }

    const nextStepDateForConversation = (conversationOutcome, currentValue) => {
        if (conversationOutcome !== 'next_edition') return currentValue
        const recycleAt = new Date()
        recycleAt.setDate(recycleAt.getDate() + 90)
        return recycleAt
    }

    const nextStepForTaskContact = (taskType, contactOutcome) => {
        if (
            AUTOMATED_CONTACT_SEQUENCE_TASKS.has(taskType)
            && ['missed', 'unanswered', 'sent'].includes(contactOutcome)
        ) return 'continue_sequence'
        return contactOutcome && !['answered', 'received'].includes(contactOutcome)
            ? 'follow_up'
            : ''
    }

    const taskActivityIsValid = Boolean(
        taskDraft?.activityType
        && taskDraft?.contactOutcome
        && taskDraft?.nextStepType
        && (!['answered', 'received'].includes(taskDraft.contactOutcome) || taskDraft.conversationOutcome)
        && (!['follow_up', 'scheduled_call', 'postpone'].includes(taskDraft.nextStepType) || taskDraft.nextStepDueAt instanceof Date),
    )

    const taskScheduleIsValid = Boolean(
        taskScheduleDraft
        && ['follow_up', 'scheduled_call'].includes(taskScheduleDraft.taskType)
        && taskScheduleDraft.dueAt instanceof Date
        && taskScheduleDraft.dueAt.getTime() > Date.now()
        && Number.isSafeInteger(Number(taskScheduleDraft.ownerId))
        && Number(taskScheduleDraft.ownerId) > 0,
    )

    const openTaskScheduleEditor = (task) => {
        if (!task || task.status !== 'open' || !['follow_up', 'scheduled_call'].includes(task.type)) return
        setTaskScheduleDraft({
            taskId: task.id,
            taskType: task.type,
            dueAt: parseDate(task.due_at),
            ownerId: task.owner?.id || detail?.owner?.id || '',
            idempotencyKey: makeIdempotencyKey(),
        })
    }

    const confirmTaskScheduleUpdate = async () => {
        if (!detail || !taskScheduleIsValid || updatingTaskSchedule) return
        setUpdatingTaskSchedule(true)
        try {
            const response = await api.patch(
                `/admin/sales/opportunities/${detail.id}/tasks/${taskScheduleDraft.taskId}/schedule`,
                {
                    task_type: taskScheduleDraft.taskType,
                    due_at: taskScheduleDraft.dueAt.toISOString(),
                    owner_id: Number(taskScheduleDraft.ownerId),
                    idempotency_key: taskScheduleDraft.idempotencyKey,
                    expected_version: detail.version,
                },
            )
            setDetail(response.opportunity)
            setTaskScheduleDraft(null)
            setTaskDraft(null)
            await Promise.all([loadOpportunities(), loadUpcomingTasks()])
        } catch {
            // Keep the draft and idempotency key so the update can be retried safely.
        } finally {
            setUpdatingTaskSchedule(false)
        }
    }

    const confirmTaskCompletion = async () => {
        if (!detail || !taskActivityIsValid || completingTask) return
        setCompletingTask(true)
        try {
            const subsequentAction = ['start_qualification', 'qualify'].includes(taskDraft.nextStepType)
                ? buildTransitionDraft('qualify', {
                    ...(taskDraft.aircallSuggestion?.qualification_prefill || {}),
                    ...(taskDraft.commercialObjection ? { objection: taskDraft.commercialObjection } : {}),
                })
                : null
            const response = taskDraft.mode === 'feedback'
                ? await api.post(
                    `/admin/sales/opportunities/${detail.id}/activities/${taskDraft.activityId}/feedback`,
                    {
                        idempotency_key: taskDraft.idempotencyKey,
                        expected_version: detail.version,
                        conversation_outcome: taskDraft.conversationOutcome || null,
                        commercial_objection: taskDraft.commercialObjection || null,
                        next_step_type: taskDraft.nextStepType,
                        next_step_due_at: ['follow_up', 'scheduled_call', 'postpone'].includes(taskDraft.nextStepType)
                            ? taskDraft.nextStepDueAt.toISOString()
                            : null,
                        notes: taskDraft.notes.trim() || null,
                    },
                )
                : await api.post(
                    `/admin/sales/opportunities/${detail.id}/activities/manual`,
                    {
                        idempotency_key: taskDraft.idempotencyKey,
                        task_id: taskDraft.taskId,
                        activity_type: taskDraft.activityType,
                        contact_outcome: taskDraft.contactOutcome,
                        conversation_outcome: taskDraft.conversationOutcome || null,
                        commercial_objection: taskDraft.commercialObjection || null,
                        next_step_type: taskDraft.nextStepType,
                        next_step_due_at: ['follow_up', 'scheduled_call', 'postpone'].includes(taskDraft.nextStepType)
                            ? taskDraft.nextStepDueAt.toISOString()
                            : null,
                        occurred_at: new Date().toISOString(),
                        notes: taskDraft.notes.trim() || null,
                    },
                )
            setDetail(response.opportunity)
            setTaskDraft(null)
            if (subsequentAction) {
                setTransitionDraft({ ...subsequentAction, completedTaskId: '' })
            }
            await Promise.all([loadOpportunities(), loadUpcomingTasks()])
        } catch {
            // Keep the draft and idempotency key so the operation can be retried safely.
        } finally {
            setCompletingTask(false)
        }
    }

    const handlePaymentLinkTask = async (mode) => {
        if (!detail || !taskDraft || completingTask) return
        setCompletingTask(true)
        try {
            const response = await api.post(
                `/admin/sales/opportunities/${detail.id}/tasks/${taskDraft.taskId}/payment-link`,
                {
                    mode,
                    channel: mode === 'manual' ? taskDraft.paymentLinkChannel : null,
                    idempotency_key: taskDraft.idempotencyKey,
                    expected_version: detail.version,
                },
            )
            setDetail(response.opportunity)
            if (mode === 'manual') setTaskDraft(null)
            await Promise.all([loadOpportunities(), loadUpcomingTasks()])
        } catch {
            // Preserve the draft and idempotency key for a safe retry.
        } finally {
            setCompletingTask(false)
        }
    }

    const qualificationFeedbackIsValid = () => {
        const action = transitionDraft?.action
        if (action === 'qualify') {
            return qualificationFeedbackComplete(transitionDraft?.qualificationFeedback)
        }
        if (action === 'mark_unqualified') return Boolean(transitionDraft.qualificationReason)
        return true
    }

    const guidedQualificationResult = transitionDraft?.action === 'qualify'
        && qualificationFeedbackComplete(transitionDraft.qualificationFeedback)
        ? deriveQualificationResult(
            transitionDraft.qualificationFeedback,
            getContactSequenceProgress(detail),
        )
        : null

    const paymentLink = checkoutLinkForOpportunity(detail)
    const latestQualificationNextStep = (detail?.lifecycle_events || []).find((event) => (
        event.metadata?.qualification_feedback?.next_step
    ))?.metadata?.qualification_feedback?.next_step
    const paymentLinkIsNextStep = latestQualificationNextStep === 'payment_link'

    const copyPaymentLink = async () => {
        if (!paymentLink) return
        await navigator.clipboard.writeText(paymentLink)
        setPaymentLinkCopied(true)
        window.setTimeout(() => setPaymentLinkCopied(false), 1800)
    }

    const negativeClosureResult = transitionDraft?.action === 'close_negative'
        ? NEGATIVE_CLOSURE_REASONS.find((reason) => reason.value === transitionDraft.negativeClosureReason)
        : null

    const confirmTransition = async () => {
        if (!detail || !transitionDraft) return
        const guidedResult = transitionDraft.action === 'qualify'
            ? deriveQualificationResult(
                transitionDraft.qualificationFeedback,
                getContactSequenceProgress(detail),
            )
            : null
        if (guidedResult?.blocked) return
        const selectedNegativeClosure = negativeClosureResult
        const action = guidedResult?.action || selectedNegativeClosure?.action || transitionDraft.action
        if (transitionDraft.action === 'close_negative' && !selectedNegativeClosure) return
        if (action === 'postpone' && !transitionDraft.recycleAt) return
        if (action === 'schedule_follow_up' && !transitionDraft.nextTaskDueAt) return

        setTransitioning(true)
        try {
            const response = await api.post(
                `/admin/sales/opportunities/${detail.id}/transition`,
                {
                    action,
                    idempotency_key: transitionDraft.idempotencyKey,
                    expected_version: detail.version,
                    completed_task_id: transitionDraft.completedTaskId
                        ? Number(transitionDraft.completedTaskId)
                        : null,
                    recycle_at: action === 'postpone' && transitionDraft.recycleAt instanceof Date
                        ? transitionDraft.recycleAt.toISOString()
                        : null,
                    next_task_due_at: action === 'schedule_follow_up' && transitionDraft.nextTaskDueAt instanceof Date
                        ? transitionDraft.nextTaskDueAt.toISOString()
                        : null,
                    next_task_type: action === 'schedule_follow_up'
                        ? transitionDraft.nextTaskType
                        : null,
                    loss_reason: action === 'mark_lost'
                        ? (guidedResult?.lossReason || selectedNegativeClosure?.reason || transitionDraft.lossReason)
                        : null,
                    qualification_reason: action === 'mark_unqualified'
                        ? (guidedResult?.qualificationReason || selectedNegativeClosure?.reason || transitionDraft.qualificationReason)
                        : null,
                    qualification_feedback: transitionDraft.action === 'qualify'
                        ? {
                            ...transitionDraft.qualificationFeedback,
                            next_step: guidedResult?.nextStep,
                        }
                        : null,
                    notes: transitionDraft.notes.trim() || null,
                },
            )
            setDetail(response.opportunity)
            setTransitionDraft(
                transitionDraft.action === 'start_qualification'
                    ? {
                        ...buildTransitionDraft('qualify'),
                        completedTaskId: response.opportunity?.next_task?.id || '',
                    }
                    : null,
            )
            await Promise.all([loadOpportunities(), loadUpcomingTasks()])
        } catch {
            // Keep the same idempotency key so an uncertain request can be retried safely.
        } finally {
            setTransitioning(false)
        }
    }

    const selectOwner = (option) => {
        if (!option || option.value === detail?.owner?.id) {
            setOwnerDraft(null)
            return
        }
        setWhatsappDraft(null)
        setTransitionDraft(null)
        setCourseDraft(null)
        setOwnerDraft({
            ownerId: option.value,
            idempotencyKey: makeIdempotencyKey(),
            notes: '',
        })
    }

    const confirmOwnerChange = async () => {
        if (!detail || !ownerDraft) return
        setChangingOwner(true)
        try {
            const response = await api.put(
                `/admin/sales/opportunities/${detail.id}/owner`,
                {
                    owner_id: ownerDraft.ownerId,
                    idempotency_key: ownerDraft.idempotencyKey,
                    expected_version: detail.version,
                    notes: ownerDraft.notes.trim() || null,
                },
            )
            setDetail(response.opportunity)
            setOwnerDraft(null)
            await Promise.all([loadOpportunities(), loadUpcomingTasks()])
        } catch {
            // Keep the idempotency key stable so the assignment can be retried safely.
        } finally {
            setChangingOwner(false)
        }
    }

    const selectCourse = (option) => {
        if (!option || option.value === detail?.application?.id) {
            setCourseDraft(null)
            setDetailCourseSearch('')
            return
        }
        setWhatsappDraft(null)
        setTransitionDraft(null)
        setOwnerDraft(null)
        setDetailCourseSearch('')
        setCourseDraft({
            applicationId: option.value,
            idempotencyKey: makeIdempotencyKey(),
            notes: '',
        })
    }

    const confirmCourseChange = async () => {
        if (!detail || !courseDraft) return
        setChangingCourse(true)
        try {
            const response = await api.put(
                `/admin/sales/opportunities/${detail.id}/application`,
                {
                    application_id: courseDraft.applicationId,
                    idempotency_key: courseDraft.idempotencyKey,
                    expected_version: detail.version,
                    notes: courseDraft.notes.trim() || null,
                },
            )
            setDetail(response.opportunity)
            setCourseDraft(null)
            setDetailCourseSearch('')
            await Promise.all([loadOpportunities(), loadUpcomingTasks()])
        } catch {
            // Keep the idempotency key stable so the course change can be retried safely.
        } finally {
            setChangingCourse(false)
        }
    }

    const changeTestStatus = async (checked) => {
        if (!detail || changingTestStatus || checked === detail.test) return
        setChangingTestStatus(true)
        try {
            const response = await api.put(
                `/admin/sales/opportunities/${detail.id}/test`,
                {
                    test: checked,
                    idempotency_key: makeIdempotencyKey(),
                    expected_version: detail.version,
                },
            )
            setDetail(response.opportunity)
            await Promise.all([loadOpportunities(), loadUpcomingTasks()])
        } catch {
            await loadDetail(detail.id)
        } finally {
            setChangingTestStatus(false)
        }
    }

    const closeDetail = () => {
        setSelectedId(null)
        setWhatsappDraft(null)
        setAircallDialMessage('')
        setTransitionDraft(null)
        setTaskDraft(null)
        setTaskScheduleDraft(null)
        setExpandedCompletedTaskId(null)
        setOwnerDraft(null)
        setCourseDraft(null)
        setContactDraft(null)
        setChannelIssueDraft(null)
        setDetailCourseSearch('')
    }

    const renderChannelIssueForm = () => {
        if (!channelIssueDraft) return null
        const options = channelIssueDraft.channel === 'whatsapp'
            ? WHATSAPP_CHANNEL_ISSUES
            : CALL_CHANNEL_ISSUES
        return (
            <div
                className={styles.channelIssueForm}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
            >
                <div>
                    <strong>Segnala un problema con il contatto</strong>
                    <p>
                        {channelIssueDraft.channel === 'whatsapp'
                            ? 'Indica se il lead non utilizza WhatsApp: i prossimi contatti verranno gestiti via email o chiamata.'
                            : 'Indica il problema riscontrato durante il tentativo di chiamata.'}
                    </p>
                </div>
                <div className={styles.confirmField}>
                    <span>Problema riscontrato</span>
                    <Select
                        appearance="modern"
                        portal
                        placeholder="Seleziona il problema"
                        selected={options.find((item) => item.value === channelIssueDraft.issueType) || null}
                        options={options}
                        onSelect={(option) => setChannelIssueDraft({
                            ...channelIssueDraft,
                            issueType: option?.value || '',
                        })}
                    />
                </div>
                <label>
                    <span>Nota (opzionale)</span>
                    <textarea
                        value={channelIssueDraft.notes}
                        onChange={(event) => setChannelIssueDraft({ ...channelIssueDraft, notes: event.target.value })}
                        rows="2"
                        placeholder="Aggiungi un dettaglio utile"
                    />
                </label>
                <div className={styles.confirmActions}>
                    <Button appearance="text" onClick={() => setChannelIssueDraft(null)} requiresEdit={false}>Annulla</Button>
                    <Button
                        appearance="subtle"
                        onClick={saveChannelIssue}
                        disabled={!channelIssueDraft.issueType || savingChannelIssue}
                        status={savingChannelIssue ? ButtonStatus.Loading : undefined}
                        requiresEdit
                    >
                        Salva segnalazione
                    </Button>
                </div>
            </div>
        )
    }

    const renderTaskCompletionForm = () => {
        const feedbackMode = taskDraft.mode === 'feedback'
        const manualContactCollapsible = !feedbackMode
            && CONTACT_TASKS_WITH_COLLAPSIBLE_MANUAL.has(taskDraft.taskType)
        const manualContactVisible = !manualContactCollapsible || taskDraft.manualContactOpen
        const sourceActivity = feedbackMode
            ? (detail.activities || []).find((activity) => activity.id === taskDraft.activityId)
            : null
        const currentTask = (detail.tasks || []).find((task) => task.id === taskDraft.taskId)
        if (taskDraft.taskType === 'send_payment_link') {
            const automaticPaymentTitle = currentTask?.metadata?.payment_destination === 'first_installment'
                ? 'Email automatica per il pagamento della prima rata'
                : 'Email automatica con link al checkout'
            return (
                <div
                    className={styles.taskCompletionCard}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    <div>
                        <h3>Invia il link di pagamento</h3>
                        <p>Scegli se condividerlo personalmente oppure far inviare al sistema la mail già predisposta.</p>
                    </div>
                    {currentTask?.mailing ? (
                        renderAutomaticEmailDetails(currentTask, automaticPaymentTitle)
                    ) : (
                        <>
                            <div className={styles.paymentLinkInline}>
                                <span>Link checkout del lead</span>
                                <code>{paymentLink || 'Link non disponibile'}</code>
                                <Button
                                    appearance="subtle"
                                    onClick={copyPaymentLink}
                                    disabled={!paymentLink}
                                    requiresEdit={false}
                                >
                                    <CopyIcon /> {paymentLinkCopied ? 'Copiato' : 'Copia link'}
                                </Button>
                            </div>
                            <div className={styles.paymentLinkChoices}>
                                <div className={styles.paymentLinkChoice}>
                                    <strong>Invio manuale</strong>
                                    <span>Copia il link, invialo al lead e indica il canale utilizzato.</span>
                                    <Select
                                        appearance="modern"
                                        portal
                                        selected={PAYMENT_LINK_CHANNELS.find((item) => item.value === taskDraft.paymentLinkChannel)}
                                        options={PAYMENT_LINK_CHANNELS}
                                        onSelect={(option) => setTaskDraft({
                                            ...taskDraft,
                                            paymentLinkChannel: option?.value || 'whatsapp',
                                        })}
                                    />
                                    <Button
                                        appearance="subtle"
                                        onClick={() => handlePaymentLinkTask('manual')}
                                        disabled={completingTask || !paymentLink}
                                        status={completingTask ? ButtonStatus.Loading : undefined}
                                        requiresEdit
                                    >
                                        Conferma invio manuale
                                    </Button>
                                </div>
                                <div className={styles.paymentLinkChoice}>
                                    <strong>Invio automatico</strong>
                                    <span>Il sistema invia il checkout oppure, se è associato un ordine rateale per questo corso, il pagamento della prima rata.</span>
                                    <Button
                                        appearance="subtle"
                                        onClick={() => handlePaymentLinkTask('automatic')}
                                        disabled={completingTask}
                                        status={completingTask ? ButtonStatus.Loading : undefined}
                                        requiresEdit
                                    >
                                        Invia automaticamente via email
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )
        }
        if (AUTOMATED_EMAIL_TASKS.has(taskDraft.taskType)) {
            return (
                <div
                    className={styles.taskCompletionCard}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    {renderAutomaticEmailDetails(
                        currentTask,
                        taskDraft.taskType === 'request_phone_update_email'
                            ? 'Richiesta automatica di correzione del numero'
                            : undefined,
                    )}
                </div>
            )
        }
        if (taskScheduleDraft?.taskId === taskDraft.taskId) {
            const selectedTaskOwner = ownerOptions.find((option) => option.value === Number(taskScheduleDraft.ownerId))
                || (currentTask?.owner
                    ? { id: currentTask.owner.id, value: currentTask.owner.id, name: fullName(currentTask.owner) }
                    : null)
            return (
                <div
                    className={styles.taskCompletionCard}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    <div className={styles.taskScheduleManagement}>
                        <div className={styles.taskScheduleHeader}>
                            <div>
                                <strong>Modifica task</strong>
                                <span>Aggiorna il tipo, la scadenza o il sales incaricato di questa attività.</span>
                            </div>
                        </div>
                        <div className={styles.taskScheduleEditor}>
                            <div className={styles.confirmField}>
                                <span>Tipo di attività</span>
                                <Select
                                    appearance="modern"
                                    portal
                                    selected={TASK_SCHEDULE_OPTIONS.find((option) => option.value === taskScheduleDraft.taskType) || null}
                                    options={TASK_SCHEDULE_OPTIONS}
                                    onSelect={(option) => setTaskScheduleDraft({
                                        ...taskScheduleDraft,
                                        taskType: option?.value || '',
                                    })}
                                />
                            </div>
                            <div className={styles.confirmField}>
                                <span>Scadenza</span>
                                <TextInput
                                    appearance="modern"
                                    type="date"
                                    value={taskScheduleDraft.dueAt}
                                    onKeyUp={(value) => setTaskScheduleDraft({ ...taskScheduleDraft, dueAt: value })}
                                />
                            </div>
                            <div className={styles.confirmField}>
                                <span>Sales assegnato</span>
                                <Select
                                    appearance="modern"
                                    portal
                                    placeholder="Seleziona il sales"
                                    selected={selectedTaskOwner}
                                    options={ownerOptions}
                                    loading={loadingOwners}
                                    onSelect={(option) => setTaskScheduleDraft({
                                        ...taskScheduleDraft,
                                        ownerId: option?.value || '',
                                    })}
                                />
                            </div>
                            <div className={styles.taskScheduleActions}>
                                <Button
                                    type="button"
                                    appearance="text"
                                    onClick={() => setTaskScheduleDraft(null)}
                                    requiresEdit={false}
                                >
                                    Annulla
                                </Button>
                                <Button
                                    type="button"
                                    appearance="subtle"
                                    onClick={confirmTaskScheduleUpdate}
                                    disabled={!taskScheduleIsValid || updatingTaskSchedule}
                                    status={updatingTaskSchedule ? ButtonStatus.Loading : undefined}
                                    requiresEdit
                                >
                                    Salva modifiche
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }
        return (
        <div
            className={styles.taskCompletionCard}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
        >
            {feedbackMode && (
                <div>
                    <h3>Completa il feedback commerciale</h3>
                    <p>La risposta è stata registrata automaticamente. Indica l’esito della conversazione per definire il prossimo passo.</p>
                </div>
            )}
            {feedbackMode && sourceActivity && (
                <div className={styles.taskFeedbackContext}>
                    <strong>{activityLabel(sourceActivity)}</strong>
                    <span>{formatDate(sourceActivity.occurred_at)}</span>
                    <span>{CONTACT_OUTCOME_LABELS[sourceActivity.contact_outcome] || sourceActivity.contact_outcome}</span>
                </div>
            )}
            {feedbackMode && taskDraft.aircallSuggestion && (
                <div className={`${styles.aircallSuggestion} ${taskDraft.aircallSuggestion.status === 'ambiguous' ? styles.aircallSuggestionAmbiguous : ''}`}>
                    <div className={styles.aircallSuggestionHeader}>
                        <strong>Suggerimento Aircall</strong>
                        <span>Da confermare</span>
                    </div>
                    <div className={styles.aircallSuggestionTags}>
                        {(taskDraft.aircallSuggestion.recognized_tags || []).map((tag) => (
                            <span key={`${tag.id || 'name'}-${tag.name}`}>{tag.name}</span>
                        ))}
                    </div>
                    <p>
                        {taskDraft.aircallSuggestion.status === 'ambiguous'
                            ? 'Aircall ha rilevato indicazioni incompatibili: seleziona manualmente l’esito corretto.'
                            : taskDraft.aircallSuggestionIncompatible
                                ? 'Il suggerimento non corrisponde agli esiti previsti nella fase di chiusura: seleziona manualmente quello corretto.'
                                : 'Il form è stato precompilato dai tag della chiamata. Puoi modificare liberamente ogni scelta prima di salvare.'}
                    </p>
                </div>
            )}
            {!feedbackMode && [
                'first_call',
                'second_call',
                'third_call',
                'follow_up',
                'nurture_follow_up',
                'verify_updated_phone',
                'scheduled_call',
                'send_whatsapp',
                'send_email',
            ].includes(taskDraft.taskType) && (
                <div className={styles.taskContactActions}>
                    <div>
                        <strong>Contatta il lead</strong>
                        <span>Scegli il canale da utilizzare per questa attività.</span>
                    </div>
                    <div className={styles.taskContactButtons}>
                        {!['send_whatsapp', 'send_email'].includes(taskDraft.taskType) && (
                            <Button
                                type="button"
                                appearance="subtle"
                                onClick={callWithAircall}
                                disabled={!detail.lead?.phone || dialingAircall}
                                status={dialingAircall ? ButtonStatus.Loading : undefined}
                                requiresEdit={false}
                            >
                                <PhoneIcon className={styles.buttonIcon} />
                                Chiama con Aircall
                            </Button>
                        )}
                        {taskDraft.taskType !== 'send_email' && (
                            <Button
                                type="button"
                                appearance="subtle"
                                onClick={messageWithAircall}
                                disabled={!detail.lead?.phone || openingAircallMessage}
                                status={openingAircallMessage ? ButtonStatus.Loading : undefined}
                                requiresEdit={false}
                            >
                                <WhatsappIcon className={styles.buttonIcon} />
                                WhatsApp con Aircall
                            </Button>
                        )}
                        {taskDraft.taskType !== 'send_email' && (
                            <Button
                                type="button"
                                appearance="subtle"
                                onClick={openTaskWhatsapp}
                                disabled={!detail.lead?.phone}
                                requiresEdit={false}
                            >
                                <WhatsappIcon className={styles.buttonIcon} />
                                Apri WhatsApp
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {!feedbackMode && (manualContactCollapsible || CHANNEL_ISSUE_TASK_TYPES.has(taskDraft.taskType) || ['follow_up', 'scheduled_call'].includes(taskDraft.taskType)) && (
                <div className={styles.taskSecondaryActions}>
                    {manualContactCollapsible && (
                        <Button
                            type="button"
                            appearance="subtle"
                            additionalClass={styles.taskSecondaryAction}
                            onClick={() => {
                                setChannelIssueDraft(null)
                                setTaskDraft({
                                    ...taskDraft,
                                    manualContactOpen: !taskDraft.manualContactOpen,
                                })
                            }}
                            aria-expanded={taskDraft.manualContactOpen}
                            requiresEdit
                        >
                            <EditIcon className={styles.buttonIcon} />
                            {taskDraft.manualContactOpen ? 'Nascondi registrazione' : 'Registra manualmente'}
                        </Button>
                    )}
                    {['follow_up', 'scheduled_call'].includes(taskDraft.taskType) && (
                        <Button
                            type="button"
                            appearance="subtle"
                            additionalClass={styles.taskSecondaryAction}
                            onClick={() => {
                                setChannelIssueDraft(null)
                                setTaskDraft({ ...taskDraft, manualContactOpen: false })
                                openTaskScheduleEditor(currentTask)
                            }}
                            requiresEdit
                        >
                            <EditIcon className={styles.buttonIcon} />
                            Modifica task
                        </Button>
                    )}
                    {CHANNEL_ISSUE_TASK_TYPES.has(taskDraft.taskType) && (
                        <Button
                            type="button"
                            appearance="subtle"
                            additionalClass={styles.taskSecondaryAction}
                            onClick={() => {
                                if (channelIssueDraft?.taskId === taskDraft.taskId) {
                                    setChannelIssueDraft(null)
                                } else if (currentTask) {
                                    setTaskDraft({ ...taskDraft, manualContactOpen: false })
                                    openTaskChannelIssue(currentTask)
                                }
                            }}
                            aria-expanded={channelIssueDraft?.taskId === taskDraft.taskId}
                            requiresEdit
                        >
                            <AttentionIcon className={styles.buttonIcon} />
                            {channelIssueDraft?.taskId === taskDraft.taskId ? 'Nascondi segnalazione' : 'Segnala un problema'}
                        </Button>
                    )}
                </div>
            )}
            {channelIssueDraft?.taskId === taskDraft.taskId && (
                <div className={styles.taskChannelIssue}>
                    {renderChannelIssueForm()}
                </div>
            )}
            {manualContactVisible && (
                <>
            <div className={styles.taskCompletionFields}>
                {!feedbackMode && (
                    <>
                <div className={styles.confirmField}>
                    <span>Canale utilizzato</span>
                    <Select
                        appearance="modern"
                        portal
                        placeholder="Scegli il canale"
                        selected={MANUAL_ACTIVITY_CHANNELS.find((item) => item.value === taskDraft.activityType) || null}
                        options={MANUAL_ACTIVITY_CHANNELS}
                        onSelect={(option) => {
                            const activityType = option?.value || ''
                            const outcomes = MANUAL_ACTIVITY_OUTCOMES[activityType] || []
                            const contactOutcome = outcomes.length === 1 ? outcomes[0].value : ''
                            setTaskDraft({
                                ...taskDraft,
                                activityType,
                                contactOutcome,
                                conversationOutcome: '',
                                commercialObjection: '',
                                nextStepType: nextStepForTaskContact(taskDraft.taskType, contactOutcome),
                            })
                        }}
                    />
                </div>
                <div className={styles.confirmField}>
                    <span>Esito</span>
                    <Select
                        appearance="modern"
                        portal
                        placeholder="Scegli l’esito"
                        disabled={!taskDraft.activityType}
                        selected={(MANUAL_ACTIVITY_OUTCOMES[taskDraft.activityType] || []).find((item) => item.value === taskDraft.contactOutcome) || null}
                        options={MANUAL_ACTIVITY_OUTCOMES[taskDraft.activityType] || []}
                        onSelect={(option) => {
                            const contactOutcome = option?.value || ''
                            setTaskDraft({
                                ...taskDraft,
                                contactOutcome,
                                conversationOutcome: '',
                                commercialObjection: '',
                                nextStepType: nextStepForTaskContact(taskDraft.taskType, contactOutcome),
                            })
                        }}
                    />
                </div>
                    </>
                )}
                {['answered', 'received'].includes(taskDraft.contactOutcome) && (
                    <>
                    <div className={styles.confirmField}>
                        <span>Esito della conversazione</span>
                        <Select
                            appearance="modern"
                            portal
                            placeholder="Scegli l’esito commerciale"
                            selected={ALL_CONVERSATION_OUTCOMES.find((item) => item.value === taskDraft.conversationOutcome) || UNCONFIRMED_CONVERSATION_OUTCOME}
                            options={conversationOutcomesForStage(detail.stage, detail.qualification_status)}
                            onSelect={(option) => {
                                const conversationOutcome = option?.value || ''
                                setTaskDraft({
                                    ...taskDraft,
                                    conversationOutcome,
                                    nextStepType: conversationOutcome ? nextStepForConversation(conversationOutcome) : '',
                                    nextStepDueAt: nextStepDateForConversation(conversationOutcome, taskDraft.nextStepDueAt),
                                })
                            }}
                        />
                    </div>
                    <div className={styles.confirmField}>
                        <span>Obiezione emersa (opzionale)</span>
                        <Select
                            appearance="modern"
                            portal
                            nullable
                            nullLabel="Nessuna obiezione registrata"
                            selected={CONTACT_OBJECTIONS.find((item) => item.value === taskDraft.commercialObjection) || null}
                            options={CONTACT_OBJECTIONS}
                            onSelect={(option) => setTaskDraft({
                                ...taskDraft,
                                commercialObjection: option?.value || '',
                            })}
                        />
                    </div>
                    </>
                )}
                <div className={styles.confirmField}>
                    <span>Prossimo passo</span>
                    <Select
                        appearance="modern"
                        portal
                        placeholder="Definito dall’esito"
                        disabled
                        selected={CONTACT_NEXT_STEPS.find((item) => item.value === taskDraft.nextStepType) || null}
                        options={CONTACT_NEXT_STEPS}
                    />
                </div>
                {['follow_up', 'scheduled_call', 'postpone'].includes(taskDraft.nextStepType) && (
                    <div className={styles.confirmField}>
                        <span>{taskDraft.nextStepType === 'postpone'
                            ? 'Data di riciclo / prossima edizione'
                            : taskDraft.nextStepType === 'scheduled_call' ? 'Chiamata fissata per' : 'Ricontatta il'}</span>
                        <TextInput
                            appearance="modern"
                            type="date"
                            value={taskDraft.nextStepDueAt}
                            onKeyUp={(value) => setTaskDraft({ ...taskDraft, nextStepDueAt: value })}
                        />
                    </div>
                )}
            </div>
            <label>
                <span>Nota sul contatto (opzionale)</span>
                <textarea
                    value={taskDraft.notes}
                    onChange={(event) => setTaskDraft({ ...taskDraft, notes: event.target.value })}
                    rows="3"
                    placeholder="Aggiungi un dettaglio utile alla cronologia"
                />
            </label>
            <div className={styles.confirmActions}>
                <Button appearance="text" onClick={() => setTaskDraft(null)} requiresEdit={false}>Annulla</Button>
                <Button
                    appearance="subtle"
                    onClick={confirmTaskCompletion}
                    disabled={completingTask || !taskActivityIsValid}
                    status={completingTask ? ButtonStatus.Loading : undefined}
                    requiresEdit
                >
                    {feedbackMode ? 'Salva feedback' : 'Registra e completa'}
                </Button>
            </div>
                </>
            )}
        </div>
        )
    }

    function renderAutomaticEmailDetails(task, title = 'Email automatica di ricontatto') {
        const mailing = task?.mailing
        const status = mailing?.status || 'draft'
        return (
            <div className={styles.automaticEmailDetails}>
                <div className={styles.automaticEmailHeader}>
                    <strong>{title}</strong>
                    <span className={`${styles.automaticEmailStatus} ${styles[`automaticEmailStatus_${status}`] || ''}`}>
                        {AUTOMATED_EMAIL_STATUS_LABELS[status] || status}
                    </span>
                </div>
                {mailing ? (
                    <>
                        <dl className={styles.automaticEmailMeta}>
                            <div>
                                <dt>Destinatario</dt>
                                <dd>{mailing.to}</dd>
                            </div>
                            <div>
                                <dt>Oggetto</dt>
                                <dd>{mailing.subject}</dd>
                            </div>
                        </dl>
                        <details className={styles.automaticEmailPreview}>
                            <summary>Visualizza anteprima della mail</summary>
                            <iframe
                                title={`Anteprima email per ${mailing.to}`}
                                srcDoc={mailing.content}
                                sandbox=""
                            />
                        </details>
                    </>
                ) : (
                    <p>Il sistema sta preparando il contenuto della mail.</p>
                )}
            </div>
        )
    }

    return (
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet><title>Candidature</title></Helmet>
            </HelmetProvider>
            <main className={styles.page}>
                <section className={styles.header}>
                    <div>
                        <div className={styles.eyebrow}>Sales workspace</div>
                        <h1>Candidature</h1>
                        <p>Gestisci lead, attività e prossime azioni del funnel commerciale.</p>
                    </div>
                    <div className={styles.headerActions}>
                        <ApplicationsManagerDialog onChanged={loadCourseOptions} />
                        <Button
                            appearance="subtle"
                            onClick={() => Promise.all([loadOpportunities(), loadUpcomingTasks()])}
                            disabled={loading || loadingUpcomingTasks}
                        >
                            <ReloadIcon className={styles.buttonIcon} /> Aggiorna
                        </Button>
                    </div>
                </section>

                <section className={styles.upcomingTasksCard}>
                    <div className={styles.upcomingTasksHeader}>
                        <div>
                            <h2>I miei task</h2>
                            <span className={styles.upcomingTasksEyebrow}>Le prossime attività della tua agenda commerciale</span>
                        </div>
                        <div className={styles.taskViewControls}>
                            <span className={styles.upcomingTasksCount}>
                                {upcomingTasksCount} {upcomingTasksCount === 1 ? 'attività aperta' : 'attività aperte'}
                            </span>
                            <div className={styles.taskViewSwitch} aria-label="Vista task">
                                <button className={taskView === 'list' ? styles.active : ''} type="button" onClick={() => setTaskView('list')}>Lista</button>
                                <button className={taskView === 'calendar' ? styles.active : ''} type="button" onClick={() => setTaskView('calendar')}><CalendarIcon /> Calendario</button>
                            </div>
                        </div>
                    </div>
                    {taskView === 'list' && <div className={styles.taskFilters}>
                        <div>
                            <span>Scadenza</span>
                            <MultipleSelect
                                appearance="modern"
                                portal
                                selected={selectedTaskDueFilters}
                                options={TASK_DUE_FILTER_OPTIONS}
                                placeholder="Tutte le scadenze"
                                allLabel="Tutte le scadenze"
                                onSelect={setSelectedTaskDueFilters}
                            />
                        </div>
                        <div>
                            <span>Attività</span>
                            <MultipleSelect
                                appearance="modern"
                                portal
                                selected={selectedTaskTypes}
                                options={TASK_FILTER_OPTIONS}
                                placeholder="Tutte le attività"
                                allLabel="Tutte le attività"
                                onSelect={setSelectedTaskTypes}
                                onSearch={() => {}}
                                searchPlaceholder="Cerca attività"
                            />
                        </div>
                    </div>}
                    {loadingUpcomingTasks ? (
                        <><span className={styles.srOnly} role="status">Caricamento task</span><TaskAgendaSkeleton calendar={taskView === 'calendar'} /></>
                    ) : taskView === 'calendar' || upcomingTasks.length > 0 ? (
                        taskView === 'calendar' ? (
                            <div className={styles.taskCalendar}>
                                <div className={styles.taskCalendarPicker}>
                                    <div className={styles.taskCalendarNavigation}>
                                        <div>
                                            <strong>{taskCalendarMonth.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}</strong>
                                            <span>{selectedTaskTypes.length > 0 ? `${calendarTasks.length} di ${upcomingTasks.length}` : upcomingTasks.length} task nel mese</span>
                                        </div>
                                        <div className={styles.taskCalendarNavigationControls}>
                                            <button type="button" onClick={() => {
                                                const today = new Date()
                                                setTaskCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1))
                                                setSelectedTaskCalendarDay(taskDateKey(today))
                                            }}>Oggi</button>
                                            <button type="button" aria-label="Mese precedente" onClick={() => navigateTaskCalendar(-1)}>‹</button>
                                            <button type="button" aria-label="Mese successivo" onClick={() => navigateTaskCalendar(1)}>›</button>
                                        </div>
                                    </div>
                                    <table className={styles.taskCalendarGrid}>
                                        <caption className={styles.srOnly}>Task di {taskCalendarMonth.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}</caption>
                                        <thead><tr>{['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'].map((day) => <th scope="col" key={day}><abbr title={day}>{day.slice(0, 3)}</abbr></th>)}</tr></thead>
                                        <tbody>{Array.from({ length: taskCalendarCells.length / 7 }, (_, week) => (
                                            <tr key={week}>{taskCalendarCells.slice(week * 7, week * 7 + 7).map((cell) => (
                                                <td className={!cell.currentMonth ? styles.outsideMonth : ''} key={cell.key}>
                                                    <button
                                                        type="button"
                                                        className={`${styles.taskCalendarDay} ${cell.today ? styles.today : ''} ${cell.key === selectedTaskCalendarDay ? styles.selected : ''}`}
                                                        aria-label={`${cell.date.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}, ${cell.tasks.length} task`}
                                                        aria-current={cell.today ? 'date' : undefined}
                                                        aria-pressed={cell.key === selectedTaskCalendarDay}
                                                        onClick={() => selectTaskCalendarDay(cell)}
                                                    >
                                                        <span className={styles.taskCalendarDayNumber}>{cell.date.getDate()}</span>
                                                        <span className={styles.taskCalendarDaySummaries} aria-hidden="true">
                                                            {cell.summaries.map(({ group, count }) => {
                                                                const TaskIcon = taskGroupIcon(group)
                                                                return <span className={`${styles.taskCalendarSummaryTag} ${styles[`taskTag_${group}`] || ''}`} title={`${count} ${TASK_GROUP_LABELS[group]}`} key={group}><TaskIcon /><strong>{count}</strong></span>
                                                            })}
                                                        </span>
                                                    </button>
                                                </td>
                                            ))}</tr>
                                        ))}</tbody>
                                    </table>
                                </div>
                                <aside className={styles.taskCalendarAgenda} aria-live="polite">
                                    <div className={styles.taskCalendarAgendaHeader}>
                                        <div>
                                            <span>Agenda del giorno</span>
                                            <h3>{selectedTaskCalendarLabel}</h3>
                                        </div>
                                        <strong>{selectedTaskCalendarTasks.length}</strong>
                                    </div>
                                    <div className={styles.taskCalendarFilters} aria-label="Filtra i task del calendario">
                                        <button
                                            type="button"
                                            className={`${styles.taskCalendarFilter} ${selectedTaskTypes.length === 0 ? styles.active : ''}`}
                                            aria-pressed={selectedTaskTypes.length === 0}
                                            onClick={() => setSelectedTaskTypes([])}
                                        >Tutti</button>
                                        {CALENDAR_TASK_FILTER_OPTIONS.map((filter) => {
                                            const active = filter.values.some((value) => selectedTaskTypeValues.has(value))
                                            const TaskIcon = taskGroupIcon(filter.group)
                                            return <button
                                                type="button"
                                                className={`${styles.taskCalendarFilter} ${styles[`taskTag_${filter.group}`] || ''} ${active ? styles.active : ''}`}
                                                aria-pressed={active}
                                                onClick={() => toggleCalendarTaskFilter(filter)}
                                                key={filter.id}
                                            ><TaskIcon /><span>{filter.name}</span></button>
                                        })}
                                    </div>
                                    {selectedTaskCalendarTasks.length > 0 ? (
                                        <div className={styles.taskCalendarAgendaList}>
                                            {selectedTaskCalendarTasks.map((task) => {
                                                const group = taskColorGroup(task.type)
                                                const TaskIcon = taskGroupIcon(group)
                                                return <button type="button" className={styles.taskCalendarAgendaItem} key={task.id} onClick={() => setSelectedId(task.opportunity_id)}>
                                                    <span className={`${styles.taskCalendarAgendaIcon} ${styles[`taskTag_${group}`] || ''}`}><TaskIcon /></span>
                                                    <span className={styles.taskCalendarAgendaBody}>
                                                        <span><time>{formatTimelineTime(task.due_at)}</time><em className={`${styles.upcomingTaskType} ${styles[`taskTag_${group}`] || ''}`}>{taskLabel(task)}</em></span>
                                                        <strong>{fullName(task.lead)}</strong>
                                                        <small>{task.application?.course?.name || task.application?.name || 'Corso non indicato'}</small>
                                                    </span>
                                                    <ChevronIcon />
                                                </button>
                                            })}
                                        </div>
                                    ) : <div className={styles.taskCalendarAgendaEmpty}><CheckCircleIcon /><span>Nessun task per questo giorno</span></div>}
                                </aside>
                            </div>
                        ) : <div className={styles.upcomingTasksGrid}>
                            {upcomingTasks.map((task) => {
                                const overdue = task.type !== 'contact_feedback' && isTaskOverdue(task)
                                const group = taskColorGroup(task.type)
                                const TaskIcon = taskGroupIcon(group)
                                return (
                                    <button
                                        type="button"
                                        className={`${styles.upcomingTask} ${overdue ? styles.upcomingTaskOverdue : ''}`}
                                        key={task.id}
                                        onClick={() => setSelectedId(task.opportunity_id)}
                                    >
                                        <span className={`${styles.upcomingTaskIcon} ${styles[`taskTag_${group}`] || ''}`} aria-hidden="true">
                                            <TaskIcon />
                                        </span>
                                        <span className={styles.upcomingTaskBody}>
                                            <span className={styles.upcomingTaskTitleRow}>
                                                <strong>{taskLabel(task)}</strong>
                                                {overdue && <span className={styles.upcomingTaskOverdueLabel}>Scaduto</span>}
                                            </span>
                                            <span className={styles.upcomingTaskLead}>
                                                <strong>{fullName(task.lead)}</strong>
                                                {task.temperature && (
                                                    <span className={`${styles.upcomingTaskTemperature} ${styles[`temperature_${task.temperature}`] || ''}`}>
                                                        {TEMPERATURE_LABELS[task.temperature] || task.temperature}
                                                    </span>
                                                )}
                                            </span>
                                            <span className={styles.upcomingTaskCourse}>
                                                {task.application?.course?.name || task.application?.name || 'Corso non indicato'}
                                            </span>
                                        </span>
                                        <span className={styles.upcomingTaskDue} aria-label={`Scadenza ${formatDate(task.due_at)}`}>
                                            <span>Scadenza</span>
                                            <strong>{formatTimelineDate(task.due_at)}</strong>
                                            <time>{formatTimelineTime(task.due_at)}</time>
                                        </span>
                                        <ChevronIcon className={styles.upcomingTaskChevron} />
                                    </button>
                                )
                            })}
                        </div>
                    ) : (
                        <div className={styles.upcomingTasksEmpty}>
                            <CheckCircleIcon />
                            <span><strong>Nessun task aperto</strong>Non hai attività commerciali assegnate.</span>
                        </div>
                    )}
                    {taskView === 'list' && <div className={styles.upcomingTasksPagination}>
                        <Pagination total={upcomingTasksCount} per={5} page={upcomingTasksPage} group={5} onChange={setUpcomingTasksPage} loading={loadingUpcomingTasks} />
                    </div>}
                </section>

                <section className={styles.tableCard}>
                    <div className={styles.tableHeader}>
                        <div>
                            <strong>{count}</strong> opportunità
                        </div>
                        <ManualApplicationButton onCreated={loadOpportunities} />
                    </div>
                    <div className={styles.search}>
                        <TextInput
                            appearance="modern"
                            type="search"
                            value={queryInput}
                            onKeyUp={setQueryInput}
                            placeholder="Cerca per nome, email, telefono o corso"
                        />
                        <button
                            type="button"
                            className={styles.filterButton}
                            aria-expanded={filtersOpen}
                            aria-controls="application-filters"
                            onClick={() => setFiltersOpen((open) => !open)}
                        >
                            <FilterIcon aria-hidden="true" />
                            Filtri
                        </button>
                    </div>
                    <div id="application-filters" className={styles.filterControls} hidden={!filtersOpen}>
                        <div className={styles.filterField}>
                            <span>Stage</span>
                            <Select
                                appearance="modern"
                                portal
                                selected={STAGES.find((option) => option.value === stage)}
                                options={STAGES}
                                onSelect={(option) => {
                                    setPage(0)
                                    setStage(option.value)
                                    if (option.value !== 'closed') setOutcome('')
                                }}
                            />
                        </div>
                        <div className={styles.filterField}>
                            <span>Qualifica</span>
                            <MultipleSelect
                                appearance="modern"
                                portal
                                selected={selectedQualifications}
                                options={QUALIFICATION_FILTER_OPTIONS}
                                placeholder="Tutte le qualifiche"
                                allLabel="Tutte le qualifiche"
                                onSelect={(options) => { setPage(0); setSelectedQualifications(options) }}
                            />
                        </div>
                        {stage === 'closed' && <div className={styles.filterField}>
                            <span>Esito</span>
                            <Select
                                appearance="modern"
                                portal
                                selected={OUTCOMES.find((option) => option.value === outcome)}
                                options={OUTCOMES}
                                onSelect={(option) => { setPage(0); setOutcome(option.value) }}
                            />
                        </div>}
                        <div className={styles.filterField}>
                            <span>Temperatura</span>
                            <MultipleSelect
                                appearance="modern"
                                portal
                                selected={selectedTemperatures}
                                options={TEMPERATURE_FILTER_OPTIONS}
                                placeholder="Tutte le temperature"
                                allLabel="Tutte le temperature"
                                onSelect={(options) => { setPage(0); setSelectedTemperatures(options) }}
                            />
                        </div>
                        <div className={styles.filterField}>
                            <span>Corso</span>
                            <MultipleSelect
                                appearance="modern"
                                portal
                                selected={selectedCourses}
                                options={filteredCourseOptions}
                                loading={loadingCourses}
                                placeholder="Tutti i corsi"
                                allLabel="Tutti i corsi"
                                onSearch={(value) => setCourseSearch(value || '')}
                                searchPlaceholder="Cerca corso"
                                onSelect={(options) => { setPage(0); setSelectedCourses(options) }}
                            />
                        </div>
                        <div className={styles.filterField}>
                            <span>Assegnatario del lead</span>
                            <MultipleSelect
                                appearance="modern"
                                portal
                                selected={selectedOwners}
                                options={ownerOptions}
                                loading={loadingOwners}
                                placeholder="Tutti gli assegnatari"
                                allLabel="Tutti gli assegnatari"
                                onSearch={() => {}}
                                searchPlaceholder="Cerca assegnatario"
                                onSelect={(options) => { setPage(0); setSelectedOwners(options); setOnlyMine(false) }}
                            />
                        </div>
                        <div className={styles.filterField}>
                            <span>Data di arrivo</span>
                            <EasePicker
                                options={arrivalDateOptions}
                                placeholder="Seleziona date"
                                className={styles.datePicker}
                            />
                        </div>
                        <div className={styles.filterToggles}>
                            <div className={styles.checkField}>
                                <Checkbox
                                    size="1.5rem"
                                    radius="6px"
                                    value={onlyMine}
                                    onChange={(checked) => { setPage(0); setSelectedOwners([]); setOnlyMine(checked) }}
                                    label="Solo assegnati a me"
                                    labelPosition="end"
                                />
                            </div>
                            <div className={styles.checkField}>
                                <Checkbox
                                    size="1.5rem"
                                    radius="6px"
                                    value={includeTest}
                                    onChange={(checked) => { setPage(0); setIncludeTest(checked) }}
                                    label="Includi test"
                                    labelPosition="end"
                                />
                            </div>
                            <Button
                                type="button"
                                appearance="subtle"
                                additionalClass={styles.exportSearchButton}
                                disabled={exporting}
                                onClick={exportOpportunities}
                            >
                                <ExportIcon aria-hidden="true" />
                                {exporting ? 'Esportazione…' : 'Esporta ricerca'}
                            </Button>
                        </div>
                    </div>
                    <div className={styles.tableScroll}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Lead</th>
                                    <th>Corso</th>
                                    <th>Data di arrivo</th>
                                    <th>Stage</th>
                                    <th>Temperatura</th>
                                    <th>Qualifica</th>
                                    <th>Esito</th>
                                    <th>Owner</th>
                                    <th>Prossimo task</th>
                                    <th>Ultimo contatto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!loading && opportunities.map((opportunity) => (
                                    <tr
                                        key={opportunity.id}
                                        onClick={() => setSelectedId(opportunity.id)}
                                        onKeyDown={(event) => {
                                            if (['Enter', ' '].includes(event.key)) {
                                                event.preventDefault()
                                                setSelectedId(opportunity.id)
                                            }
                                        }}
                                        tabIndex="0"
                                    >
                                        <td data-label="Lead">
                                            <strong>{fullName(opportunity.lead)}</strong>
                                            <span>{opportunity.lead?.email || '—'}</span>
                                        </td>
                                        <td data-label="Corso">{opportunity.application?.course?.name || opportunity.application?.name || '—'}</td>
                                        <td data-label="Data di arrivo">{formatDate(opportunity.application_created_at)}</td>
                                        <td data-label="Stage"><span className={stageClass(opportunity.stage)}>{STAGE_LABELS[opportunity.stage] || opportunity.stage}</span></td>
                                        <td data-label="Temperatura">
                                            {opportunity.temperature
                                                ? <span className={temperatureClass(opportunity.temperature)}>{TEMPERATURE_LABELS[opportunity.temperature] || opportunity.temperature}</span>
                                                : '—'}
                                        </td>
                                        <td data-label="Qualifica">{QUALIFICATION_LABELS[opportunity.qualification_status] || opportunity.qualification_status}</td>
                                        <td data-label="Esito">
                                            {opportunity.outcome
                                                ? <span className={outcomeClass(opportunity.outcome)}>{OUTCOME_LABELS[opportunity.outcome] || opportunity.outcome}</span>
                                                : '—'}
                                        </td>
                                        <td data-label="Owner">{fullName(opportunity.owner)}</td>
                                        <td data-label="Prossimo task">
                                            <strong>{taskLabel(opportunity.next_task)}</strong>
                                            <span className={isOverdue(opportunity.next_task) ? styles.overdue : ''}>
                                                {formatDate(opportunity.next_task?.due_at)}
                                            </span>
                                        </td>
                                        <td data-label="Ultimo contatto">
                                            <strong>{activityLabel(opportunity.last_activity)}</strong>
                                            <span>{formatDate(opportunity.last_activity?.occurred_at)}</span>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && opportunities.length === 0 && (
                                    <tr className={styles.emptyRow}><td className={styles.empty} colSpan="10">Nessuna opportunità trovata con questi filtri.</td></tr>
                                )}
                            </tbody>
                        </table>
                        {loading && <div className={styles.loading}><Loader /></div>}
                    </div>
                    <div className={styles.pagination}>
                        <Pagination total={count} per={per} page={page} group={5} onChange={setPage} loading={loading} />
                    </div>
                </section>
            </main>

            {selectedId && (
                <div className={styles.drawerBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) closeDetail() }}>
                    <aside className={styles.drawer}>
                        <header className={styles.drawerHeader}>
                            <div>
                                <span>Opportunity #{selectedId}</span>
                                <h2>{detail ? fullName(detail.lead) : 'Caricamento…'}</h2>
                            </div>
                            <div className={styles.drawerHeaderActions}>
                                {detail && (
                                    <TooltipWrapper text={detail.test ? 'Rimuovi il contrassegno di test' : 'Contrassegna come candidatura di test'}>
                                        <Button
                                            appearance="subtle"
                                            additionalClass={`${styles.testToggle} ${detail.test ? styles.testToggleActive : ''}`}
                                            onClick={() => changeTestStatus(!detail.test)}
                                            disabled={changingTestStatus}
                                            aria-label={detail.test ? 'Rimuovi il contrassegno di test' : 'Contrassegna come candidatura di test'}
                                            aria-pressed={Boolean(detail.test)}
                                            requiresEdit
                                        >
                                            {!detail.test && <TestInactiveIcon />}
                                            {detail.test && <span>Test</span>}
                                        </Button>
                                    </TooltipWrapper>
                                )}
                                <button className={styles.closeButton} onClick={closeDetail} aria-label="Chiudi dettaglio"><CloseIcon /></button>
                            </div>
                        </header>

                        {detailLoading && !detail && <div className={styles.loading}><Loader /></div>}
                        {detail && (
                            <div className={styles.drawerContent}>
                                <section className={styles.summaryGrid}>
                                    <div><span>Stage</span><strong>{STAGE_LABELS[detail.stage] || detail.stage}</strong></div>
                                    <div>
                                        <span>Temperatura</span>
                                        {detail.temperature
                                            ? <strong className={temperatureClass(detail.temperature)}>{TEMPERATURE_LABELS[detail.temperature] || detail.temperature}</strong>
                                            : <strong>—</strong>}
                                    </div>
                                    <div><span>Qualifica</span><strong>{QUALIFICATION_LABELS[detail.qualification_status] || detail.qualification_status}</strong></div>
                                    <div className={styles.editableSummary}>
                                        <span>Responsabile commerciale</span>
                                        <Select
                                            appearance="modern"
                                            portal
                                            selected={ownerOptions.find((option) => option.value === (ownerDraft?.ownerId || detail.owner?.id)) || (detail.owner ? { id: detail.owner.id, value: detail.owner.id, name: fullName(detail.owner) } : null)}
                                            options={ownerOptions}
                                            loading={loadingOwners}
                                            placeholder="Seleziona un owner"
                                            onSelect={selectOwner}
                                        />
                                        {ownerDraft && (
                                            <div className={styles.inlineConfirmation}>
                                                <TextInput
                                                    appearance="modern"
                                                    type="textarea"
                                                    value={ownerDraft.notes}
                                                    placeholder="Nota sulla riassegnazione (opzionale)"
                                                    onKeyUp={(value) => setOwnerDraft({ ...ownerDraft, notes: value })}
                                                    resizable={false}
                                                />
                                                <div className={styles.confirmActions}>
                                                    <Button appearance="text" onClick={() => setOwnerDraft(null)} requiresEdit={false}>Annulla</Button>
                                                    <Button
                                                        appearance="subtle"
                                                        onClick={confirmOwnerChange}
                                                        disabled={changingOwner}
                                                        status={changingOwner ? ButtonStatus.Loading : undefined}
                                                        requiresEdit
                                                    >
                                                        Conferma
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.editableSummary}>
                                        <span>Corso</span>
                                        <Select
                                            appearance="modern"
                                            portal
                                            selected={detailCourseOptions.find((option) => option.value === (courseDraft?.applicationId || detail.application?.id)) || (detail.application?.course ? { id: detail.application.id, value: detail.application.id, name: detail.application.course.name } : null)}
                                            options={detailCourseOptions}
                                            loading={loadingCourses}
                                            placeholder="Seleziona un corso"
                                            onSearch={(value) => setDetailCourseSearch(value || '')}
                                            searchPlaceholder="Cerca corso"
                                            onSelect={selectCourse}
                                        />
                                        {courseDraft && (
                                            <div className={styles.inlineConfirmation}>
                                                <TextInput
                                                    appearance="modern"
                                                    type="textarea"
                                                    value={courseDraft.notes}
                                                    placeholder="Nota sul cambio corso (opzionale)"
                                                    onKeyUp={(value) => setCourseDraft({ ...courseDraft, notes: value })}
                                                    resizable={false}
                                                />
                                                <div className={styles.confirmActions}>
                                                    <Button appearance="text" onClick={() => setCourseDraft(null)} requiresEdit={false}>Annulla</Button>
                                                    <Button
                                                        appearance="subtle"
                                                        onClick={confirmCourseChange}
                                                        disabled={changingCourse}
                                                        status={changingCourse ? ButtonStatus.Loading : undefined}
                                                        requiresEdit
                                                    >
                                                        Conferma
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {detail.outcome && <div><span>Esito</span><strong>{OUTCOME_LABELS[detail.outcome] || detail.outcome}</strong></div>}
                                </section>

                                {detail.order && (
                                    <section className={styles.orderCard}>
                                        <header className={styles.orderHeader}>
                                            <div>
                                                <span>Ordine collegato</span>
                                                <h3>#{detail.order.order_no || detail.order.id}</h3>
                                            </div>
                                            <div className={styles.orderHeaderActions}>
                                                <span className={`${styles.orderStatus} ${styles[`orderStatus_${detail.order.status}`] || ''}`}>
                                                    {PAYMENT_STATUS_LABELS[detail.order.status] || detail.order.status}
                                                </span>
                                                <a href={`/orders/${detail.order.id}`}>Apri ordine</a>
                                            </div>
                                        </header>
                                        <div className={styles.orderOverview}>
                                            <div><span>Totale</span><strong>{formatCurrency(detail.order.amount)}</strong></div>
                                            <div><span>Data ordine</span><strong>{formatDate(detail.order.created_at)}</strong></div>
                                            <div><span>Metodo di pagamento</span><strong>{PAYMENT_METHOD_LABELS[detail.order.payment_method] || detail.order.payment_method || '—'}</strong></div>
                                            <div><span>Completato il</span><strong>{formatDate(detail.order.completed_at)}</strong></div>
                                        </div>
                                        <div className={styles.installmentsSection}>
                                            <div className={styles.installmentsHeading}>
                                                <h4>Rate</h4>
                                                <span>{detail.order.installments?.length ? `${detail.order.installments.length} previste` : 'Pagamento unico'}</span>
                                            </div>
                                            {detail.order.installments?.length > 0 ? (
                                                <div className={styles.installmentsList}>
                                                    {detail.order.installments.map((installment, index) => (
                                                        <div className={styles.installmentRow} key={installment.id}>
                                                            <div className={styles.installmentNumber}>
                                                                <span>Rata</span>
                                                                <strong>{index + 1}</strong>
                                                            </div>
                                                            <div className={styles.installmentMain}>
                                                                <strong>{formatCurrency(installment.amount)}</strong>
                                                                <span>Scadenza {formatDate(installment.expired_at, false)}</span>
                                                            </div>
                                                            <div className={styles.installmentPayment}>
                                                                <span className={`${styles.orderStatus} ${styles[`orderStatus_${installment.status}`] || ''}`}>
                                                                    {PAYMENT_STATUS_LABELS[installment.status] || installment.status}
                                                                </span>
                                                                <small>{installment.completed_at ? `Pagata il ${formatDate(installment.completed_at, false)}` : (PAYMENT_METHOD_LABELS[installment.payment_method] || installment.payment_method || '')}</small>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className={styles.orderEmptyInstallments}>Questo ordine non prevede un piano rateale.</p>
                                            )}
                                        </div>
                                    </section>
                                )}

                                {(detail.available_actions || []).length > 0 && (
                                    <section className={styles.actionsCard}>
                                        <div className={styles.actionsCardHeader}>
                                            <div>
                                                <span>Workflow commerciale</span>
                                                <h3>Prossima decisione commerciale</h3>
                                                <p>Scegli l’azione più coerente con la fase attuale del lead.</p>
                                            </div>
                                            <span className={styles.actionsCount}>
                                                {displayTransitionActions(detail.available_actions || []).length} disponibili
                                            </span>
                                        </div>
                                        <div className={styles.actionButtons}>
                                            {displayTransitionActions(detail.available_actions || []).map((action) => {
                                                const config = ACTIONS[action]
                                                if (!config) return null
                                                const label = action === 'close_negative'
                                                    ? negativeDecisionLabel(detail.available_actions || [])
                                                    : config.label
                                                return (
                                                    <Button
                                                        key={action}
                                                        appearance="subtle"
                                                        additionalClass={styles.commercialActionButton}
                                                        accentColor={config.destructive ? 'var(--sf-red)' : config.positive ? 'var(--sf-green)' : undefined}
                                                        onClick={() => openTransition(action)}
                                                        disabled={transitioning}
                                                        requiresEdit
                                                    >
                                                        {label}
                                                    </Button>
                                                )
                                            })}
                                        </div>
                                    </section>
                                )}

                                {detail.stage !== 'closed' && paymentLinkIsNextStep && paymentLink && (
                                    <section className={styles.paymentLinkCard}>
                                        <div>
                                            <span>Prossimo passo suggerito</span>
                                            <h3>Invia il link di pagamento</h3>
                                            <p>Condividi questo checkout con il lead. Il link contiene già la sua e-mail.</p>
                                        </div>
                                        <div className={styles.paymentLinkValue}>
                                            <code>{paymentLink}</code>
                                            <Button appearance="subtle" onClick={copyPaymentLink} requiresEdit={false}>
                                                <CopyIcon /> {paymentLinkCopied ? 'Copiato' : 'Copia link'}
                                            </Button>
                                        </div>
                                    </section>
                                )}

                                {transitionDraft && (
                                    <section className={styles.transitionCard}>
                                        <div>
                                            <h3>{ACTIONS[transitionDraft.action]?.title}</h3>
                                            <p>{ACTIONS[transitionDraft.action]?.description}</p>
                                        </div>
                                        {transitionDraft.action === 'postpone' && (
                                            <div className={styles.confirmField}>
                                                <span>Data di riciclo / prossima edizione</span>
                                                <TextInput
                                                    appearance="modern"
                                                    type="date"
                                                    value={transitionDraft.recycleAt}
                                                    onKeyUp={(value) => setTransitionDraft({ ...transitionDraft, recycleAt: value })}
                                                />
                                            </div>
                                        )}
                                        {transitionDraft.action === 'schedule_follow_up' && (
                                            <>
                                                <div className={styles.confirmField}>
                                                    <span>Tipo di attività</span>
                                                    <Select
                                                        appearance="modern"
                                                        portal
                                                        selected={TASK_SCHEDULE_OPTIONS.find((option) => option.value === transitionDraft.nextTaskType)}
                                                        options={TASK_SCHEDULE_OPTIONS}
                                                        onSelect={(option) => setTransitionDraft({ ...transitionDraft, nextTaskType: option.value })}
                                                    />
                                                </div>
                                                <div className={styles.confirmField}>
                                                    <span>{transitionDraft.nextTaskType === 'scheduled_call' ? 'Chiamata fissata per' : 'Ricontatta il'}</span>
                                                    <TextInput
                                                        appearance="modern"
                                                        type="date"
                                                        value={transitionDraft.nextTaskDueAt}
                                                        onKeyUp={(value) => setTransitionDraft({ ...transitionDraft, nextTaskDueAt: value })}
                                                    />
                                                </div>
                                            </>
                                        )}
                                        {transitionDraft.action === 'close_negative' && (() => {
                                            const closureTypes = availableNegativeClosureTypes(detail.available_actions || [])
                                            const selectedClosureType = closureTypes.find((type) => type.value === transitionDraft.negativeClosureType) || null
                                            const closureOptions = availableNegativeClosureReasons(detail.available_actions || []).filter((reason) => (
                                                !selectedClosureType || reason.action === selectedClosureType.action
                                            ))
                                            const selectedClosure = negativeClosureResult
                                            return (
                                                <>
                                                    {closureTypes.length > 1 && (
                                                        <div className={styles.confirmField}>
                                                            <span>Esito della decisione</span>
                                                            <Select
                                                                appearance="modern"
                                                                portal
                                                                nullable
                                                                nullLabel="Seleziona l’esito"
                                                                selected={selectedClosureType}
                                                                options={closureTypes}
                                                                onSelect={(option) => setTransitionDraft({
                                                                    ...transitionDraft,
                                                                    negativeClosureType: option?.value || '',
                                                                    negativeClosureReason: '',
                                                                })}
                                                            />
                                                        </div>
                                                    )}
                                                    {selectedClosureType && (
                                                    <div className={styles.confirmField}>
                                                        <span>{selectedClosureType.value === 'unqualified' ? 'Motivazione della non qualifica' : 'Motivazione della perdita'}</span>
                                                        <Select
                                                            appearance="modern"
                                                            portal
                                                            nullable
                                                            nullLabel="Seleziona una motivazione"
                                                            selected={selectedClosure || null}
                                                            options={closureOptions}
                                                            onSelect={(option) => setTransitionDraft({
                                                                ...transitionDraft,
                                                                negativeClosureReason: option?.value || '',
                                                            })}
                                                        />
                                                    </div>
                                                    )}
                                                    {selectedClosure && (
                                                        <div className={styles.qualificationResult}>
                                                            <span>Decisione selezionata</span>
                                                            <h4>{selectedClosure.outcomeLabel}</h4>
                                                            <p>
                                                                {selectedClosure.action === 'mark_unqualified'
                                                                    ? <>Il lead resterà in <strong>Qualifica</strong> come <strong>Non qualificato</strong> con motivazione “{selectedClosure.name}”.</>
                                                                    : <>L’opportunità verrà chiusa come <strong>Persa</strong> con motivazione “{selectedClosure.name}”.</>}
                                                            </p>
                                                        </div>
                                                    )}
                                                </>
                                            )
                                        })()}
                                        {transitionDraft.action === 'qualify' && (() => {
                                            const step = transitionDraft.qualificationStep || 0
                                            const field = QUALIFICATION_WIZARD_FIELDS[step]
                                            const options = field ? qualificationWizardOptions(field) : []
                                            const fieldValue = field ? transitionDraft.qualificationFeedback[field] : ''
                                            return (
                                                <div className={styles.qualificationWizard}>
                                                    <div className={styles.qualificationProgress}>
                                                        {QUALIFICATION_WIZARD_FIELDS.map((item, index) => (
                                                            <span
                                                                className={index <= step ? styles.qualificationProgressActive : ''}
                                                                key={item}
                                                            />
                                                        ))}
                                                        <span className={step >= QUALIFICATION_WIZARD_FIELDS.length ? styles.qualificationProgressActive : ''} />
                                                    </div>
                                                    {field ? (
                                                        <div className={styles.qualificationStep}>
                                                            <span className={styles.qualificationStepCount}>Domanda {step + 1} di {QUALIFICATION_WIZARD_FIELDS.length}</span>
                                                            <h4>{QUALIFICATION_FIELD_LABELS[field] || (field === 'objection' ? 'Obiezione o motivo principale' : 'Prossimo passo')}</h4>
                                                            <Select
                                                                appearance="modern"
                                                                portal
                                                                placeholder="Seleziona una risposta"
                                                                selected={options.find((item) => item.value === fieldValue) || null}
                                                                options={options}
                                                                onSelect={(option) => setTransitionDraft({
                                                                    ...transitionDraft,
                                                                    qualificationFeedback: {
                                                                        ...transitionDraft.qualificationFeedback,
                                                                        [field]: option?.value || '',
                                                                    },
                                                                })}
                                                            />
                                                            <div className={styles.qualificationNavigation}>
                                                                <Button
                                                                    appearance="text"
                                                                    onClick={() => setTransitionDraft({ ...transitionDraft, qualificationStep: Math.max(0, step - 1) })}
                                                                    disabled={step === 0}
                                                                    requiresEdit={false}
                                                                >
                                                                    Indietro
                                                                </Button>
                                                                <Button
                                                                    appearance="subtle"
                                                                    onClick={() => setTransitionDraft({ ...transitionDraft, qualificationStep: step + 1 })}
                                                                    disabled={!fieldValue}
                                                                    requiresEdit={false}
                                                                >
                                                                    Avanti
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className={styles.qualificationResult}>
                                                            <span>Esito proposto</span>
                                                            <h4>{guidedQualificationResult?.label}</h4>
                                                            <p>{guidedQualificationResult?.description}</p>
                                                            <div className={styles.qualificationSummary}>
                                                                {QUALIFICATION_WIZARD_FIELDS.map((item) => (
                                                                    <div key={item}>
                                                                        <span>{QUALIFICATION_FIELD_LABELS[item] || (item === 'objection' ? 'Obiezione' : 'Prossimo passo')}</span>
                                                                        <strong>{qualificationWizardLabel(item, transitionDraft.qualificationFeedback[item])}</strong>
                                                                    </div>
                                                                ))}
                                                                <div className={styles.qualificationSuggestedStep}>
                                                                    <span>Prossimo passo suggerito</span>
                                                                    <strong>
                                                                        {guidedQualificationResult?.nextStepLabel
                                                                            || QUALIFICATION_NEXT_STEP_LABELS[guidedQualificationResult?.nextStep]
                                                                            || guidedQualificationResult?.nextStep}
                                                                    </strong>
                                                                </div>
                                                            </div>
                                                            {guidedQualificationResult?.nextStep === 'payment_link' && paymentLink && (
                                                                <div className={styles.paymentLinkInline}>
                                                                    <span>Link di pagamento pronto</span>
                                                                    <code>{paymentLink}</code>
                                                                    <Button appearance="subtle" onClick={copyPaymentLink} requiresEdit={false}>
                                                                        <CopyIcon /> {paymentLinkCopied ? 'Copiato' : 'Copia link'}
                                                                    </Button>
                                                                </div>
                                                            )}
                                                            {guidedQualificationResult?.action === 'postpone' && (
                                                                <div className={styles.confirmField}>
                                                                    <span>Data di riciclo / prossima edizione</span>
                                                                    <TextInput
                                                                        appearance="modern"
                                                                        type="date"
                                                                        value={transitionDraft.recycleAt}
                                                                        onKeyUp={(value) => setTransitionDraft({ ...transitionDraft, recycleAt: value })}
                                                                    />
                                                                </div>
                                                            )}
                                                            {guidedQualificationResult?.action === 'schedule_follow_up' && (
                                                                <div className={styles.confirmField}>
                                                                    <span>Ricontatta il</span>
                                                                    <TextInput
                                                                        appearance="modern"
                                                                        type="date"
                                                                        value={transitionDraft.nextTaskDueAt}
                                                                        onKeyUp={(value) => setTransitionDraft({ ...transitionDraft, nextTaskDueAt: value })}
                                                                    />
                                                                </div>
                                                            )}
                                                            <Button
                                                                appearance="text"
                                                                onClick={() => setTransitionDraft({
                                                                    ...transitionDraft,
                                                                    qualificationStep: QUALIFICATION_WIZARD_FIELDS.length - 1,
                                                                })}
                                                                requiresEdit={false}
                                                            >
                                                                Modifica risposte
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        })()}
                                        {transitionDraft.action === 'mark_unqualified' && (
                                            <div className={styles.confirmField}>
                                                <span>Motivazione della non qualifica</span>
                                                <Select
                                                    appearance="modern"
                                                    portal
                                                    nullable
                                                    nullLabel="Seleziona una motivazione"
                                                    selected={UNQUALIFIED_REASONS.find((reason) => reason.value === transitionDraft.qualificationReason) || null}
                                                    options={UNQUALIFIED_REASONS}
                                                    onSelect={(option) => setTransitionDraft({ ...transitionDraft, qualificationReason: option?.value || '' })}
                                                />
                                            </div>
                                        )}
                                        {transitionDraft.action === 'mark_lost' && (
                                            <div className={styles.confirmField}>
                                                <span>Motivazione della perdita</span>
                                                <Select
                                                    appearance="modern"
                                                    portal
                                                    nullable
                                                    nullLabel="Seleziona una motivazione"
                                                    selected={LOSS_REASONS.find((reason) => reason.value === transitionDraft.lossReason) || null}
                                                    options={LOSS_REASONS}
                                                    onSelect={(option) => setTransitionDraft({ ...transitionDraft, lossReason: option?.value || '' })}
                                                />
                                            </div>
                                        )}
                                        {taskOptions.length > 0 && transitionDraft.action !== 'reopen' && (
                                            <div className={styles.confirmField}>
                                                <span>Task da completare (opzionale)</span>
                                                <Select
                                                    appearance="modern"
                                                    portal
                                                    nullable
                                                    nullLabel="Nessun task"
                                                    selected={taskOptions.find((option) => option.value === Number(transitionDraft.completedTaskId)) || null}
                                                    options={taskOptions}
                                                    onSelect={(option) => setTransitionDraft({ ...transitionDraft, completedTaskId: option?.value || '' })}
                                                />
                                            </div>
                                        )}
                                        <div className={styles.confirmField}>
                                            <span>Nota commerciale (opzionale)</span>
                                            <TextInput
                                                appearance="modern"
                                                type="textarea"
                                                value={transitionDraft.notes}
                                                placeholder="Aggiungi il motivo o il contesto della decisione"
                                                onKeyUp={(value) => setTransitionDraft({ ...transitionDraft, notes: value })}
                                                resizable={false}
                                            />
                                        </div>
                                        <div className={styles.confirmActions}>
                                            <Button appearance="text" onClick={() => setTransitionDraft(null)} requiresEdit={false}>Annulla</Button>
                                            <Button
                                                onClick={confirmTransition}
                                                disabled={transitioning
                                                    || (transitionDraft.action === 'postpone' && !transitionDraft.recycleAt)
                                                    || (transitionDraft.action === 'schedule_follow_up' && !transitionDraft.nextTaskDueAt)
                                                    || (transitionDraft.action === 'mark_lost' && !transitionDraft.lossReason)
                                                    || (transitionDraft.action === 'mark_unqualified' && !transitionDraft.qualificationReason)
                                                    || (transitionDraft.action === 'close_negative' && !transitionDraft.negativeClosureReason)
                                                    || (transitionDraft.action === 'qualify'
                                                        && (guidedQualificationResult?.blocked
                                                            || transitionDraft.qualificationStep < QUALIFICATION_WIZARD_FIELDS.length
                                                            || (guidedQualificationResult?.action === 'postpone' && !transitionDraft.recycleAt)
                                                            || (guidedQualificationResult?.action === 'schedule_follow_up' && !transitionDraft.nextTaskDueAt)))
                                                    || !qualificationFeedbackIsValid()}
                                                status={transitioning ? ButtonStatus.Loading : undefined}
                                                appearance="subtle"
                                                accentColor={ACTIONS[transitionDraft.action]?.destructive ? 'var(--sf-red)' : ACTIONS[transitionDraft.action]?.positive ? 'var(--sf-green)' : undefined}
                                                requiresEdit
                                            >
                                                {transitionDraft.action === 'qualify' && guidedQualificationResult
                                                    ? `Conferma: ${guidedQualificationResult.label}`
                                                    : (transitionDraft.action === 'close_negative' && negativeClosureResult
                                                        ? `Conferma: ${negativeClosureResult.outcomeLabel}`
                                                        : 'Conferma azione')}
                                            </Button>
                                        </div>
                                    </section>
                                )}

                                <section className={`${styles.contactCard} ${contactDraft ? styles.contactCardEditing : ''}`}>
                                    <div className={styles.contactInfo}>
                                        <div className={styles.contactCardHeader}>
                                            <div>
                                                <span>Contatto principale</span>
                                                <h3>{contactDraft ? 'Modifica i dati del lead' : fullName(detail.lead)}</h3>
                                            </div>
                                            <span className={`${styles.aircallContactStatus} ${styles[`aircallContactStatus_${detail.aircall_contact?.status || 'not_synced'}`] || ''}`}>
                                                {AIRCALL_CONTACT_STATUS_LABELS[detail.aircall_contact?.status] || AIRCALL_CONTACT_STATUS_LABELS.not_synced}
                                            </span>
                                        </div>
                                        {contactDraft ? (
                                            <div className={styles.contactForm}>
                                                <div className={styles.contactFields}>
                                                    <TextInput
                                                        appearance="modern"
                                                        type="text"
                                                        value={contactDraft.name}
                                                        label="Nome"
                                                        placeholder="Nome"
                                                        onKeyUp={(value) => setContactDraft((draft) => ({ ...draft, name: value }))}
                                                    />
                                                    <TextInput
                                                        appearance="modern"
                                                        type="text"
                                                        value={contactDraft.surname}
                                                        label="Cognome"
                                                        placeholder="Cognome"
                                                        onKeyUp={(value) => setContactDraft((draft) => ({ ...draft, surname: value }))}
                                                    />
                                                    <TextInput
                                                        appearance="modern"
                                                        type="text"
                                                        value={contactDraft.email}
                                                        label="Email"
                                                        placeholder="Email"
                                                        disabled={detail.lead?.has_profile}
                                                        onKeyUp={(value) => setContactDraft((draft) => ({ ...draft, email: value }))}
                                                    />
                                                    <TextInput
                                                        appearance="modern"
                                                        type="text"
                                                        value={contactDraft.phone}
                                                        label="Telefono"
                                                        placeholder="Telefono"
                                                        onKeyUp={(value) => setContactDraft((draft) => ({ ...draft, phone: value }))}
                                                    />
                                                </div>
                                                {detail.lead?.has_profile && (
                                                    <span className={styles.contactFormHint}>L’email non è modificabile perché l’utente ha già un profilo associato.</span>
                                                )}
                                                <div className={styles.confirmActions}>
                                                    <Button appearance="text" onClick={() => setContactDraft(null)} requiresEdit={false}>Annulla</Button>
                                                    <Button
                                                        appearance="subtle"
                                                        onClick={saveContact}
                                                        disabled={!contactDraftIsValid || savingContact}
                                                        status={savingContact ? ButtonStatus.Loading : undefined}
                                                        requiresEdit
                                                    >
                                                        Salva contatto
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className={styles.contactDetails}>
                                                <a href={`mailto:${detail.lead?.email || ''}`}>
                                                    <span>Email</span>
                                                    <strong>{detail.lead?.email || 'Non disponibile'}</strong>
                                                    {detail.contact_channels?.email && <small>{CONTACT_CHANNEL_STATUS_LABELS[detail.contact_channels.email] || detail.contact_channels.email}</small>}
                                                </a>
                                                <div>
                                                    <span>Telefono</span>
                                                    <strong>{detail.lead?.phone || 'Non disponibile'}</strong>
                                                    {detail.contact_channels?.phone && <small>{CONTACT_CHANNEL_STATUS_LABELS[detail.contact_channels.phone] || detail.contact_channels.phone}</small>}
                                                </div>
                                                <div>
                                                    <span>WhatsApp</span>
                                                    <strong>{CONTACT_CHANNEL_STATUS_LABELS[detail.contact_channels?.whatsapp] || 'Da verificare'}</strong>
                                                </div>
                                            </div>
                                        )}
                                        {['pending', 'emailed'].includes(detail.contact_update_request?.status) && (
                                            <div className={styles.contactUpdateNotice}>
                                                <strong>In attesa dell’aggiornamento del numero</strong>
                                                <span>
                                                    La richiesta automatica è stata inviata al lead
                                                    {detail.contact_update_request?.expires_at
                                                        ? ` · link valido fino al ${formatDate(detail.contact_update_request.expires_at)}`
                                                        : ''}.
                                                </span>
                                            </div>
                                        )}
                                        {detail.aircall_contact?.error && (
                                            <span className={styles.aircallContactError}>{detail.aircall_contact.error}</span>
                                        )}
                                        {aircallDialMessage && <span className={styles.aircallDialMessage}>{aircallDialMessage}</span>}
                                    </div>
                                    {!contactDraft && <div className={styles.contactActions}>
                                        <TooltipWrapper text="Modifica contatto">
                                            <Button
                                                appearance="subtle"
                                                additionalClass={styles.contactIconButton}
                                                aria-label="Modifica contatto"
                                                onClick={openContactEdit}
                                                requiresEdit
                                            >
                                                <EditIcon className={styles.buttonIcon} />
                                            </Button>
                                        </TooltipWrapper>
                                        <TooltipWrapper text="Registra attività commerciale">
                                            <Button
                                                appearance="subtle"
                                                additionalClass={styles.contactIconButton}
                                                aria-label="Registra attività commerciale"
                                                onClick={openManualActivity}
                                                requiresEdit
                                            >
                                                <PlusIcon className={styles.buttonIcon} />
                                            </Button>
                                        </TooltipWrapper>
                                        {detail.aircall_contact?.status !== 'synced' && (
                                            <Button
                                                appearance="subtle"
                                                additionalClass={styles.contactSyncButton}
                                                onClick={syncAircallContact}
                                                disabled={!detail.lead?.phone || syncingAircallContact}
                                                status={syncingAircallContact ? ButtonStatus.Loading : undefined}
                                                requiresEdit={false}
                                            >
                                                <ReloadIcon className={styles.buttonIcon} /> Sincronizza contatto
                                            </Button>
                                        )}
                                        <TooltipWrapper text="Chiama con Aircall">
                                            <Button
                                                appearance="subtle"
                                                additionalClass={styles.contactIconButton}
                                                aria-label="Chiama con Aircall"
                                                onClick={callWithAircall}
                                                disabled={!detail.lead?.phone || dialingAircall}
                                                status={dialingAircall ? ButtonStatus.Loading : undefined}
                                                requiresEdit={false}
                                            >
                                                <PhoneIcon className={styles.buttonIcon} />
                                            </Button>
                                        </TooltipWrapper>
                                        <TooltipWrapper text="Messaggia con WhatsApp Aircall">
                                            <Button
                                                appearance="subtle"
                                                additionalClass={styles.contactIconButton}
                                                aria-label="Messaggia con WhatsApp Aircall"
                                                onClick={messageWithAircall}
                                                disabled={!detail.lead?.phone || openingAircallMessage}
                                                status={openingAircallMessage ? ButtonStatus.Loading : undefined}
                                                requiresEdit={false}
                                            >
                                                <WhatsappIcon className={styles.buttonIcon} />
                                            </Button>
                                        </TooltipWrapper>
                                        <TooltipWrapper text="Apri WhatsApp">
                                            <Button
                                                additionalClass={styles.contactIconButton}
                                                aria-label="Apri WhatsApp"
                                                onClick={openWhatsapp}
                                                disabled={!detail.lead?.phone}
                                                requiresEdit={false}
                                            >
                                                <WhatsappIcon className={styles.buttonIcon} />
                                            </Button>
                                        </TooltipWrapper>
                                    </div>}
                                </section>

                                {manualActivityDraft && (
                                    <section className={styles.confirmCard}>
                                        <div>
                                            <h3>{manualActivityDraft.replyToActivityId ? 'Registra risposta ricevuta' : 'Registra attività commerciale'}</h3>
                                            <p>
                                                {manualActivityDraft.replyToActivityId
                                                    ? `La risposta verrà collegata al messaggio #${manualActivityDraft.replyToActivityId} senza modificare task o workflow.`
                                                    : 'Annota un contatto aggiuntivo. Questa attività non completerà, annullerà o creerà task e non cambierà lo stato dell’opportunità.'}
                                            </p>
                                        </div>
                                        <div className={styles.taskCompletionFields}>
                                            {!manualActivityDraft.replyToActivityId && (
                                                <>
                                                    <div className={styles.confirmField}>
                                                        <span>Canale</span>
                                                        <Select
                                                            appearance="modern"
                                                            portal
                                                            selected={MANUAL_ACTIVITY_CHANNELS.find((item) => item.value === manualActivityDraft.activityType) || null}
                                                            options={MANUAL_ACTIVITY_CHANNELS}
                                                            onSelect={(option) => {
                                                                const activityType = option?.value || ''
                                                                const firstOutcome = MANUAL_ACTIVITY_OUTCOMES[activityType]?.[0]?.value || ''
                                                                setManualActivityDraft({
                                                                    ...manualActivityDraft,
                                                                    activityType,
                                                                    contactOutcome: firstOutcome,
                                                                    conversationOutcome: '',
                                                                })
                                                            }}
                                                        />
                                                    </div>
                                                    <div className={styles.confirmField}>
                                                        <span>Esito del contatto</span>
                                                        <Select
                                                            appearance="modern"
                                                            portal
                                                            selected={(MANUAL_ACTIVITY_OUTCOMES[manualActivityDraft.activityType] || []).find((item) => item.value === manualActivityDraft.contactOutcome) || null}
                                                            options={MANUAL_ACTIVITY_OUTCOMES[manualActivityDraft.activityType] || []}
                                                            onSelect={(option) => {
                                                                const contactOutcome = option?.value || ''
                                                                setManualActivityDraft({
                                                                    ...manualActivityDraft,
                                                                    contactOutcome,
                                                                    conversationOutcome: ['answered', 'received'].includes(contactOutcome)
                                                                        ? manualActivityDraft.conversationOutcome
                                                                        : '',
                                                                })
                                                            }}
                                                        />
                                                    </div>
                                                </>
                                            )}
                                            {['answered', 'received'].includes(manualActivityDraft.contactOutcome) && (
                                                <div className={styles.confirmField}>
                                                    <span>Esito della conversazione (opzionale)</span>
                                                    <Select
                                                        appearance="modern"
                                                        portal
                                                        nullable
                                                        nullLabel="Nessun esito commerciale"
                                                        selected={ALL_CONVERSATION_OUTCOMES.find((item) => item.value === manualActivityDraft.conversationOutcome) || null}
                                                        options={conversationOutcomesForStage(detail.stage, detail.qualification_status)}
                                                        onSelect={(option) => setManualActivityDraft({
                                                            ...manualActivityDraft,
                                                            conversationOutcome: option?.value || '',
                                                        })}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <label>
                                            <span>Note (opzionali)</span>
                                            <textarea
                                                value={manualActivityDraft.notes}
                                                onChange={(event) => setManualActivityDraft({ ...manualActivityDraft, notes: event.target.value })}
                                                placeholder="Es. Inviate informazioni aggiuntive sul programma via WhatsApp"
                                                rows="3"
                                            />
                                        </label>
                                        <div className={styles.confirmActions}>
                                            <Button appearance="text" onClick={() => setManualActivityDraft(null)} requiresEdit={false}>Annulla</Button>
                                            <Button
                                                appearance="subtle"
                                                onClick={saveManualActivity}
                                                disabled={!manualActivityIsValid || savingManualActivity}
                                                status={savingManualActivity ? ButtonStatus.Loading : undefined}
                                                requiresEdit
                                            >
                                                Registra attività
                                            </Button>
                                        </div>
                                    </section>
                                )}

                                {aircallSimulationOpen && detail.test && detail.aircall_webhook_simulator_enabled && (
                                    <section className={styles.confirmCard}>
                                        <div>
                                            <h3>Simulatore webhook Aircall</h3>
                                            <p>Gli eventi vengono registrati davvero, ma sono contrassegnati come simulazioni. Disponibile solo sulle candidature di test.</p>
                                        </div>
                                        <div className={styles.confirmField}>
                                            <span>Scenario</span>
                                            <Select
                                                appearance="modern"
                                                portal
                                                selected={aircallSimulationScenario}
                                                options={AIRCALL_SIMULATION_SCENARIOS}
                                                onSelect={(option) => setAircallSimulationScenario(option || AIRCALL_SIMULATION_SCENARIOS[0])}
                                            />
                                            <span>{aircallSimulationScenario.description}</span>
                                        </div>
                                        {aircallSimulationMessage && <span>{aircallSimulationMessage}</span>}
                                        <div className={styles.confirmActions}>
                                            <Button appearance="text" onClick={() => setAircallSimulationOpen(false)} requiresEdit={false}>Chiudi</Button>
                                            <Button
                                                appearance="subtle"
                                                onClick={simulateAircallWebhook}
                                                disabled={simulatingAircall}
                                                status={simulatingAircall ? ButtonStatus.Loading : undefined}
                                                requiresEdit={false}
                                            >
                                                Simula evento
                                            </Button>
                                        </div>
                                    </section>
                                )}

                                {whatsappDraft && (
                                    <section className={styles.confirmCard}>
                                        <div>
                                            <h3>Registra il contatto WhatsApp</h3>
                                            <p>Indica se hai solamente inviato il messaggio oppure se il lead ha già risposto.</p>
                                        </div>
                                        <div className={styles.taskCompletionFields}>
                                            <div className={styles.confirmField}>
                                                <span>Esito del contatto</span>
                                                <Select
                                                    appearance="modern"
                                                    portal
                                                    selected={MANUAL_ACTIVITY_OUTCOMES.whatsapp.find((item) => item.value === whatsappDraft.contactOutcome) || null}
                                                    options={MANUAL_ACTIVITY_OUTCOMES.whatsapp}
                                                    onSelect={(option) => {
                                                        const contactOutcome = option?.value || ''
                                                        setWhatsappDraft({
                                                            ...whatsappDraft,
                                                            contactOutcome,
                                                            conversationOutcome: '',
                                                            commercialObjection: '',
                                                            nextStepType: contactOutcome === 'sent' ? 'follow_up' : '',
                                                        })
                                                    }}
                                                />
                                            </div>
                                            {whatsappDraft.contactOutcome === 'received' && (
                                                <>
                                                <div className={styles.confirmField}>
                                                    <span>Esito della conversazione</span>
                                                    <Select
                                                        appearance="modern"
                                                        portal
                                                        placeholder="Scegli l’esito commerciale"
                                                        selected={ALL_CONVERSATION_OUTCOMES.find((item) => item.value === whatsappDraft.conversationOutcome) || null}
                                                        options={conversationOutcomesForStage(detail.stage, detail.qualification_status)}
                                                        onSelect={(option) => {
                                                            const conversationOutcome = option?.value || ''
                                                            setWhatsappDraft({
                                                                ...whatsappDraft,
                                                                conversationOutcome,
                                                                nextStepType: conversationOutcome ? nextStepForConversation(conversationOutcome) : '',
                                                                nextStepDueAt: nextStepDateForConversation(conversationOutcome, whatsappDraft.nextStepDueAt),
                                                            })
                                                        }}
                                                    />
                                                </div>
                                                <div className={styles.confirmField}>
                                                    <span>Obiezione emersa (opzionale)</span>
                                                    <Select
                                                        appearance="modern"
                                                        portal
                                                        nullable
                                                        nullLabel="Nessuna obiezione registrata"
                                                        selected={CONTACT_OBJECTIONS.find((item) => item.value === whatsappDraft.commercialObjection) || null}
                                                        options={CONTACT_OBJECTIONS}
                                                        onSelect={(option) => setWhatsappDraft({
                                                            ...whatsappDraft,
                                                            commercialObjection: option?.value || '',
                                                        })}
                                                    />
                                                </div>
                                                </>
                                            )}
                                            <div className={styles.confirmField}>
                                                <span>Prossimo passo</span>
                                                <Select
                                                    appearance="modern"
                                                    portal
                                                    placeholder="Definito dall’esito"
                                                    disabled
                                                    selected={CONTACT_NEXT_STEPS.find((item) => item.value === whatsappDraft.nextStepType) || null}
                                                    options={CONTACT_NEXT_STEPS}
                                                />
                                            </div>
                                            {['follow_up', 'scheduled_call', 'postpone'].includes(whatsappDraft.nextStepType) && (
                                                <div className={styles.confirmField}>
                                                    <span>{whatsappDraft.nextStepType === 'postpone'
                                                        ? 'Data di riciclo / prossima edizione'
                                                        : whatsappDraft.nextStepType === 'scheduled_call' ? 'Chiamata fissata per' : 'Ricontatta il'}</span>
                                                    <TextInput
                                                        appearance="modern"
                                                        type="date"
                                                        value={whatsappDraft.nextStepDueAt}
                                                        onKeyUp={(value) => setWhatsappDraft({ ...whatsappDraft, nextStepDueAt: value })}
                                                    />
                                                </div>
                                            )}
                                            <div className={styles.confirmField}>
                                                <span>Task collegato (opzionale)</span>
                                                <Select
                                                    appearance="modern"
                                                    portal
                                                    nullable
                                                    nullLabel="Nessun task"
                                                    selected={taskOptions.find((option) => option.value === Number(whatsappDraft.taskId)) || null}
                                                    options={taskOptions}
                                                    onSelect={(option) => setWhatsappDraft({ ...whatsappDraft, taskId: option?.value || '' })}
                                                />
                                            </div>
                                        </div>
                                        <label>
                                            <span>Note (opzionali)</span>
                                            <textarea value={whatsappDraft.notes} onChange={(event) => setWhatsappDraft({ ...whatsappDraft, notes: event.target.value })} rows="3" />
                                        </label>
                                        <div className={styles.confirmActions}>
                                            <Button appearance="text" onClick={() => setWhatsappDraft(null)} requiresEdit={false}>Annulla</Button>
                                            <Button
                                                appearance="subtle"
                                                onClick={confirmWhatsapp}
                                                disabled={confirmingWhatsapp || !whatsappDraftIsValid}
                                                status={confirmingWhatsapp ? ButtonStatus.Loading : undefined}
                                                requiresEdit
                                            >
                                                Registra contatto
                                            </Button>
                                        </div>
                                    </section>
                                )}

                                <section>
                                    <div className={styles.taskSectionHeader}>
                                        <h3 className={styles.sectionTitle}>Task</h3>
                                        <span className={`${styles.taskCount} ${openTasks.length === 0 ? styles.taskCountComplete : ''}`}>
                                            {openTasks.length === 0 ? 'Tutto completato' : `${openTasks.length} da fare`}
                                        </span>
                                    </div>
                                    <div className={`${styles.stack} ${styles.taskStack}`}>
                                        {sortedTasks.map((task) => {
                                            const overdue = task.type !== 'contact_feedback' && isTaskOverdue(task)
                                            const statusLabel = overdue ? 'Scaduto' : TASK_STATUS_LABELS[task.status] || task.status
                                            const linkedActivities = (detail.activities || []).filter((activity) => (
                                                Number(task.metadata?.completed_by_activity_id) === activity.id
                                                || Number(activity.metadata?.completed_task_id) === task.id
                                            ))
                                            const linkedEvents = (detail.lifecycle_events || []).filter((event) => (
                                                Number(event.metadata?.completed_task_id) === task.id
                                            ))
                                            const completedEntriesCount = linkedActivities.length || linkedEvents.length
                                            const expandable = ['open', 'completed'].includes(task.status)
                                            const expanded = taskDraft?.taskId === task.id || expandedCompletedTaskId === task.id
                                            return (
                                            <article
                                                className={`${styles.taskItem} ${styles[`taskItem_${task.status}`] || ''} ${overdue ? styles.taskItemOverdue : ''} ${expandable ? styles.taskItemClickable : ''} ${expanded ? styles.taskItemSelected : ''}`}
                                                key={task.id}
                                                role={expandable ? 'button' : undefined}
                                                tabIndex={expandable ? 0 : undefined}
                                                aria-expanded={expandable ? expanded : undefined}
                                                onClick={() => task.status === 'open' ? openTaskCompletion(task) : toggleCompletedTask(task)}
                                                onKeyDown={(event) => {
                                                    if (expandable && ['Enter', ' '].includes(event.key)) {
                                                        event.preventDefault()
                                                        if (task.status === 'open') openTaskCompletion(task)
                                                        else toggleCompletedTask(task)
                                                    }
                                                }}
                                            >
                                                <div className={`${styles.taskStatusIcon} ${styles[`taskStatusIcon_${task.status}`] || ''}`} aria-hidden="true">
                                                    {task.status === 'completed' && <CheckCircleIcon />}
                                                    {task.status === 'cancelled' && <DismissCircleIcon />}
                                                    {task.status === 'open' && <span />}
                                                </div>
                                                <div className={styles.taskContent}>
                                                    <div className={styles.taskTitleRow}>
                                                        <strong>{taskLabel(task)}</strong>
                                                        <span className={`${styles.taskStatus} ${overdue ? styles.taskStatusOverdue : ''}`}>{statusLabel}</span>
                                                    </div>
                                                    <span className={styles.taskDate}>{taskDateLabel(task)}</span>
                                                    {task.metadata?.calendly?.status === 'scheduled' && (
                                                        <span className={styles.taskCalendlyBadge}>
                                                            <CalendarIcon /> Calendly · orario scelto dal lead
                                                        </span>
                                                    )}
                                                </div>
                                                {taskDraft?.taskId === task.id && renderTaskCompletionForm()}
                                                {expandedCompletedTaskId === task.id && (
                                                    <div
                                                        className={styles.taskExpandedContent}
                                                        onClick={(event) => event.stopPropagation()}
                                                        onKeyDown={(event) => event.stopPropagation()}
                                                    >
                                                        <div className={styles.taskExpandedHeader}>
                                                            <div>
                                                                <span className={styles.taskExpandedLabel}>Attività svolte</span>
                                                                <strong>
                                                                    {completedEntriesCount > 0
                                                                        ? `${completedEntriesCount} ${completedEntriesCount === 1 ? 'attività collegata' : 'attività collegate'}`
                                                                        : 'Nessuna attività collegata'}
                                                                </strong>
                                                            </div>
                                                            <span className={styles.taskCompletedBadge}>
                                                                <CheckCircleIcon /> Completato
                                                            </span>
                                                        </div>
                                                {AUTOMATED_EMAIL_TASKS.has(task.type) && renderAutomaticEmailDetails(
                                                    task,
                                                    task.type === 'request_phone_update_email'
                                                        ? 'Richiesta automatica di correzione del numero'
                                                        : undefined,
                                                )}
                                                        {CHANNEL_ISSUE_TASK_TYPES.has(task.type) && (
                                                            <div className={styles.taskChannelIssue}>
                                                                {channelIssueDraft?.taskId === task.id
                                                                    ? renderChannelIssueForm()
                                                                    : (
                                                                        <Button
                                                                            type="button"
                                                                            appearance="subtle"
                                                                            onClick={() => openTaskChannelIssue(task)}
                                                                            requiresEdit
                                                                        >
                                                                            Segnala problema
                                                                        </Button>
                                                                    )}
                                                            </div>
                                                        )}
                                                        {linkedActivities.map((activity) => (
                                                            <div className={styles.taskLinkedActivity} key={activity.id}>
                                                                <div className={styles.taskLinkedActivityHeader}>
                                                                    <div className={styles.taskActivityIdentity}>
                                                                        <span className={styles.taskActivityIcon}><CheckCircleIcon /></span>
                                                                        <div>
                                                                            <strong>{activityLabel(activity)}</strong>
                                                                            <time>{formatDate(activity.occurred_at)}</time>
                                                                        </div>
                                                                    </div>
                                                                    <div className={styles.taskActivityBadges}>
                                                                        <span className={styles.taskActivityDirection}>{DIRECTION_LABELS[activity.direction] || activity.direction || '—'}</span>
                                                                        {activity.provider && <span className={styles.taskActivitySource}>{SOURCE_LABELS[activity.provider] || activity.provider}</span>}
                                                                    </div>
                                                                </div>
                                                                <div className={styles.taskActivityDetails}>
                                                                    {activityDetailFields(activity).map((item) => (
                                                                        <div className={styles.taskActivityField} key={`${item.label}-${item.value}`}>
                                                                            <span>{item.label}</span>
                                                                            <strong>{item.value}</strong>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                {activity.notes && (
                                                                    <div className={styles.taskActivityNote}>
                                                                        <span>Nota</span>
                                                                        <p>{activity.notes}</p>
                                                                    </div>
                                                                )}
                                                                {activity.recording_external_id && (
                                                                    <Button
                                                                        type="button"
                                                                        appearance="subtle"
                                                                        additionalClass={styles.taskRecordingButton}
                                                                        onClick={() => window.open(activity.recording_external_id, '_blank', 'noopener,noreferrer')}
                                                                        requiresEdit={false}
                                                                    >
                                                                        <PlayIcon className={styles.recordingIcon} />
                                                                        Ascolta registrazione
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        ))}
                                                        {linkedActivities.length === 0 && linkedEvents.map((event) => (
                                                            <div className={styles.taskLinkedActivity} key={`event-${event.id}`}>
                                                                <div className={styles.taskLinkedActivityHeader}>
                                                                    <div className={styles.taskActivityIdentity}>
                                                                        <span className={styles.taskActivityIcon}><CheckCircleIcon /></span>
                                                                        <div>
                                                                            <strong>{lifecycleTitle(event)}</strong>
                                                                            <time>{formatDate(event.occurred_at)}</time>
                                                                        </div>
                                                                    </div>
                                                                    <span className={styles.taskEventSource}>{SOURCE_LABELS[event.source] || event.source || 'Sistema'}</span>
                                                                </div>
                                                                <div className={styles.taskActivityDetails}>
                                                                    {labeledLifecycleDetails(event).map((item) => (
                                                                        <div className={styles.taskActivityField} key={`${item.label}-${item.value}`}>
                                                                            <span>{item.label}</span>
                                                                            <strong>{item.value}</strong>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                {event.metadata?.notes && (
                                                                    <div className={styles.taskActivityNote}>
                                                                        <span>Nota</span>
                                                                        <p>{event.metadata.notes}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                        {linkedActivities.length === 0 && linkedEvents.length === 0 && (
                                                            <span className={styles.muted}>Nessuna attività collegata disponibile.</span>
                                                        )}
                                                    </div>
                                                )}
                                            </article>
                                            )
                                        })}
                                        {detail.tasks?.length === 0 && <div className={styles.muted}>Nessun task.</div>}
                                    </div>
                                </section>

                                <section>
                                    <h3 className={styles.sectionTitle}>Attività</h3>
                                    <div className={styles.stack}>
                                        {(detail.activities || []).map((activity) => (
                                            <article className={`${styles.timelineItem} ${styles.structuredTimelineItem}`} key={activity.id}>
                                                <div className={styles.timelineContent}>
                                                    <div className={styles.timelineHeader}>
                                                        <strong>{activityLabel(activity)}</strong>
                                                        <time>{formatDate(activity.occurred_at)}</time>
                                                    </div>
                                                    <div className={styles.timelineBadges}>
                                                        <span className={styles.timelineBadge}>{DIRECTION_LABELS[activity.direction] || activity.direction || '—'}</span>
                                                        {activity.provider && <span className={styles.timelineSource}>{SOURCE_LABELS[activity.provider] || activity.provider}</span>}
                                                        {manuallyRepliedActivityIds.has(activity.id) && <span className={styles.timelineBadge}>Risposta ricevuta</span>}
                                                    </div>
                                                    {activity.notes && <span className={styles.timelineNote}>{activity.notes}</span>}
                                                    <div className={styles.timelineDetails}>
                                                        {activityDetails(activity).map((item) => <span key={item}>{item}</span>)}
                                                    </div>
                                                    {activity.recording_external_id && (
                                                        <Button
                                                            type="button"
                                                            appearance="subtle"
                                                            additionalClass={styles.recordingButton}
                                                            onClick={() => window.open(activity.recording_external_id, '_blank', 'noopener,noreferrer')}
                                                            requiresEdit={false}
                                                        >
                                                            <PlayIcon className={styles.recordingIcon} />
                                                            Ascolta registrazione
                                                        </Button>
                                                    )}
                                                    {activity.metadata?.capture_mode === 'standalone_manual_activity'
                                                        && activity.type === 'whatsapp'
                                                        && activity.direction === 'outbound'
                                                        && activity.contact_outcome === 'sent'
                                                        && !manuallyRepliedActivityIds.has(activity.id) && (
                                                        <Button
                                                            type="button"
                                                            appearance="subtle"
                                                            onClick={() => openManualActivityReply(activity)}
                                                            requiresEdit
                                                        >
                                                            Registra risposta
                                                        </Button>
                                                    )}
                                                </div>
                                            </article>
                                        ))}
                                        {detail.activities?.length === 0 && <div className={styles.muted}>Nessuna attività registrata.</div>}
                                    </div>
                                </section>

                                <section>
                                    <h3 className={styles.sectionTitle}>Cronologia commerciale</h3>
                                    <div className={`${styles.stack} ${styles.commercialTimeline}`}>
                                        {(detail.lifecycle_events || []).map((event) => (
                                            <div className={styles.timelineEntry} key={event.id}>
                                                <time className={styles.timelineTimestamp} dateTime={event.occurred_at}>
                                                    <strong>{formatTimelineDate(event.occurred_at)}</strong>
                                                    <span>{formatTimelineTime(event.occurred_at)}</span>
                                                </time>
                                                <span className={styles.timelineDot} aria-hidden="true" />
                                                <article className={`${styles.timelineItem} ${styles.lifecycleItem}`}>
                                                    <div className={styles.timelineContent}>
                                                        <div className={styles.timelineHeader}>
                                                            <strong>{lifecycleTitle(event)}</strong>
                                                        </div>
                                                        {lifecycleChanges(event).length > 0 && (
                                                            <div className={styles.lifecycleChanges}>
                                                                {lifecycleChanges(event).map((change) => <span className={styles.lifecycleChange} key={change}>{change}</span>)}
                                                            </div>
                                                        )}
                                                        <div className={styles.timelineDetails}>
                                                            {lifecycleDetails(event).map((item) => <span key={item}>{item}</span>)}
                                                        </div>
                                                        {event.event_type === 'opportunity_created' && Object.keys(detail.utm || {}).length > 0 && (
                                                            <div className={styles.utmList}>
                                                                {Object.entries(detail.utm).map(([key, value]) => (
                                                                    <span className={styles.utmValue} key={key}>
                                                                        <b>{UTM_LABELS[key] || key}</b>
                                                                        {value}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {event.metadata?.notes && <span className={styles.lifecycleNote}>“{event.metadata.notes}”</span>}
                                                        <div className={styles.timelineFooter}>
                                                            <span>{event.actor ? fullName(event.actor) : SOURCE_LABELS[event.source] || event.source || 'Sistema'}</span>
                                                            {event.actor && event.source && (
                                                                <span className={styles.timelineSource}>{SOURCE_LABELS[event.source] || event.source}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </article>
                                            </div>
                                        ))}
                                        {detail.lifecycle_events?.length === 0 && <div className={styles.muted}>Nessun evento commerciale registrato.</div>}
                                    </div>
                                </section>
                            </div>
                        )}
                    </aside>
                </div>
            )}
            <AircallWorkspacePanel
                mounted={aircallPanelMounted}
                open={aircallPanelOpen}
                besideDrawer={Boolean(selectedId)}
                pendingDial={pendingAircallDial}
                onClose={() => setAircallPanelOpen(false)}
                onOpen={() => {
                    setAircallPanelMounted(true)
                    setAircallPanelOpen(true)
                }}
                onDialHandled={(success) => {
                    setPendingAircallDial(null)
                    if (success) {
                        setAircallDialMessage('Numero pronto nel dialer Aircall')
                    }
                }}
            />
            <LeadFlowHelpDialog />
        </HeaderFooterLayout>
    )
}

export default ApplicationsV2
